import React, {
  useState, useEffect, useRef, useCallback, useContext
} from 'react';
import { AuthContext } from './App';

/* ─────────────────────────────────────────────────────────────────
   Constants & helpers
───────────────────────────────────────────────────────────────── */
const API = process.env.REACT_APP_API_URL || '/api';

/* ── Lucide-style inline SVG icons ───────────────────────────────── */
function I(paths) {
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" style="width:15px;height:15px;display:block">${paths}</svg>`;
}
const ICONS = {
  zap:        I('<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>'),
  monitor:    I('<rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>'),
  cpu:        I('<rect x="4" y="4" width="16" height="16" rx="1"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/><line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/><line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/>'),
  terminal:   I('<polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/>'),
  activity:   I('<polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>'),
  columns:    I('<rect x="3" y="3" width="8" height="18" rx="1"/><rect x="13" y="3" width="8" height="18" rx="1"/>'),
  message:    I('<path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>'),
  folder:     I('<path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>'),
  filetext:   I('<path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>'),
  clock:      I('<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>'),
  barchart:   I('<line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>'),
  settings:   I('<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>'),
  trending:   I('<polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>'),
  lightbulb:  I('<line x1="9" y1="18" x2="15" y2="18"/><line x1="10" y1="22" x2="14" y2="22"/><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0018 8 6 6 0 006 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 018.91 14"/>'),
  lock:       I('<rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>'),
  truck:      I('<rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>'),
  cockpit:    I('<rect x="2" y="7" width="20" height="15" rx="2"/><path d="M16 2l-4 5-4-5"/><circle cx="12" cy="14" r="2"/><line x1="8" y1="14" x2="7" y2="14"/><line x1="16" y1="14" x2="17" y2="14"/>'),
  prism:      I('<polygon points="12 2 2 19 22 19"/><line x1="12" y1="9" x2="12" y2="14"/><line x1="12" y1="17" x2="12.01" y2="17"/>'),
};

async function apiFetch(token, path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
  if (res.status === 401) {
    // Session abgelaufen — auto-logout auslösen
    window.dispatchEvent(new CustomEvent('auth:expired'));
    throw new Error('Session abgelaufen. Bitte neu anmelden.');
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}: ${text}`);
  }
  return res.json();
}

// ── UI-only: Farben pro Projekt-ID (Backend liefert keine Farben) ────────────
const PROJECT_COLORS = {
  'p4a-audit':            '#1a56db',
  'prism-built':          '#059669',
  'sales-coach':          '#7c3aed',
  'dev-dash':             '#0891b2',
  'nfon-audit':           '#dc2626',
  'energy-consulting':    '#10b981',
  'prism-eagleeye':       '#f97316',
  'consulting-dashboard': '#0ea5e9',
};
function getProjectColor(id) {
  if (PROJECT_COLORS[id]) return PROJECT_COLORS[id];
  // Deterministisch aus ID ableiten
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) & 0xffffffff;
  return `hsl(${Math.abs(h) % 360}, 55%, 50%)`;
}

// ── Projekte die noch NICHT im Backend sind (reine UI-Platzhalter) ───────────
const PROJECT_PLACEHOLDERS = [
  { id: 'bpos-secaas',    label: 'Horizon BPOS',            icon: '🌅', description: 'Business Process Operating System · Horizon 9',      domain: '—', stack: [], color: '#f59e0b', placeholder: true, scoping: false },
  { id: 'horizon9-dash',  label: 'HORIZON9 DASHBOARD',      icon: '🌐', description: 'Management-Dashboard für die Horizon9-Plattform.',    domain: '—', stack: [], color: '#8b5cf6', placeholder: true, scoping: false },
  { id: 'sales-automater',label: 'SALES AUTOMATER',         icon: '🤖', description: 'Automatisierter Sales-Funnel mit KI-Unterstützung.',  domain: '—', stack: [], color: '#ef4444', placeholder: true, scoping: false },
];

// ── Context: wird in CockpitPage befüllt, alle Tabs lesen daraus ─────────────
const ProjectsCtx = React.createContext([]);

// Hilfsfunktion: API-Response → Projektliste
function buildProjects(apiData) {
  return Object.entries(apiData).map(([id, cfg]) => ({
    id,
    label:       cfg.label       || id,
    icon:        cfg.icon        || '📦',
    description: cfg.description || '',
    domain:      cfg.domain      || '—',
    stack:       cfg.stack       || [],
    scoping:     cfg.scoping     || false,
    placeholder: false,
    color:       getProjectColor(id),
  }));
}

const KANBAN_COLS = [
  { id: 'TODO',        label: 'TODO',        color: '#4f6ef7' },
  { id: 'IN_PROGRESS', label: 'IN PROGRESS', color: '#f97316' },
  { id: 'DONE',        label: 'DONE',        color: '#22c55e' },
  { id: 'BLOCKED',     label: 'BLOCKED',     color: '#ef4444' },
  { id: 'REJECTED',    label: 'REJECTED',    color: '#9ca3af' },
];

const STATUS_BADGE_CLASS = {
  TODO:        'badge-todo',
  IN_PROGRESS: 'badge-progress',
  DONE:        'badge-done',
  BLOCKED:     'badge-blocked',
  REJECTED:    'badge-blocked', // REJECTED = gleiche Farbe wie BLOCKED
};

const PRIORITY_META = {
  P1: { color: '#ef4444', bg: '#3a1414', label: 'P1' },
  P2: { color: '#f97316', bg: '#3a2010', label: 'P2' },
  P3: { color: '#6b7280', bg: '#1e2235', label: 'P3' },
};

const RISK_META = {
  HIGH:   { color: '#dc2626', bg: '#fee2e2' },
  MEDIUM: { color: '#d97706', bg: '#fef3c7' },
  LOW:    { color: '#059669', bg: '#d1fae5' },
};

const QUICK_ACTIONS = [
  'Zeige aktuellen BACKLOG-Status',
  'Was sind die nächsten Prioritäten?',
  'Analysiere mögliche Blocker',
  'Erstelle neuen Task',
];

function fmtTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
  } catch { return ''; }
}

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', timeZone: 'Europe/Berlin' });
  } catch { return ''; }
}

/* ─────────────────────────────────────────────────────────────────
   Sub-components
───────────────────────────────────────────────────────────────── */

/* Priority badge */
function PriBadge({ priority }) {
  const label = (priority && PRIORITY_META[priority]?.label) || 'P3';
  const cls   = priority === 'P1' ? 'badge-p1' : priority === 'P2' ? 'badge-p2' : 'badge-p3';
  return <span className={`badge ${cls}`}>{label}</span>;
}

/* Status badge */
function StatusBadge({ status }) {
  if (!status) return null;
  const cls   = STATUS_BADGE_CLASS[status] || 'badge-gray';
  const label = status === 'IN_PROGRESS' ? 'IN PROGRESS' : status;
  return <span className={`badge ${cls}`}>{label}</span>;
}

/* Risk badge */
function RiskBadge({ risk }) {
  if (!risk) return null;
  const m = RISK_META[risk] || RISK_META.LOW;
  return (
    <span style={{
      display: 'inline-block', padding: '1px 6px', borderRadius: 10,
      fontSize: 10, fontWeight: 600, background: m.bg, color: m.color,
    }}>{risk}</span>
  );
}

/* Status dot */
function StatusDot({ ok, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: ok ? '#059669' : '#dc2626' }}>
      <span style={{
        width: 7, height: 7, borderRadius: '50%',
        background: ok ? '#059669' : '#dc2626',
        display: 'inline-block',
        boxShadow: ok ? '0 0 5px #05966966' : '0 0 5px #dc262666',
      }} />
      {label}
    </span>
  );
}

/* Spinner */
function Spin({ size = 14 }) {
  return (
    <span
      role="status"
      aria-label="Lädt…"
      style={{
        display: 'inline-block', width: size, height: size,
        border: `2px solid var(--border2)`, borderTopColor: 'var(--accent)',
        borderRadius: '50%', animation: 'spin 0.6s linear infinite', flexShrink: 0,
      }}
    />
  );
}

/* Modal overlay */
function Modal({ children, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg2)', borderRadius: 12, padding: 24,
          maxWidth: 560, width: '100%', maxHeight: '85vh', overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
      >
        {children}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Setup Wizard Modal
───────────────────────────────────────────────────────────────── */
const STACK_OPTIONS = ['React', 'Next.js', 'Vue', 'FastAPI', 'Django', 'Node.js',
  'Supabase', 'PostgreSQL', 'Docker', 'nginx', 'Claude', 'OpenAI', 'Redis', 'TypeScript'];

function SetupWizardModal({ proj, token, onClose, onDone }) {
  const [step,    setStep]    = useState(1);
  const [form,    setForm]    = useState({
    id:          proj.id,
    label:       proj.label,
    domain:      '',
    description: proj.description,
    goal:        '',
    stack:       [],
    customStack: '',
  });
  const [files,    setFiles]   = useState([]);
  const [briefing, setBriefing] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [result,   setResult]   = useState(null);

  const STEPS = ['Rahmenbedingungen', 'Dokumente', 'Claude-Briefing', 'Abschluss'];

  function toggleStack(s) {
    setForm(f => ({
      ...f,
      stack: f.stack.includes(s) ? f.stack.filter(x => x !== s) : [...f.stack, s],
    }));
  }

  async function generateBriefing() {
    setLoading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('data', JSON.stringify(form));
      files.forEach(f => fd.append('docs', f));
      const res = await fetch(`${API}/cockpit/projects/setup/brief`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
      const data = await res.json();
      setBriefing(data.briefing || '');
      setStep(3);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function createProject() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/cockpit/projects/setup/create`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...form, briefing }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
      const data = await res.json();
      setResult(data);
      setStep(4);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  const stack = form.stack.concat(
    form.customStack ? form.customStack.split(',').map(s => s.trim()).filter(Boolean) : []
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg2)', borderRadius: 14, padding: 28,
          maxWidth: 640, width: '100%', maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
          border: `1px solid ${proj.color}44`,
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
              {proj.icon} Setup — {proj.label}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 2 }}>
              Schritt {step} / {STEPS.length} — {STEPS[step - 1]}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>

        {/* Step indicator */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 22 }}>
          {STEPS.map((s, i) => (
            <div key={s} style={{ flex: 1, height: 4, borderRadius: 3,
              background: i < step ? proj.color : 'var(--border)' }} />
          ))}
        </div>

        {error && (
          <div className="alert alert-err" style={{ marginBottom: 14 }}>{error}</div>
        )}

        {/* ── Step 1: Rahmenbedingungen ── */}
        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="field-label">Projekt-ID *</label>
                <input className="field-input" value={form.id}
                  onChange={e => setForm(f => ({ ...f, id: e.target.value.toLowerCase().replace(/\s+/g, '-') }))}
                  placeholder="mein-projekt" />
              </div>
              <div className="form-group">
                <label className="field-label">Label *</label>
                <input className="field-input" value={form.label}
                  onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                  placeholder="MEIN PROJEKT" />
              </div>
            </div>
            <div className="form-group">
              <label className="field-label">Domain / URL</label>
              <input className="field-input" value={form.domain}
                onChange={e => setForm(f => ({ ...f, domain: e.target.value }))}
                placeholder="meinprojekt.example.com" />
            </div>
            <div className="form-group">
              <label className="field-label">Beschreibung</label>
              <textarea className="field-input form-textarea" rows={2} value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Kurze Beschreibung des Projekts" />
            </div>
            <div className="form-group">
              <label className="field-label">Ziel / MVP</label>
              <textarea className="field-input form-textarea" rows={3} value={form.goal}
                onChange={e => setForm(f => ({ ...f, goal: e.target.value }))}
                placeholder="Was soll das Projekt erreichen? Was ist der MVP?" />
            </div>
            <div className="form-group">
              <label className="field-label">Tech-Stack</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {STACK_OPTIONS.map(s => (
                  <button key={s} onClick={() => toggleStack(s)}
                    style={{
                      padding: '3px 10px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
                      border: `1px solid ${form.stack.includes(s) ? proj.color : 'var(--border)'}`,
                      background: form.stack.includes(s) ? `${proj.color}22` : 'var(--bg3)',
                      color: form.stack.includes(s) ? proj.color : 'var(--text3)',
                    }}>{s}</button>
                ))}
              </div>
              <input className="field-input" value={form.customStack}
                onChange={e => setForm(f => ({ ...f, customStack: e.target.value }))}
                placeholder="Weitere (kommagetrennt): Laravel, Go, …" />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
              <button className="btn btn-primary"
                onClick={() => setStep(2)}
                disabled={!form.id.trim() || !form.label.trim()}
              >
                Weiter →
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2: Dokumente hochladen ── */}
        {step === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.6 }}>
              Optional: Lade PDFs oder Textdokumente aus bestehenden Claude Sessions hoch.
              Sie werden in <code style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>/memory/</code> des Projekts gespeichert
              und von Claude als Kontext genutzt.
            </div>
            <div
              style={{
                border: `2px dashed ${files.length ? proj.color : 'var(--border)'}`,
                borderRadius: 10, padding: '24px 16px', textAlign: 'center',
                background: 'var(--bg3)', cursor: 'pointer',
              }}
              onDragOver={e => e.preventDefault()}
              onDrop={e => {
                e.preventDefault();
                const MAX = 10 * 1024 * 1024;
                const valid = Array.from(e.dataTransfer.files).filter(f => {
                  if (f.size > MAX) { alert(`"${f.name}" ist zu groß (max. 10 MB).`); return false; }
                  return true;
                });
                setFiles(prev => [...prev, ...valid]);
              }}
              onClick={() => document.getElementById('wizard-file-input').click()}
            >
              <div style={{ fontSize: 28, marginBottom: 8 }}>📄</div>
              <div style={{ fontSize: 13, color: 'var(--text2)' }}>
                {files.length ? `${files.length} Datei(en) ausgewählt` : 'Hier klicken oder Dateien hierher ziehen'}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 4 }}>PDF, TXT, MD — max. 10 MB pro Datei</div>
              <input
                id="wizard-file-input" type="file" multiple accept=".pdf,.txt,.md"
                style={{ display: 'none' }}
                onChange={e => {
                const MAX = 10 * 1024 * 1024; // 10 MB
                const valid = Array.from(e.target.files).filter(f => {
                  if (f.size > MAX) { alert(`"${f.name}" ist zu groß (max. 10 MB).`); return false; }
                  return true;
                });
                setFiles(prev => [...prev, ...valid]);
              }}
              />
            </div>
            {files.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {files.map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 10px', background: 'var(--bg3)', borderRadius: 6,
                    border: '1px solid var(--border)', fontSize: 12.5 }}>
                    <span style={{ flex: 1, color: 'var(--text)' }}>📄 {f.name}</span>
                    <span style={{ color: 'var(--text3)' }}>{(f.size / 1024).toFixed(0)} KB</span>
                    <button style={{ background: 'none', border: 'none', color: 'var(--text3)',
                      cursor: 'pointer', fontSize: 14, padding: '0 2px' }}
                      onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}>✕</button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 6 }}>
              <button className="btn btn-ghost" onClick={() => setStep(1)}>← Zurück</button>
              <button className="btn btn-primary" onClick={() => { setError(null); generateBriefing(); }} disabled={loading}>
                {loading ? <><Spin /> Claude analysiert…</> : error ? '↻ Erneut versuchen' : 'Weiter → Claude Briefing'}
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3: Claude-Briefing ── */}
        {step === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.6 }}>
              Claude hat das Projekt analysiert. Überprüfe und ergänze das Briefing — es wird als
              Grundlage für die <code style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>CLAUDE.md</code> verwendet.
            </div>
            <textarea
              className="field-input form-textarea"
              rows={14}
              value={briefing}
              onChange={e => setBriefing(e.target.value)}
              style={{ fontFamily: 'var(--mono)', fontSize: 12 }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 6 }}>
              <button className="btn btn-ghost" onClick={() => setStep(2)}>← Zurück</button>
              <button className="btn btn-primary" onClick={createProject} disabled={loading || !briefing.trim()}>
                {loading ? <><Spin /> Projekt wird angelegt…</> : 'Projekt anlegen →'}
              </button>
            </div>
          </div>
        )}

        {/* ── Step 4: Abschluss ── */}
        {step === 4 && result && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>✅</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>
                Projekt angelegt!
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--text2)' }}>
                <code style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{result.path}</code>
              </div>
            </div>
            <div style={{ background: 'var(--bg3)', borderRadius: 8, padding: 14,
              border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text2)', marginBottom: 8 }}>
                Angelegte Dateien:
              </div>
              {(result.files || []).map(f => (
                <div key={f} style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text3)',
                  padding: '2px 0' }}>📄 {f}</div>
              ))}
            </div>
            {result.next_steps && (
              <div style={{ fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.7,
                background: `${proj.color}11`, borderRadius: 8, padding: 12,
                border: `1px solid ${proj.color}33` }}>
                <strong>Nächste Schritte:</strong><br />
                <pre style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap', fontSize: 12,
                  fontFamily: 'var(--mono)', color: 'var(--text2)' }}>{result.next_steps}</pre>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
              <button className="btn btn-primary" onClick={() => { onDone && onDone(result); onClose(); }}>
                Fertig
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Tab 1 — Kanban
───────────────────────────────────────────────────────────────── */
function KanbanTab({ token, onOpenDiscuss }) {
  const { projects, taskVersion } = useContext(ProjectsCtx);
  const [activeProjects, setActiveProjects] = useState(null); // null = nicht initialisiert; wird aus projects abgeleitet
  // Sobald Projekte geladen sind, alle aktiven vorselektieren
  useEffect(() => {
    if (projects.length > 0 && activeProjects === null) {
      setActiveProjects(projects.filter(p => !p.placeholder && !p.scoping).map(p => p.id));
    }
  }, [projects, activeProjects]);
  const [tasks, setTasks]           = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [selected, setSelected]     = useState(null);
  const [unblockTask, setUnblockTask] = useState(null);
  const [orchData, setOrchData]     = useState(null);
  const [orchOpen, setOrchOpen]     = useState(true);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch(token, '/cockpit/backlogs');
      const all = [];
      for (const [pid, pdata] of Object.entries(data)) {
        if ((activeProjects || []).includes(pid)) {
          (pdata.tasks || []).forEach(t => all.push({ ...t, _project: pid }));
        }
      }
      setTasks(all);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token, activeProjects]);

  const loadOrch = useCallback(async () => {
    try {
      const d = await apiFetch(token, '/cockpit/orchestrator/status');
      setOrchData(d);
    } catch(e) { console.warn("[dev-dash]", e?.message || e); }
  }, [token]);

  useEffect(() => { loadTasks(); loadOrch(); }, [loadTasks, loadOrch]);
  // Re-fetch when a task is created from another tab (ProjekteTab etc.)
  useEffect(() => { if (taskVersion > 0) { loadTasks(); } }, [taskVersion, loadTasks]);

  function toggleProject(pid) {
    setActiveProjects(prev =>
      (prev || []).includes(pid) ? (prev || []).filter(p => p !== pid) : [...(prev || []), pid]
    );
    setOrchData(null); // Veraltete Orchestrator-Daten beim Projektwechsel leeren
    setSelected(null); // Offenen Task-Modal schließen
  }

  const colTasks = col => tasks.filter(t => t.status === col);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* ── Orchestrator Plan Panel ─────────────────────────────── */}
      {orchData && (
        <div className="card" style={{ borderLeft: '3px solid var(--accent)' }}>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: orchOpen ? 10 : 0 }}
            onClick={() => setOrchOpen(o => !o)}
          >
            <span style={{ fontSize: 14 }}>🤖</span>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text)' }}>Orchestrator-Plan</span>
            <span style={{ fontSize: 10.5, color: 'var(--text3)', marginLeft: 4 }}>
              {orchData.generated_at?.slice(11, 16)} Uhr
              {orchData.summary && ` · ${orchData.summary.total_todo || 0} offen · ${orchData.summary.total_wip || 0} laufend · ${orchData.summary.total_blocked || 0} blockiert`}
            </span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>{orchOpen ? '▲' : '▼'}</span>
          </div>
          {orchOpen && (
            <div>
              {/* Blockers */}
              {(orchData.blockers || []).length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 5 }}>
                    Blocker ({orchData.blockers.length})
                  </div>
                  {orchData.blockers.map((t, i) => (
                    <div key={i} onClick={() => setSelected({ ...t, _project: t.project })} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px',
                      background: '#fee2e211', border: '1px solid #dc262633', borderRadius: 6, marginBottom: 4,
                      cursor: 'pointer',
                    }}>
                      <span style={{ fontSize: 11 }}>{projects.find(p => p.id === t.project)?.icon || '📦'}</span>
                      <PriBadge priority={t.priority} />
                      <span style={{ fontSize: 12, color: 'var(--text)', flex: 1 }}>{t.title}</span>
                      <span style={{ fontSize: 10, color: 'var(--text3)' }}>{t.label}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* Next priorities */}
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 5 }}>
                  Nächste Prioritäten
                </div>
                {(orchData.next_priorities || []).slice(0, 8).map((t, i) => {
                  const proj = projects.find(p => p.id === t.project);
                  const isWip = t.status === 'IN_PROGRESS';
                  return (
                    <div key={i} onClick={() => setSelected({ ...t, _project: t.project })} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px',
                      background: isWip ? 'var(--accent-light)' : 'var(--bg3)',
                      border: `1px solid ${isWip ? 'var(--accent)' : 'var(--border)'}`,
                      borderRadius: 6, marginBottom: 4, cursor: 'pointer',
                    }}>
                      <span style={{ fontSize: 11 }}>{proj?.icon || '📦'}</span>
                      <PriBadge priority={t.priority} />
                      {isWip && <span className="badge badge-progress" style={{ fontSize: 9 }}>WIP</span>}
                      <span style={{ fontSize: 12, color: 'var(--text)', flex: 1 }}>{t.title}</span>
                      <span style={{ fontSize: 10, color: 'var(--text3)' }}>{t.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Project filter + refresh ─────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11.5, color: 'var(--text3)', fontWeight: 500 }}>Filter:</span>
        {projects.filter(p => !p.placeholder && !p.scoping).map(p => (
          <button
            key={p.id}
            onClick={() => toggleProject(p.id)}
            style={{
              padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500,
              border: `1px solid ${(activeProjects || []).includes(p.id) ? p.color : 'var(--border)'}`,
              background: (activeProjects || []).includes(p.id) ? `${p.color}18` : 'var(--bg3)',
              color: (activeProjects || []).includes(p.id) ? p.color : 'var(--text3)',
              cursor: 'pointer', transition: 'all 0.15s',
            }}
          >
            {p.icon} {p.label}
          </button>
        ))}
        <button className="btn btn-secondary btn-sm" onClick={() => { loadTasks(); loadOrch(); }} style={{ marginLeft: 'auto' }}>
          {loading ? <Spin /> : '↻'} Refresh
        </button>
      </div>

      {error && (
        <div className="alert alert-err" style={{ marginBottom: 12 }}>
          Fehler beim Laden: {error}
        </div>
      )}

      {/* Kanban board — 4 columns (BLOCKED hat eigene Sektion) */}
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(180px, 1fr))',
        gap: 12,
        minWidth: 760,
      }}>
        {KANBAN_COLS.filter(c => c.id !== 'BLOCKED').map(col => (
          <div key={col.id} style={{
            background: 'var(--bg3)', borderRadius: 10,
            padding: '10px 8px', minWidth: 200,
          }}>
            {/* Column header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: 10, padding: '0 4px',
            }}>
              <span style={{
                fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: 1, color: col.color,
              }}>{col.label}</span>
              <span style={{
                fontSize: 11, fontWeight: 600, color: 'var(--text3)',
                background: 'var(--bg4)', borderRadius: 10, padding: '1px 7px',
              }}>{colTasks(col.id).length}</span>
            </div>

            {/* Cards */}
            <div className="kanban-col-scroll" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {loading && colTasks(col.id).length === 0 && (
                <div style={{ textAlign: 'center', padding: 20 }}><Spin size={18} /></div>
              )}
              {colTasks(col.id).map(task => (
                <KanbanCard key={task.id} task={task} onClick={() => setSelected(task)} />
              ))}
              {!loading && colTasks(col.id).length === 0 && (
                <div style={{
                  textAlign: 'center', padding: '20px 8px',
                  border: '1px dashed var(--border)', borderRadius: 8,
                }}>
                  <div style={{ fontSize: 20, marginBottom: 5, opacity: 0.35 }}>
                    {col.id === 'TODO' ? '📋' : col.id === 'IN_PROGRESS' ? '⚡' : col.id === 'DONE' ? '✅' : '🚫'}
                  </div>
                  <div style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--text2)' }}>Keine Tasks</div>
                  <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 3 }}>
                    {col.id === 'TODO' ? 'Alle Tasks in Arbeit' : col.id === 'IN_PROGRESS' ? 'Nichts aktiv' : col.id === 'DONE' ? 'Noch nichts erledigt' : 'Kein Blocker'}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      </div>

      {/* ── Blockiert-Sektion ─────────────────────────────────────── */}
      {(colTasks('BLOCKED').length > 0 || loading) && (
        <div style={{ marginTop: 4 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
            borderBottom: '2px solid #ef444433', paddingBottom: 8,
          }}>
            <span style={{ fontSize: 16 }}>🚧</span>
            <span style={{
              fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: 1.2, color: '#ef4444',
            }}>Blockiert</span>
            <span style={{
              fontSize: 11, fontWeight: 600, color: '#ef4444',
              background: '#ef444422', border: '1px solid #ef444433',
              borderRadius: 10, padding: '1px 8px',
            }}>{colTasks('BLOCKED').length}</span>
            <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 4 }}>
              — Blockierte Tasks brauchen eine Entscheidung bevor sie weiterlaufen können
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {loading && colTasks('BLOCKED').length === 0 && (
              <div style={{ textAlign: 'center', padding: 20 }}><Spin size={18} /></div>
            )}
            {colTasks('BLOCKED').map(task => {
              const proj = projects.find(p => p.id === task._project);
              return (
                <div key={task.id} style={{
                  background: '#fee2e210',
                  border: '1px solid #ef444455',
                  borderLeft: '4px solid #ef4444',
                  borderRadius: 8, padding: '10px 14px',
                  display: 'flex', alignItems: 'flex-start', gap: 12,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <PriBadge priority={task.priority} />
                      {task.risk && <RiskBadge risk={task.risk} />}
                      {proj && (
                        <span style={{ fontSize: 10, color: 'var(--text3)' }}>{proj.icon} {task._project}</span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#fca5a5', marginBottom: task.description ? 4 : 0, lineHeight: 1.35 }}>
                      {task.title}
                    </div>
                    {task.description && (
                      <div style={{ fontSize: 11.5, color: 'var(--text3)', lineHeight: 1.5 }}>
                        {task.description.length > 120 ? task.description.slice(0, 120) + '…' : task.description}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                    <button
                      className="btn btn-primary btn-sm"
                      style={{ background: '#16a34a', borderColor: '#16a34a', fontSize: 11.5, whiteSpace: 'nowrap' }}
                      onClick={() => setUnblockTask(task)}
                    >
                      ✓ Entblocken
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}
                      onClick={() => setSelected(task)}
                    >
                      ℹ Details
                    </button>
                    {onOpenDiscuss && (
                      <button
                        className="btn btn-secondary btn-sm"
                        style={{ fontSize: 11.5, whiteSpace: 'nowrap', color: 'var(--accent)', borderColor: 'var(--accent)' }}
                        onClick={() => {
                          const context = [
                            `Ich brauche Hilfe beim Entblocken dieses Tasks:`,
                            ``,
                            `**Task**: ${task.title}`,
                            `**Projekt**: ${task._project}`,
                            task.description ? `**Beschreibung**: ${task.description}` : null,
                            task.acceptance_criteria ? `**Akzeptanzkriterien**: ${task.acceptance_criteria}` : null,
                            ``,
                            `Was sind die Optionen, diesen Block aufzulösen?`,
                          ].filter(Boolean).join('\n');
                          onOpenDiscuss(task._project, context);
                        }}
                      >
                        💬 Mit Claude besprechen
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {selected && (
        <TaskDetailModal
          task={selected}
          token={token}
          onClose={() => { setSelected(null); }}
          onRefresh={() => { loadTasks(); loadOrch(); }}
        />
      )}
      {unblockTask && (
        <UnblockModal
          task={unblockTask}
          token={token}
          onClose={() => setUnblockTask(null)}
          onRefresh={() => { loadTasks(); loadOrch(); }}
          onOpenDiscuss={onOpenDiscuss}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Shared Task Detail Modal — wiederverwendbar in allen Tabs
   task muss haben: .title, ._project ODER .project, .id
───────────────────────────────────────────────────────────────── */
function TaskDetailModal({ task, token, onClose, onRefresh }) {
  const { projects } = useContext(ProjectsCtx);
  const pid = task._project || task.project || '';
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg,     setActionMsg]     = useState(null);
  const [runNote,       setRunNote]       = useState('');
  const [runLoading,    setRunLoading]    = useState(false);
  const [fixData,       setFixData]       = useState(null);
  const [fixLoading,    setFixLoading]    = useState(false);

  async function handleApproval(action) {
    setActionLoading(true); setActionMsg(null);
    try {
      await apiFetch(token, `/cockpit/registry/approve/${pid}/${task.id}`, {
        method: 'POST', body: JSON.stringify({ action }),
      });
      setActionMsg({ ok: true, text: `Task ${action === 'accept' ? 'gestartet' : 'blockiert'}.` });
      onRefresh?.();
      setTimeout(onClose, 1200);
    } catch (e) { setActionMsg({ ok: false, text: e.message }); }
    finally { setActionLoading(false); }
  }

  async function handleRunTask() {
    setRunLoading(true); setActionMsg(null);
    try {
      await apiFetch(token, `/cockpit/registry/run-task/${pid}`, {
        method: 'POST',
        body: JSON.stringify({ task_title: task.title, task_id: task.id, note: runNote }),
      });
      setActionMsg({ ok: true, text: `▶ Task "${task.title}" angestoßen — Claude arbeitet im Hintergrund.` });
      setRunNote('');
      onRefresh?.();
    } catch (e) { setActionMsg({ ok: false, text: e.message }); }
    finally { setRunLoading(false); }
  }

  async function handleFixTask() {
    if (!fixData) return;
    setFixLoading(true); setActionMsg(null);
    try {
      await apiFetch(token, `/cockpit/registry/fix-task/${pid}`, {
        method: 'PATCH',
        body: JSON.stringify({
          raw_line:     task.raw_line,
          new_status:   fixData.status,
          new_priority: fixData.priority,
          new_title:    fixData.title,
        }),
      });
      setActionMsg({ ok: true, text: '✓ Task erfolgreich repariert und gespeichert.' });
      setFixData(null);
      onRefresh?.();
      setTimeout(onClose, 1400);
    } catch (e) { setActionMsg({ ok: false, text: e.message }); }
    finally { setFixLoading(false); }
  }

  const projObj = projects.find(p => p.id === pid);

  return (
    <Modal onClose={onClose}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
            <PriBadge priority={task.priority} />
            <RiskBadge risk={task.risk} />
            {projObj && <span style={{ fontSize: 10.5, color: 'var(--text3)' }}>{projObj.icon} {pid}</span>}
            <StatusBadge status={task.status} />
            {task.malformed && (
              <span style={{ fontSize: 10, fontWeight: 700, color: '#ef4444', background: '#dc262618', border: '1px solid #dc262633', borderRadius: 4, padding: '1px 6px' }}>⚠ FEHLERHAFT</span>
            )}
          </div>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: task.malformed ? '#ef4444' : 'var(--text)' }}>{task.title}</h3>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
      </div>

      {/* Malformed Fix Editor */}
      {task.malformed && (
        <div style={{ background: '#fee2e214', border: '1px solid #dc2626', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#ef4444', marginBottom: 6 }}>⚠ Fehlerhafte BACKLOG-Zeile erkannt</div>
          <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#fca5a5', background: '#1a0000', borderRadius: 5, padding: '8px 10px', marginBottom: 10, wordBreak: 'break-all', lineHeight: 1.5 }}>
            {task.raw_line}
          </div>
          {task.parse_warning && <div style={{ fontSize: 11.5, color: '#fca5a5', marginBottom: 10, lineHeight: 1.5 }}>{task.parse_warning}</div>}
          {!fixData ? (
            <button className="btn btn-primary" style={{ fontSize: 12, background: '#dc2626', borderColor: '#dc2626' }}
              onClick={() => setFixData({ status: task.status, priority: task.priority, title: task.title.replace(/\[(?:TODO|IN_PROGRESS|DONE|BLOCKED|REJECTED)\]\s+\[P\d+\]\s*/g, '').trim() })}>
              ✎ Task reparieren
            </button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div className="field-label" style={{ marginBottom: 3 }}>Status</div>
                  <select className="field-input" value={fixData.status} onChange={e => setFixData(d => ({ ...d, status: e.target.value }))} style={{ fontSize: 12 }}>
                    {['TODO','IN_PROGRESS','DONE','BLOCKED','REJECTED'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <div className="field-label" style={{ marginBottom: 3 }}>Priorität</div>
                  <select className="field-input" value={fixData.priority} onChange={e => setFixData(d => ({ ...d, priority: e.target.value }))} style={{ fontSize: 12 }}>
                    {['P1','P2','P3','P4','P5'].map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <input className="field-input" value={fixData.title} onChange={e => setFixData(d => ({ ...d, title: e.target.value }))} style={{ fontSize: 12 }} placeholder="Korrekter Titel" />
              <div style={{ fontSize: 10.5, color: 'var(--text3)', fontFamily: 'monospace', background: 'var(--bg3)', borderRadius: 4, padding: '5px 8px' }}>
                Vorschau: ### [{fixData.status}] [{fixData.priority}] {fixData.title}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" disabled={fixLoading || !fixData.title.trim()} onClick={handleFixTask} style={{ background: '#16a34a', borderColor: '#16a34a' }}>
                  {fixLoading ? <Spin size={12} /> : '✓'} Speichern
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setFixData(null)}>Abbrechen</button>
              </div>
            </div>
          )}
        </div>
      )}

      {task.description && (
        <div style={{ marginBottom: 14 }}>
          <div className="field-label">Beschreibung</div>
          <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, margin: 0 }}>{task.description}</p>
        </div>
      )}
      {task.acceptance_criteria && (
        <div style={{ marginBottom: 14 }}>
          <div className="field-label">Akzeptanzkriterien</div>
          <p style={{ fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>{task.acceptance_criteria}</p>
        </div>
      )}
      {task.context && (
        <div style={{ marginBottom: 14 }}>
          <div className="field-label">Kontext</div>
          <p style={{ fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.6, margin: 0 }}>{task.context}</p>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        {task.status && <StatusBadge status={task.status} />}
        {task.created_at && <span style={{ fontSize: 11, color: 'var(--text3)' }}>Erstellt: {fmtDate(task.created_at)}</span>}
      </div>

      {actionMsg && (
        <div className={`alert ${actionMsg.ok ? 'alert-ok' : 'alert-err'}`} style={{ marginBottom: 12 }}>{actionMsg.text}</div>
      )}

      {task.status === 'TODO' && (task.risk === 'HIGH' || task.risk === 'MEDIUM') && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <button className="btn btn-primary" disabled={actionLoading} onClick={() => handleApproval('accept')}>
            {actionLoading ? <Spin /> : '✓'} Akzeptieren
          </button>
          <button className="btn btn-secondary" disabled={actionLoading} onClick={() => handleApproval('reject')} style={{ color: 'var(--red)', borderColor: 'var(--red)' }}>
            ✕ Ablehnen
          </button>
        </div>
      )}

      {(task.status === 'TODO' || task.status === 'IN_PROGRESS') && (
        <div style={{ marginTop: 8, padding: '12px 14px', borderRadius: 8, background: 'var(--bg3)', border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text2)', marginBottom: 8 }}>▶ Task direkt anstoßen</div>
          <textarea className="field-input form-textarea" rows={2} value={runNote} onChange={e => setRunNote(e.target.value)}
            placeholder="Optionaler Hinweis für Claude (z.B. 'Nur Backend, kein Frontend')" style={{ fontSize: 12, marginBottom: 8 }} />
          <button className="btn btn-primary" disabled={runLoading} onClick={handleRunTask} style={{ width: '100%' }}>
            {runLoading ? <><Spin size={12} /> Claude wird beauftragt…</> : '▶ Jetzt umsetzen lassen'}
          </button>
        </div>
      )}
    </Modal>
  );
}

/* ─────────────────────────────────────────────────────────────────
   UnblockModal — Dialog zum Entblocken eines BLOCKED-Tasks
───────────────────────────────────────────────────────────────── */
function UnblockModal({ task, token, onClose, onRefresh, onOpenDiscuss }) {
  const pid = task._project || task.project || '';
  const [decision, setDecision]       = useState('');
  const [newDesc, setNewDesc]         = useState(task.description || '');
  const [editDesc, setEditDesc]       = useState(false);
  const [saving, setSaving]           = useState(false);
  const [msg, setMsg]                 = useState(null);

  async function handleUnblock() {
    if (!decision.trim()) {
      setMsg({ ok: false, text: 'Bitte eine Entscheidung / neue Informationen eintragen.' });
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      const updatedTitle = task.title;
      const updatedDesc  = editDesc ? newDesc : task.description;
      await apiFetch(token, `/cockpit/registry/fix-task/${pid}`, {
        method: 'PATCH',
        body: JSON.stringify({
          raw_line:     task.raw_line,
          new_status:   'TODO',
          new_priority: task.priority,
          new_title:    updatedTitle,
          new_description: updatedDesc ? `${updatedDesc}\n\nEntscheidung: ${decision.trim()}` : `Entscheidung: ${decision.trim()}`,
        }),
      });
      setMsg({ ok: true, text: '✓ Task entblockt und auf TODO gesetzt.' });
      onRefresh?.();
      setTimeout(onClose, 1200);
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
            <span style={{
              fontSize: 10, fontWeight: 700, color: '#ef4444',
              background: '#dc262618', border: '1px solid #dc262633',
              borderRadius: 4, padding: '2px 7px', letterSpacing: 0.5,
            }}>BLOCKED</span>
            <PriBadge priority={task.priority} />
            {task.risk && <RiskBadge risk={task.risk} />}
            <span style={{ fontSize: 10.5, color: 'var(--text3)' }}>{pid}</span>
          </div>
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, color: 'var(--text)' }}>{task.title}</h3>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
      </div>

      {/* Block-Grund */}
      {task.description && (
        <div style={{
          background: '#fee2e210', border: '1px solid #ef444433',
          borderRadius: 8, padding: '10px 14px', marginBottom: 14,
        }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 5 }}>
            Block-Kontext
          </div>
          <p style={{ fontSize: 12.5, color: 'var(--text2)', margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {task.description}
          </p>
          {task.acceptance_criteria && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #ef444422' }}>
              <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 600, marginBottom: 3 }}>Akzeptanzkriterien</div>
              <p style={{ fontSize: 12, color: 'var(--text3)', margin: 0, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{task.acceptance_criteria}</p>
            </div>
          )}
        </div>
      )}

      {/* Entscheidungs-Eingabe */}
      <div style={{ marginBottom: 14 }}>
        <div className="field-label" style={{ marginBottom: 5 }}>
          Entscheidung / Neue Informationen <span style={{ color: '#ef4444' }}>*</span>
        </div>
        <textarea
          className="field-input form-textarea"
          rows={3}
          value={decision}
          onChange={e => setDecision(e.target.value)}
          placeholder="z.B. 'Blockchain-Ansatz verworfen, stattdessen tamper-evident Hashing. Abhängigkeit von Lib X durch Y ersetzt.'"
          style={{ fontSize: 12.5 }}
          autoFocus
        />
      </div>

      {/* Optionale Beschreibungsanpassung */}
      <div style={{ marginBottom: 16 }}>
        <button
          type="button"
          onClick={() => setEditDesc(v => !v)}
          style={{
            background: 'none', border: 'none', padding: 0,
            fontSize: 12, color: 'var(--accent)', cursor: 'pointer',
            textDecoration: 'underline', fontFamily: 'var(--font)',
          }}
        >
          {editDesc ? '▼ Beschreibung ausblenden' : '▶ Beschreibung anpassen (optional)'}
        </button>
        {editDesc && (
          <textarea
            className="field-input form-textarea"
            rows={4}
            value={newDesc}
            onChange={e => setNewDesc(e.target.value)}
            style={{ fontSize: 12, marginTop: 8 }}
            placeholder="Neue Beschreibung für den Task..."
          />
        )}
      </div>

      {msg && (
        <div className={`alert ${msg.ok ? 'alert-ok' : 'alert-err'}`} style={{ marginBottom: 12 }}>
          {msg.text}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          className="btn btn-primary"
          disabled={saving || !decision.trim()}
          onClick={handleUnblock}
          style={{ background: '#16a34a', borderColor: '#16a34a' }}
        >
          {saving ? <><Spin size={12} /> Speichern…</> : '✓ Entblocken & Status → TODO'}
        </button>
        {onOpenDiscuss && (
          <button
            className="btn btn-secondary"
            onClick={() => {
              const context = [
                `Ich brauche Hilfe beim Entblocken dieses Tasks:`,
                ``,
                `**Task**: ${task.title}`,
                `**Projekt**: ${pid}`,
                task.description ? `**Beschreibung**: ${task.description}` : null,
                task.acceptance_criteria ? `**Akzeptanzkriterien**: ${task.acceptance_criteria}` : null,
                ``,
                `Was sind die Optionen, diesen Block aufzulösen?`,
              ].filter(Boolean).join('\n');
              onClose();
              onOpenDiscuss(pid, context);
            }}
            style={{ color: 'var(--accent)', borderColor: 'var(--accent)' }}
          >
            💬 Mit Claude besprechen
          </button>
        )}
        <button className="btn btn-secondary btn-sm" onClick={onClose} style={{ marginLeft: 'auto' }}>
          Abbrechen
        </button>
      </div>
    </Modal>
  );
}

function KanbanCard({ task, onClick }) {
  const { projects } = useContext(ProjectsCtx);
  const proj = projects.find(p => p.id === task._project);
  const desc = task.description || '';
  const snippet = desc.length > 80 ? desc.slice(0, 80) + '…' : desc;
  const isErr = !!task.malformed;

  return (
    <div
      onClick={onClick}
      style={{
        background: isErr ? '#fee2e210' : 'var(--bg2)',
        border: `1px solid ${isErr ? '#dc2626' : 'var(--border)'}`,
        borderRadius: 8, padding: '10px 12px', cursor: 'pointer',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        boxShadow: isErr ? '0 0 0 1px #dc262622' : 'var(--shadow-sm)',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = isErr ? '#ef4444' : 'var(--accent)';
        e.currentTarget.style.boxShadow = 'var(--shadow)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = isErr ? '#dc2626' : 'var(--border)';
        e.currentTarget.style.boxShadow = isErr ? '0 0 0 1px #dc262622' : 'var(--shadow-sm)';
      }}
    >
      {isErr && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 5,
          background: '#dc262618', border: '1px solid #dc262633',
          borderRadius: 5, padding: '3px 7px', marginBottom: 7,
          fontSize: 10.5, color: '#ef4444', fontWeight: 600,
        }}>
          <span>⚠</span> Fehlerhafte BACKLOG-Zeile — zum Reparieren öffnen
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
        <PriBadge priority={task.priority} />
        {task.risk && <RiskBadge risk={task.risk} />}
        {proj && (
          <span style={{ fontSize: 9.5, color: 'var(--text3)', marginLeft: 'auto' }}>{proj.icon}</span>
        )}
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: isErr ? '#ef4444' : 'var(--text)', marginBottom: 4, lineHeight: 1.35 }}>
        {task.title}
      </div>
      {snippet && (
        <div style={{ fontSize: 11.5, color: 'var(--text3)', lineHeight: 1.5 }}>{snippet}</div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Tab 2 — Diskussion
───────────────────────────────────────────────────────────────── */
function DiskussionTab({ token, lastProject, onProjectChange, initialMessage, onInitialMessageUsed }) {
  const { projects } = useContext(ProjectsCtx);
  const [project, setProject]   = useState(lastProject || 'bpos');
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [histLoading, setHistLoading] = useState(false);
  const [projectError, setProjectError] = useState(null);
  const bottomRef = useRef(null);

  // Vorgeladener Kontext aus Kanban (z.B. BLOCKED-Task besprechen)
  useEffect(() => {
    if (initialMessage) {
      setInput(initialMessage);
      onInitialMessageUsed?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMessage]);

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const loadHistory = useCallback(async () => {
    setHistLoading(true);
    setProjectError(null);
    try {
      const data = await apiFetch(token, `/cockpit/discuss/${project}`);
      setMessages(data.messages || data.history || []);
    } catch(e) {
      setMessages([]);
      if (e.message.includes('404')) {
        setProjectError(`Projekt "${project}" ist im Backend nicht konfiguriert — Nachrichten werden nicht gespeichert!`);
      }
    } finally {
      setHistLoading(false);
    }
  }, [token, project]);

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  function handleProjectChange(pid) {
    setProject(pid);
    onProjectChange(pid);
    setMessages([]);
    setProjectError(null);
  }

  async function sendMessage(text) {
    const msg = text || input.trim();
    if (!msg) return;
    if (projectError) {
      alert(`Dieses Projekt ist im Backend nicht konfiguriert.\nNachrichten können nicht gespeichert werden.\n\nBitte zuerst das Projekt in cockpit.py eintragen.`);
      return;
    }
    setInput('');

    const userMsg = { role: 'user', content: msg, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    // Placeholder
    const placeholderId = Date.now();
    setMessages(prev => [...prev, { role: 'assistant', content: '…', timestamp: null, _id: placeholderId, _loading: true }]);

    try {
      const data = await apiFetch(token, `/cockpit/discuss/${project}`, {
        method: 'POST',
        body: JSON.stringify({ message: msg }),
      });
      setMessages(prev => prev
        .filter(m => m._id !== placeholderId)
        .concat([{ role: 'assistant', content: data.response || data.message || JSON.stringify(data), timestamp: new Date().toISOString() }])
      );
    } catch (e) {
      setMessages(prev => prev
        .filter(m => m._id !== placeholderId)
        .concat([{ role: 'assistant', content: `Fehler: ${e.message}`, timestamp: new Date().toISOString(), _error: true }])
      );
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100dvh - 220px)', gap: 12 }}>
      {/* Project selector + clear */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <select
          className="field-input"
          value={project}
          onChange={e => handleProjectChange(e.target.value)}
          style={{ maxWidth: 220 }}
        >
          {projects.filter(p => !p.placeholder && !p.scoping).map(p => (
            <option key={p.id} value={p.id}>{p.icon} {p.label}</option>
          ))}
        </select>
        {histLoading && <Spin />}
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => setMessages([])}
          style={{ marginLeft: 'auto' }}
        >
          Chat leeren
        </button>
        <button className="btn btn-secondary btn-sm" onClick={loadHistory}>
          ↻ Verlauf
        </button>
      </div>

      {/* Project not configured warning */}
      {projectError && (
        <div style={{
          background: 'rgba(239,68,68,0.12)', border: '1px solid #ef4444',
          borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#ef4444' }}>Projekt nicht im Backend konfiguriert</div>
            <div style={{ fontSize: 11.5, color: 'var(--text2)', marginTop: 2 }}>{projectError}</div>
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {QUICK_ACTIONS.map(a => (
          <button
            key={a}
            className="btn btn-secondary btn-sm"
            onClick={() => sendMessage(a)}
            disabled={loading}
            style={{ fontSize: 11.5 }}
          >
            {a}
          </button>
        ))}
      </div>

      {/* Messages */}
      <div style={{
        flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column',
        gap: 10, padding: '8px 4px',
        background: 'var(--bg3)', borderRadius: 10,
        border: '1px solid var(--border)',
      }}>
        {messages.length === 0 && !histLoading && (
          <div className="empty-state">
            <div className="empty-icon">💬</div>
            <div className="empty-text">Starte eine Diskussion</div>
            <div className="empty-hint">Frage Claude über das Projekt oder lass dir den Backlog-Status zeigen.</div>
          </div>
        )}
        {messages.map((m, i) => (
          <ChatMessage key={i} msg={m} />
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <textarea
          className="field-input chat-input-area"
          placeholder="Nachricht eingeben… (Enter = senden, Shift+Enter = Zeile)"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
          disabled={loading}
          style={{ flex: 1, resize: 'none', fontFamily: 'var(--font)' }}
        />
        <button
          className="btn btn-primary"
          onClick={() => sendMessage()}
          disabled={loading || !input.trim()}
          style={{ padding: '10px 16px', alignSelf: 'stretch' }}
        >
          {loading ? <Spin /> : '➤'}
        </button>
      </div>
    </div>
  );
}

function ChatMessage({ msg }) {
  const isUser = msg.role === 'user';
  const isLoading = msg._loading;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start', padding: '0 8px' }}>
      <div className={isUser ? 'chat-bubble-user' : 'chat-bubble-ai'}
        style={msg._error ? { borderColor: 'var(--red)', color: 'var(--red)' } : {}}
      >
        {isLoading ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 7, color: 'var(--text3)', fontStyle: 'italic' }}>
            <Spin size={12} /> Claude denkt…
          </span>
        ) : msg.content}
      </div>
      <div className="chat-meta">
        {isUser ? 'Du' : 'Claude'}
        {msg.timestamp && ` · ${fmtTime(msg.timestamp)}`}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Tab 3 — Projekte
───────────────────────────────────────────────────────────────── */
function ProjekteTab({ token }) {
  const { projects, reloadProjects, bumpTaskVersion } = useContext(ProjectsCtx);
  const [expanded, setExpanded] = useState(null);
  const [projectTasks, setProjectTasks] = useState({});
  const [loadingProject, setLoadingProject] = useState(null);
  const [showNewTask, setShowNewTask] = useState(null);
  const [wizardProj, setWizardProj] = useState(null);
  const [briefingModal, setBriefingModal] = useState(null); // { pid, label, content }
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskForm, setTaskForm] = useState({
    title: '', priority: 'P2', description: '',
    acceptance_criteria: '', context: '', risk: 'LOW',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState(null);

  async function toggleExpand(pid) {
    if (expanded === pid) { setExpanded(null); return; }
    setExpanded(pid);
    if (!projectTasks[pid]) {
      setLoadingProject(pid);
      try {
        const data = await apiFetch(token, `/cockpit/registry/backlog/${pid}`);
        setProjectTasks(prev => ({ ...prev, [pid]: data.tasks || data || [] }));
      } catch {
        setProjectTasks(prev => ({ ...prev, [pid]: [] }));
      } finally {
        setLoadingProject(null);
      }
    }
  }

  async function submitTask(pid) {
    if (!taskForm.title.trim()) return;
    setSubmitting(true);
    setSubmitMsg(null);
    try {
      await apiFetch(token, `/cockpit/registry/add-task/${pid}`, {
        method: 'POST',
        body: JSON.stringify(taskForm),
      });
      setSubmitMsg({ ok: true, text: 'Task erfolgreich angelegt.' });
      setTaskForm({ title: '', priority: 'P2', description: '', acceptance_criteria: '', context: '', risk: 'LOW' });
      // Refresh tasks for this project + notify Kanban
      const data = await apiFetch(token, `/cockpit/registry/backlog/${pid}`);
      setProjectTasks(prev => ({ ...prev, [pid]: data.tasks || data || [] }));
      bumpTaskVersion();
      setTimeout(() => { setShowNewTask(null); setSubmitMsg(null); }, 1500);
    } catch (e) {
      setSubmitMsg({ ok: false, text: e.message });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* ── Header: Titel + Neues Projekt Button ─────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: 'var(--text)', letterSpacing: -0.3 }}>Projekte</h2>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => setWizardProj({ id: '', label: '', icon: '📦', description: '', domain: '', stack: [], color: '#6b7280', placeholder: true })}
          style={{ display: 'flex', alignItems: 'center', gap: 5 }}
        >
          + Neues Projekt
        </button>
      </div>

      {projects.map(proj => {
        // ── Placeholder card ────────────────────────────────────────
        if (proj.placeholder) {
          return (
            <div key={proj.id} className="project-card" style={{ opacity: 0.75 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                  background: `${proj.color}12`, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 22,
                  border: `1px dashed ${proj.color}55`,
                }}>
                  {proj.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                    <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text)' }}>{proj.label}</span>
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20,
                      background: '#6b728022', color: '#9ca3af', letterSpacing: 0.5,
                      textTransform: 'uppercase',
                    }}>Nicht gestartet</span>
                  </div>
                  <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text3)', lineHeight: 1.55 }}>
                    {proj.description}
                  </p>
                </div>
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ borderColor: proj.color, color: proj.color, whiteSpace: 'nowrap' }}
                  onClick={() => setWizardProj(proj)}
                >
                  ▶ Setup starten
                </button>
              </div>
            </div>
          );
        }

        // ── Scoping / In Planung card ────────────────────────────────
        if (proj.scoping) {
          return (
            <div key={proj.id} className="project-card" style={{ opacity: 0.85, borderColor: 'rgba(245,158,11,0.25)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                  background: `${proj.color}15`, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 22,
                  border: `1px solid ${proj.color}40`,
                }}>
                  {proj.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                    <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text)' }}>{proj.label}</span>
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20,
                      background: 'rgba(245,158,11,0.15)', color: '#f59e0b', letterSpacing: 0.5,
                      textTransform: 'uppercase', border: '1px solid rgba(245,158,11,0.3)',
                    }}>📋 In Planung</span>
                  </div>
                  <p style={{ margin: '0 0 6px', fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.55 }}>
                    {proj.description}
                  </p>
                  {proj.stack.length > 0 && (
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      {proj.stack.map(s => (
                        <span key={s} style={{ fontSize: 10.5, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 7px', color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{s}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                  <span style={{ fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                    → Task Generator
                  </span>
                </div>
              </div>
            </div>
          );
        }

        // ── Aktives Projekt ─────────────────────────────────────────
        const tasks = projectTasks[proj.id] || [];
        const todo = tasks.filter(t => t.status === 'TODO').length;
        const inprog = tasks.filter(t => t.status === 'IN_PROGRESS').length;
        const done = tasks.filter(t => t.status === 'DONE').length;
        const isOpen = expanded === proj.id;

        return (
          <div key={proj.id} className="project-card">
            {/* Card header */}
            <div
              style={{ display: 'flex', alignItems: 'flex-start', gap: 14, cursor: 'pointer' }}
              onClick={() => toggleExpand(proj.id)}
            >
              <div style={{
                width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                background: `${proj.color}18`, display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 22,
                border: `1px solid ${proj.color}33`,
              }}>
                {proj.icon}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                  <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text)' }}>{proj.label}</span>
                  <span style={{ fontSize: 10.5, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{proj.domain}</span>
                </div>
                <p style={{ margin: '0 0 8px', fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.55 }}>
                  {proj.description}
                </p>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                  {proj.stack.map(s => (
                    <span key={s} className="tag">{s}</span>
                  ))}
                  {projectTasks[proj.id] && (
                    <>
                      <span style={{ margin: '0 4px', color: 'var(--border2)' }}>·</span>
                      <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                        TODO: <strong>{todo}</strong>
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                        WIP: <strong style={{ color: '#1a56db' }}>{inprog}</strong>
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                        Done: <strong style={{ color: '#059669' }}>{done}</strong>
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={e => { e.stopPropagation(); setShowNewTask(proj.id); }}
                >
                  + Neuer Task
                </button>
                {proj.dynamic && (
                  <button
                    className="btn btn-ghost btn-sm"
                    title="Briefing anzeigen"
                    onClick={async e => {
                      e.stopPropagation();
                      try {
                        const d = await apiFetch(token, `/cockpit/projects/${proj.id}/briefing`);
                        setBriefingModal({ pid: proj.id, label: proj.label, content: d.content });
                      } catch { setBriefingModal({ pid: proj.id, label: proj.label, content: 'Briefing konnte nicht geladen werden.' }); }
                    }}
                  >
                    📄 Briefing
                  </button>
                )}
                <button className="btn btn-ghost btn-sm">
                  {isOpen ? '▲' : '▼'}
                </button>
              </div>
            </div>

            {/* Expanded task list */}
            {isOpen && (
              <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                {loadingProject === proj.id ? (
                  <div style={{ textAlign: 'center', padding: 20 }}><Spin size={18} /></div>
                ) : tasks.length === 0 ? (
                  <div className="empty-state" style={{ padding: '20px 0' }}>
                    <div className="empty-text">Keine Tasks</div>
                    <div className="empty-hint">Lege den ersten Task an.</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {['TODO', 'IN_PROGRESS', 'DONE', 'BLOCKED'].map(status => {
                      const stTasks = tasks.filter(t => t.status === status);
                      if (stTasks.length === 0) return null;
                      const col = KANBAN_COLS.find(c => c.id === status);
                      return (
                        <div key={status}>
                          <div style={{ fontSize: 10.5, fontWeight: 700, color: col?.color, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 5, marginTop: 4 }}>
                            {status} ({stTasks.length})
                          </div>
                          {stTasks.map(t => (
                            <div key={t.id} onClick={() => setSelectedTask({ ...t, _project: proj.id })} style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '7px 10px', background: 'var(--bg3)',
                              borderRadius: 6, marginBottom: 4,
                              border: '1px solid var(--border)', cursor: 'pointer',
                            }}>
                              <PriBadge priority={t.priority} />
                              <span style={{ fontSize: 12.5, color: 'var(--text)', flex: 1 }}>{t.title}</span>
                              {t.risk && <RiskBadge risk={t.risk} />}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Setup Wizard */}
      {wizardProj && (
        <SetupWizardModal
          proj={wizardProj}
          token={token}
          onClose={() => setWizardProj(null)}
          onDone={() => { setWizardProj(null); reloadProjects(); }}
        />
      )}

      {/* Briefing Modal */}
      {briefingModal && (
        <Modal onClose={() => setBriefingModal(null)}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>📄 Briefing — {briefingModal.label}</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => setBriefingModal(null)}>✕</button>
          </div>
          <pre style={{
            background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8,
            padding: 16, fontSize: 12, lineHeight: 1.6, overflowY: 'auto',
            maxHeight: '60vh', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            color: 'var(--text2)', fontFamily: 'var(--mono)',
          }}>
            {briefingModal.content}
          </pre>
        </Modal>
      )}

      {/* New Task Modal */}
      {showNewTask && (
        <Modal onClose={() => { setShowNewTask(null); setSubmitMsg(null); }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
              Neuer Task — {projects.find(p => p.id === showNewTask)?.label}
            </h3>
            <button className="btn btn-ghost btn-sm" onClick={() => { setShowNewTask(null); setSubmitMsg(null); }}>✕</button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="form-group">
              <label className="field-label">Titel *</label>
              <input
                className="field-input"
                value={taskForm.title}
                onChange={e => setTaskForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Kurzer, präziser Titel"
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="field-label">Priorität</label>
                <select className="field-input" value={taskForm.priority} onChange={e => setTaskForm(f => ({ ...f, priority: e.target.value }))}>
                  <option value="P1">P1 — Kritisch</option>
                  <option value="P2">P2 — Hoch</option>
                  <option value="P3">P3 — Normal</option>
                </select>
              </div>
              <div className="form-group">
                <label className="field-label">Risiko</label>
                <select className="field-input" value={taskForm.risk} onChange={e => setTaskForm(f => ({ ...f, risk: e.target.value }))}>
                  <option value="HIGH">HIGH</option>
                  <option value="MEDIUM">MEDIUM</option>
                  <option value="LOW">LOW</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="field-label">Beschreibung</label>
              <textarea
                className="field-input form-textarea"
                value={taskForm.description}
                onChange={e => setTaskForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Was soll implementiert werden?"
                rows={3}
              />
            </div>
            <div className="form-group">
              <label className="field-label">Akzeptanzkriterien</label>
              <textarea
                className="field-input form-textarea"
                value={taskForm.acceptance_criteria}
                onChange={e => setTaskForm(f => ({ ...f, acceptance_criteria: e.target.value }))}
                placeholder="Wann gilt der Task als erledigt?"
                rows={3}
              />
            </div>
            <div className="form-group">
              <label className="field-label">Kontext / Hintergrund</label>
              <textarea
                className="field-input form-textarea"
                value={taskForm.context}
                onChange={e => setTaskForm(f => ({ ...f, context: e.target.value }))}
                placeholder="Zusätzlicher Kontext für den Entwickler"
                rows={2}
              />
            </div>
          </div>

          {submitMsg && (
            <div className={`alert ${submitMsg.ok ? 'alert-ok' : 'alert-err'}`} style={{ marginTop: 12 }}>
              {submitMsg.text}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button
              className="btn btn-primary"
              disabled={submitting || !taskForm.title.trim()}
              onClick={() => submitTask(showNewTask)}
            >
              {submitting ? <Spin /> : '+'} Task anlegen
            </button>
            <button className="btn btn-secondary" onClick={() => { setShowNewTask(null); setSubmitMsg(null); }}>
              Abbrechen
            </button>
          </div>
        </Modal>
      )}
      {selectedTask && (
        <TaskDetailModal task={selectedTask} token={token} onClose={() => setSelectedTask(null)}
          onRefresh={() => {
            const pid = selectedTask._project;
            if (pid) apiFetch(token, `/cockpit/registry/backlog/${pid}`)
              .then(d => setProjectTasks(prev => ({ ...prev, [pid]: d.tasks || d || [] })))
              .catch(() => {});
          }} />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Tab 4 — Logs
───────────────────────────────────────────────────────────────── */
function LogsTab({ token }) {
  const { projects } = useContext(ProjectsCtx);
  const [project, setProject]   = useState('bpos');
  const [logType, setLogType]   = useState('orchestrator');
  const [lines, setLines]       = useState([]);
  const [meta, setMeta]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const bottomRef = useRef(null);
  const intervalRef = useRef(null);
  const isMounted = useRef(true);
  useEffect(() => { return () => { isMounted.current = false; }; }, []);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch(token, `/cockpit/logs/${project}?type=${logType}`);
      if (!isMounted.current) return;
      setLines(data.lines || data || []);
      setMeta(data.meta || null);
    } catch (e) {
      if (!isMounted.current) return;
      setLines([`[ERROR] Konnte Logs nicht laden: ${e.message}`]);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [token, project, logType]);

  useEffect(() => {
    loadLogs();
    intervalRef.current = setInterval(loadLogs, 30000);
    return () => clearInterval(intervalRef.current);
  }, [loadLogs]);

  useEffect(() => {
    if (autoScroll) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [lines, autoScroll]);

  function colorLine(line) {
    const l = line.toLowerCase();
    if (l.includes('error') || l.includes('fehler') || l.includes('critical') || l.includes('traceback')) return '#f87171';
    if (l.includes('warn') || l.includes('warning')) return '#fbbf24';
    if (l.includes('success') || l.includes('done') || l.includes('ok') || l.includes('completed')) return '#34d399';
    if (l.includes('pipeline') || l.includes('info') || l.includes('start') || l.includes('deploy')) return '#60a5fa';
    return 'rgba(255,255,255,0.7)';
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Controls */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          className="field-input"
          value={project}
          onChange={e => setProject(e.target.value)}
          style={{ maxWidth: 200 }}
        >
          {projects.filter(p => !p.placeholder && !p.scoping).map(p => <option key={p.id} value={p.id}>{p.icon} {p.label}</option>)}
        </select>
        <select
          className="field-input"
          value={logType}
          onChange={e => setLogType(e.target.value)}
          style={{ maxWidth: 180 }}
        >
          <option value="orchestrator">Orchestrator</option>
          <option value="qa">QA Agent</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text2)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={autoScroll}
            onChange={e => setAutoScroll(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          Auto-scroll
        </label>
        {meta && (
          <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 4 }}>
            {meta.line_count != null ? `${meta.line_count} Zeilen` : ''}
            {meta.size ? ` · ${meta.size}` : ''}
          </span>
        )}
        <button
          className="btn btn-secondary btn-sm"
          onClick={loadLogs}
          style={{ marginLeft: 'auto' }}
        >
          {loading ? <Spin /> : '↻'} Refresh
        </button>
      </div>

      {/* Terminal */}
      <div className="terminal-log" style={{
        background: '#0f172a', borderRadius: 10, padding: '14px 16px',
        fontFamily: 'var(--mono)', fontSize: 11.5, lineHeight: 1.75,
        height: 'calc(100dvh - 310px)', overflowY: 'auto',
        border: '1px solid rgba(255,255,255,0.06)',
        boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.3)',
      }}>
        {/* Title bar dots */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#f87171', display: 'inline-block' }} />
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#fbbf24', display: 'inline-block' }} />
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#34d399', display: 'inline-block' }} />
          <span style={{ marginLeft: 8, color: 'rgba(255,255,255,0.3)', fontSize: 10.5 }}>
            {project} / {logType}.log
          </span>
        </div>

        {loading && lines.length === 0 && (
          <div style={{ textAlign: 'center', padding: 30, color: 'rgba(255,255,255,0.3)' }}>
            <Spin size={18} />
            <div style={{ marginTop: 8 }}>Lade Logs…</div>
          </div>
        )}
        {lines.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: 30, marginBottom: 10, opacity: 0.3 }}>📄</div>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.35)' }}>Keine Logeinträge</div>
            <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.2)', marginTop: 5 }}>Log-Datei ist leer oder noch nicht vorhanden.</div>
          </div>
        )}
        {lines.map((line, i) => (
          <div key={i} style={{ color: colorLine(line), whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            <span style={{ color: 'rgba(255,255,255,0.2)', marginRight: 10, userSelect: 'none', fontSize: 10 }}>
              {String(i + 1).padStart(4, ' ')}
            </span>
            {line}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Incident Proposal Card (Jira → P4A)
───────────────────────────────────────────────────────────────── */
function IncidentProposalCard({ token, approval, onDecided }) {
  const [incident,     setIncident]     = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [expanded,     setExpanded]     = useState(true);
  const [chatMsg,      setChatMsg]      = useState('');
  const [chatLoading,  setChatLoading]  = useState(false);
  const [chatError,    setChatError]    = useState(null);
  const [deciding,     setDeciding]     = useState(false);
  const [comment,      setComment]      = useState('');
  // Experten
  const [experts,      setExperts]      = useState([]);
  const [showExperts,  setShowExperts]  = useState(false);
  const [newExpertRole, setNewExpertRole] = useState('');
  const [newExpertDesc, setNewExpertDesc] = useState('');
  // Rückfrage an Reporter
  const [showAsk,      setShowAsk]      = useState(false);
  const [askMsg,       setAskMsg]       = useState('');
  const [askLoading,   setAskLoading]   = useState(false);
  const [askResult,    setAskResult]    = useState(null);
  const [retryLoading, setRetryLoading] = useState(false);
  const isMounted = useRef(true);
  useEffect(() => { return () => { isMounted.current = false; }; }, []);

  const incidentId = approval.incident_id;

  useEffect(() => {
    if (!incidentId) return;
    apiFetch(token, `/cockpit/incidents/${incidentId}`)
      .then(d => { if (isMounted.current) { setIncident(d); setLoading(false); } })
      .catch(() => { if (isMounted.current) setLoading(false); });
  }, [token, incidentId]);

  // Orchestrator Live-Status Polling: alle 5s neu laden solange running
  useEffect(() => {
    if (incident?.orchestrator_status !== 'running') return;
    const timer = setInterval(() => {
      apiFetch(token, `/cockpit/incidents/${incidentId}`)
        .then(d => { if (isMounted.current) setIncident(d); })
        .catch(() => {});
    }, 5000);
    return () => clearInterval(timer);
  }, [incident?.orchestrator_status, token, incidentId]);

  async function retryAnalysis() {
    if (retryLoading) return;
    setRetryLoading(true);
    try {
      await apiFetch(token, `/cockpit/incidents/${incidentId}/retry-analysis`, { method: 'POST' });
      if (!isMounted.current) return;
      setLoading(true);
      const d = await apiFetch(token, `/cockpit/incidents/${incidentId}`);
      if (isMounted.current) { setIncident(d); setLoading(false); }
    } catch (e) {
      if (isMounted.current) alert(e.message);
    } finally {
      if (isMounted.current) setRetryLoading(false);
    }
  }

  async function sendChat() {
    if (!chatMsg.trim() || chatLoading) return;
    const msg = chatMsg;
    const ts  = new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    setChatMsg('');
    setChatError(null);
    setChatLoading(true);
    // Nachricht sofort anzeigen (optimistisch)
    setIncident(prev => ({
      ...prev,
      chat_history: [...(prev?.chat_history || []), { role: 'user', content: msg, ts }],
    }));
    try {
      const data = await apiFetch(token, `/cockpit/incidents/${incidentId}/chat`, {
        method: 'POST',
        body: JSON.stringify({ message: msg, experts }),
      });
      if (isMounted.current && data.history) {
        setIncident(prev => ({ ...prev, chat_history: data.history }));
      }
    } catch (e) {
      if (isMounted.current) {
        setChatMsg(msg);
        setChatError(e.message);
        // Optimistische Nachricht rückgängig
        setIncident(prev => ({
          ...prev,
          chat_history: (prev?.chat_history || []).slice(0, -1),
        }));
      }
    } finally {
      if (isMounted.current) setChatLoading(false);
    }
  }

  async function sendAsk() {
    if (!askMsg.trim() || askLoading) return;
    setAskLoading(true);
    setAskResult(null);
    try {
      const data = await apiFetch(token, `/cockpit/incidents/${incidentId}/ask-reporter`, {
        method: 'POST', body: JSON.stringify({ question: askMsg }),
      });
      if (!isMounted.current) return;
      if (data.jira_result?.skipped) {
        setAskResult({ ok: false, error: 'Jira-Credentials nicht konfiguriert — bitte JIRA_BASE_URL, JIRA_API_USER und JIRA_API_TOKEN in der .env setzen.' });
      } else if (!data.jira_result?.ok) {
        setAskResult({ ok: false, error: `Jira-Fehler: ${data.jira_result?.error || 'Unbekannt'}` });
      } else {
        setAskResult({ ok: true });
        setAskMsg('');
        const updated = await apiFetch(token, `/cockpit/incidents/${incidentId}`);
        if (isMounted.current) setIncident(updated);
      }
    } catch (e) {
      if (isMounted.current) setAskResult({ ok: false, error: e.message });
    } finally {
      if (isMounted.current) setAskLoading(false);
    }
  }

  async function decide(action) {
    setDeciding(true);
    try {
      await apiFetch(token, `/cockpit/incidents/${incidentId}/approve`, {
        method: 'POST', body: JSON.stringify({ action, comment }),
      });
      onDecided();
    } catch (e) {
      alert(e.message);
    } finally {
      if (isMounted.current) setDeciding(false);
    }
  }

  const analysis     = incident?.analysis     || {};
  const ticket       = incident?.ticket       || {};
  const chatHistory  = incident?.chat_history || [];
  const [activePane, setActivePane] = useState('ticket'); // ticket | analyse | chat | jira

  const PANES = [
    { id: 'ticket',  label: '📋 Ticket',   color: 'var(--text2)' },
    { id: 'analyse', label: '🤖 Analyse',  color: 'var(--accent)' },
    { id: 'chat',    label: `💬 Chat${chatHistory.filter(m => m.role !== 'reporter_question').length > 0 ? ` (${chatHistory.filter(m => m.role !== 'reporter_question').length})` : ''}`, color: 'var(--green)' },
    { id: 'jira',    label: `✉️ Jira${chatHistory.filter(m => m.role === 'reporter_question').length > 0 ? ` (${chatHistory.filter(m => m.role === 'reporter_question').length})` : ''}`, color: 'var(--amber)' },
  ];

  return (
    <div className="card" style={{ borderLeft: '3px solid var(--red)', padding: 0, overflow: 'hidden' }}>

      {/* ── Header (immer sichtbar) ───────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', cursor: 'pointer', background: 'rgba(255,60,60,0.06)' }}
        onClick={() => setExpanded(e => !e)}>
        <span style={{ fontSize: 18 }}>🚨</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)' }}>
            <span style={{ color: 'var(--red)', marginRight: 6 }}>{ticket.key || approval.jira_key}</span>
            {approval.title?.replace(/^\[[^\]]+\]\s*/, '') || ticket.summary || '—'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2, display: 'flex', gap: 12 }}>
            <span>👤 {ticket.reporter_email || approval.reporter || 'Reporter unbekannt'}</span>
            <span>🕐 {approval.created_at}</span>
            {incident?.orchestrator_status && (
              <span style={{ color: incident.orchestrator_status === 'done' ? 'var(--green)' : incident.orchestrator_status === 'error' ? 'var(--red)' : 'var(--amber)' }}>
                {incident.orchestrator_status === 'running' ? '⏳ Umsetzung läuft' : incident.orchestrator_status === 'done' ? '✅ Erledigt' : '❌ Fehler'}
              </span>
            )}
          </div>
        </div>
        <PriBadge priority={approval.priority || 'P2'} />
        <span style={{ color: 'var(--text2)', fontSize: 13 }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 30 }}><Spin size={20} /></div>
          ) : (
            <>
              {/* ── Tab-Navigation ───────────────────────────────── */}
              <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', background: 'var(--surface2)' }}>
                {PANES.map(p => (
                  <button key={p.id} onClick={() => setActivePane(p.id)} style={{
                    flex: 1, padding: '8px 4px', fontSize: 11, fontWeight: activePane === p.id ? 700 : 400,
                    border: 'none', borderBottom: activePane === p.id ? `2px solid ${p.color}` : '2px solid transparent',
                    background: 'none', color: activePane === p.id ? p.color : 'var(--text2)',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}>{p.label}</button>
                ))}
              </div>

              {/* ── Pane: Ticket ─────────────────────────────────── */}
              {activePane === 'ticket' && (
                <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <div style={{ background: 'var(--surface2)', borderRadius: 6, padding: '8px 12px' }}>
                      <div style={{ fontSize: 10, color: 'var(--text2)', marginBottom: 2 }}>TICKET</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--red)' }}>{ticket.key}</div>
                    </div>
                    <div style={{ background: 'var(--surface2)', borderRadius: 6, padding: '8px 12px' }}>
                      <div style={{ fontSize: 10, color: 'var(--text2)', marginBottom: 2 }}>PRIORITÄT (JIRA)</div>
                      <div style={{ fontSize: 12, color: 'var(--text)' }}>{ticket.priority || '—'}</div>
                    </div>
                    <div style={{ background: 'var(--surface2)', borderRadius: 6, padding: '8px 12px' }}>
                      <div style={{ fontSize: 10, color: 'var(--text2)', marginBottom: 2 }}>REPORTER</div>
                      <div style={{ fontSize: 12, color: 'var(--text)' }}>{ticket.reporter_name || '—'}<br/><span style={{ fontSize: 10, color: 'var(--text2)' }}>{ticket.reporter_email || ''}</span></div>
                    </div>
                    <div style={{ background: 'var(--surface2)', borderRadius: 6, padding: '8px 12px' }}>
                      <div style={{ fontSize: 10, color: 'var(--text2)', marginBottom: 2 }}>STATUS</div>
                      <div style={{ fontSize: 12, color: 'var(--text)' }}>{ticket.status || '—'}</div>
                    </div>
                  </div>
                  {ticket.description && (
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>Fehlerbeschreibung</div>
                      <div style={{ background: 'var(--surface2)', borderRadius: 6, padding: '10px 12px', fontSize: 12, color: 'var(--text)', whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto', lineHeight: 1.6 }}>
                        {typeof ticket.description === 'string' ? ticket.description : JSON.stringify(ticket.description)}
                      </div>
                    </div>
                  )}
                  {ticket.attachments?.length > 0 && (
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>Anhänge</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {ticket.attachments.map((att, i) => (
                          <span key={i} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 4, padding: '3px 8px', fontSize: 11, color: 'var(--text2)' }}>📎 {att.filename}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Pane: Analyse ────────────────────────────────── */}
              {activePane === 'analyse' && (
                <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {analysis.problem_summary === 'Analyse fehlgeschlagen' ? (
                    <div className="alert alert-err" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                      <span>Analyse fehlgeschlagen — bitte manuell prüfen.</span>
                      <button
                        onClick={retryAnalysis}
                        disabled={retryLoading}
                        style={{ background: 'var(--red)', color: '#fff', border: 'none', borderRadius: 5, padding: '4px 12px', fontSize: 12, cursor: retryLoading ? 'not-allowed' : 'pointer', opacity: retryLoading ? 0.6 : 1, whiteSpace: 'nowrap' }}
                      >
                        {retryLoading ? <Spin size={12} /> : '↻ Erneut analysieren'}
                      </button>
                    </div>
                  ) : (
                    <>
                      <div style={{ background: 'rgba(100,160,255,0.08)', border: '1px solid rgba(100,160,255,0.2)', borderRadius: 6, padding: '10px 12px' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>Problem</div>
                        <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.6 }}>{analysis.problem_summary}</div>
                      </div>
                      <div style={{ background: 'rgba(255,180,0,0.08)', border: '1px solid rgba(255,180,0,0.2)', borderRadius: 6, padding: '10px 12px' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--amber)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>Root Cause</div>
                        <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.6 }}>{analysis.root_cause}</div>
                      </div>
                      {analysis.affected_areas?.length > 0 && (
                        <div style={{ background: 'var(--surface2)', borderRadius: 6, padding: '10px 12px' }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>Betroffene Bereiche</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {analysis.affected_areas.map((a, i) => (
                              <span key={i} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontFamily: 'monospace', color: 'var(--text)' }}>{a}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {analysis.solution_steps?.length > 0 && (
                        <div style={{ background: 'rgba(60,200,100,0.06)', border: '1px solid rgba(60,200,100,0.2)', borderRadius: 6, padding: '10px 12px' }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--green)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>Lösungsschritte</div>
                          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: 'var(--text)', lineHeight: 1.8 }}>
                            {analysis.solution_steps.map((s, i) => <li key={i}>{s}</li>)}
                          </ol>
                        </div>
                      )}
                      {analysis.tasks?.length > 0 && (
                        <div style={{ background: 'var(--surface2)', borderRadius: 6, padding: '10px 12px' }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>Tasks nach Freigabe ({analysis.tasks.length})</div>
                          {analysis.tasks.map((t, i) => (
                            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6, paddingBottom: 6, borderBottom: i < analysis.tasks.length - 1 ? '1px solid var(--border)' : 'none' }}>
                              <PriBadge priority={t.priority || 'P2'} />
                              <div>
                                <div style={{ fontSize: 12, color: 'var(--text)', fontWeight: 600 }}>{t.title}</div>
                                <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>{t.description}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* ── Pane: Chat mit AI ────────────────────────────── */}
              {activePane === 'chat' && (
                <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {/* Experten */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ fontSize: 11, color: 'var(--text2)' }}>Stelle Fragen zur Analyse oder diskutiere Lösungsansätze.</div>
                    <button className="btn btn-secondary btn-sm" onClick={() => setShowExperts(e => !e)} style={{ fontSize: 11 }}>
                      👥 Experten {experts.length > 0 && <span style={{ background: 'var(--accent)', color: '#fff', borderRadius: 8, padding: '0 5px', marginLeft: 4, fontSize: 10 }}>{experts.length}</span>}
                    </button>
                  </div>
                  {showExperts && (
                    <div style={{ background: 'var(--surface2)', borderRadius: 6, padding: '10px 12px' }}>
                      <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 8 }}>Experten geben Claude zusätzlichen Kontext (z.B. "Security Expert: Fokus auf Auth-Schwachstellen")</div>
                      {experts.map((e, i) => (
                        <div key={e.id} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                          <span style={{ fontSize: 11, color: 'var(--text)', flex: 1 }}><strong>{e.role}</strong>{e.description ? `: ${e.description}` : ''}</span>
                          <button onClick={() => setExperts(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
                        </div>
                      ))}
                      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                        <input value={newExpertRole} onChange={e => setNewExpertRole(e.target.value)} placeholder="Rolle" style={{ flex: 1, fontSize: 11, padding: '4px 8px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)' }} />
                        <input value={newExpertDesc} onChange={e => setNewExpertDesc(e.target.value)} placeholder="Beschreibung (optional)" style={{ flex: 2, fontSize: 11, padding: '4px 8px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)' }} />
                        <button className="btn btn-secondary btn-sm" onClick={() => { if (!newExpertRole.trim()) return; setExperts(prev => [...prev, { id: Date.now(), role: newExpertRole.trim(), description: newExpertDesc.trim() }]); setNewExpertRole(''); setNewExpertDesc(''); }}>+</button>
                      </div>
                    </div>
                  )}
                  {/* Chat-History (nur AI/User) */}
                  {chatHistory.filter(m => m.role !== 'reporter_question').length > 0 && (
                    <div style={{ background: 'var(--surface2)', borderRadius: 6, padding: '10px 12px', maxHeight: 280, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {chatHistory.filter(m => m.role !== 'reporter_question').map((m, i) => (
                        <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                          <div style={{ width: 28, height: 28, borderRadius: '50%', background: m.role === 'user' ? 'var(--accent)' : 'var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                            {m.role === 'user' ? 'Du' : 'AI'}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 10, color: 'var(--text2)', marginBottom: 3 }}>{m.ts}</div>
                            <div style={{ fontSize: 12, color: 'var(--text)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{m.content}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {chatLoading && (
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '6px 0' }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0 }}>AI</div>
                      <div style={{ fontSize: 12, color: 'var(--text2)' }}>
                        <Spin size={10} /> <span style={{ marginLeft: 6 }}>Claude schreibt…</span>
                      </div>
                    </div>
                  )}
                  {chatError && <div className="alert alert-err" style={{ fontSize: 11 }}>{chatError}</div>}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <textarea value={chatMsg} onChange={e => setChatMsg(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                      placeholder="Frage oder Anmerkung… (Enter zum Senden, Shift+Enter für Zeilenumbruch)"
                      style={{ flex: 1, minHeight: 64, resize: 'vertical', fontSize: 12, padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', fontFamily: 'inherit' }}
                      disabled={chatLoading} />
                    <button className="btn btn-primary" onClick={sendChat} disabled={chatLoading || !chatMsg.trim()} style={{ alignSelf: 'flex-end', minWidth: 80 }}>
                      {chatLoading ? <Spin size={12} /> : '➤ Senden'}
                    </button>
                  </div>
                </div>
              )}

              {/* ── Pane: Jira Kommunikation ─────────────────────── */}
              {activePane === 'jira' && (
                <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>
                    Rückfragen werden als <strong>öffentlicher Jira-Kommentar</strong> gepostet — der Reporter erhält automatisch eine Email.
                  </div>
                  {/* Verlauf gesendeter Rückfragen */}
                  {chatHistory.filter(m => m.role === 'reporter_question').length > 0 && (
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>Gesendete Rückfragen</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {chatHistory.filter(m => m.role === 'reporter_question').map((m, i) => (
                          <div key={i} style={{ background: 'rgba(255,180,0,0.08)', border: '1px solid rgba(255,180,0,0.25)', borderRadius: 6, padding: '10px 12px' }}>
                            <div style={{ fontSize: 10, color: 'var(--amber)', fontWeight: 600, marginBottom: 4 }}>✉️ {m.ts} → {m.sent_to || 'Reporter'}</div>
                            <div style={{ fontSize: 12, color: 'var(--text)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{m.content}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Neue Rückfrage */}
                  <div style={{ background: 'var(--surface2)', borderRadius: 6, padding: '12px' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--amber)', marginBottom: 8 }}>✉️ Neue Rückfrage an Reporter</div>
                    {askResult?.ok  && <div className="alert alert-ok"  style={{ fontSize: 11, marginBottom: 8 }}>Rückfrage erfolgreich gesendet.</div>}
                    {askResult?.error && <div className="alert alert-err" style={{ fontSize: 11, marginBottom: 8 }}>{askResult.error}</div>}
                    <textarea value={askMsg} onChange={e => setAskMsg(e.target.value)}
                      placeholder="Welche zusätzliche Information benötigen Sie?"
                      style={{ width: '100%', minHeight: 80, resize: 'vertical', fontSize: 12, padding: '8px 10px', borderRadius: 6, border: '1px solid var(--amber)', background: 'var(--surface)', color: 'var(--text)', fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 8 }} />
                    <button className="btn btn-primary" onClick={sendAsk} disabled={askLoading || !askMsg.trim()} style={{ background: 'var(--amber)', borderColor: 'var(--amber)' }}>
                      {askLoading ? <Spin size={12} /> : '✉️ An Reporter senden'}
                    </button>
                  </div>
                </div>
              )}

              {/* ── Entscheidungsleiste (immer sichtbar) ─────────── */}
              <div style={{ padding: '12px 16px', borderTop: '2px solid var(--border)', background: 'var(--surface2)', display: 'flex', gap: 10, alignItems: 'center' }}>
                <input value={comment} onChange={e => setComment(e.target.value)}
                  placeholder="Kommentar zur Entscheidung (optional)…"
                  style={{ flex: 1, fontSize: 12, padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontFamily: 'inherit' }} />
                <button className="btn btn-primary" disabled={deciding} onClick={() => decide('approve')} style={{ whiteSpace: 'nowrap' }}>
                  {deciding ? <Spin size={12} /> : '✅ Freigeben'}
                </button>
                <button className="btn btn-secondary" disabled={deciding} onClick={() => decide('reject')} style={{ color: 'var(--red)', borderColor: 'var(--red)', whiteSpace: 'nowrap' }}>
                  Ablehnen
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Tab 5 — Freigaben
───────────────────────────────────────────────────────────────── */
function FreigabenTab({ token, onCountChange }) {
  const [pending, setPending]         = useState([]);
  const [history, setHistory]         = useState([]);
  const [incidents, setIncidents]     = useState([]);
  const [loading, setLoading]         = useState(false);
  const [actioning, setActioning]     = useState(null);
  const [msg, setMsg]                 = useState(null);
  const isMounted = useRef(true);
  useEffect(() => { return () => { isMounted.current = false; }; }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [appData, incData] = await Promise.all([
        apiFetch(token, '/cockpit/approvals'),
        apiFetch(token, '/cockpit/incidents').catch(() => ({ incidents: [] })),
      ]);
      if (!isMounted.current) return;
      // Incidents-Pending sind Approvals mit type=incident
      const allPending = appData.pending || [];
      const incidentPending = allPending.filter(a => a.type === 'incident' || a.incident_id);
      const normalPending   = allPending.filter(a => !a.type || a.type !== 'incident');
      setPending(normalPending);
      setHistory(appData.history || []);
      setIncidents(incidentPending);
      onCountChange(allPending.length);
    } catch {
      if (!isMounted.current) return;
      setPending([]);
      setHistory([]);
      setIncidents([]);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [token, onCountChange]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000); // Poll alle 30s für neue Freigaben
    return () => clearInterval(iv);
  }, [load]);

  async function handleAction(approval, action) {
    setActioning(approval.id);
    setMsg(null);
    try {
      await apiFetch(token, `/cockpit/approvals/${approval.id}/${action}`, { method: 'POST' });
      setMsg({ ok: true, text: `Freigabe ${action === 'approve' ? 'erteilt' : 'abgelehnt'}.` });
      await load();
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setActioning(null);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Offene Freigaben</span>
          {pending.length > 0 && (
            <span style={{
              marginLeft: 8, background: 'var(--red)', color: '#fff',
              borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 700,
            }}>{pending.length}</span>
          )}
        </div>
        <button className="btn btn-secondary btn-sm" onClick={load}>
          {loading ? <Spin /> : '↻'} Aktualisieren
        </button>
      </div>

      {msg && (
        <div className={`alert ${msg.ok ? 'alert-ok' : 'alert-err'}`}>{msg.text}</div>
      )}

      {/* ── Support Incidents (Jira → P4A) ── */}
      {incidents.length > 0 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--red)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.8, display: 'flex', alignItems: 'center', gap: 8 }}>
            🚨 Support Incidents
            <span style={{ background: 'var(--red)', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 700 }}>{incidents.length}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {incidents.map(a => (
              <IncidentProposalCard key={a.id} token={token} approval={a} onDecided={load} />
            ))}
          </div>
        </div>
      )}

      {/* ── Normale Code-Freigaben ── */}
      {(incidents.length > 0 || pending.length > 0) && (
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
          Code-Freigaben
        </div>
      )}
      {loading && pending.length === 0 && incidents.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 30 }}><Spin size={20} /></div>
      ) : pending.length === 0 && incidents.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">✅</div>
          <div className="empty-text">Keine offenen Freigaben</div>
          <div className="empty-hint">Alle Genehmigungen sind erledigt.</div>
        </div>
      ) : pending.length === 0 ? null : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {pending.map(a => (
            <ApprovalCard
              key={a.id}
              approval={a}
              actioning={actioning === a.id}
              onAction={action => handleAction(a, action)}
            />
          ))}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.8 }}>
            Verlauf
          </div>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="tbl-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Titel</th>
                  <th>Projekt</th>
                  <th className="hide-mobile">Entschieden</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.id}>
                    <td style={{ fontWeight: 500, color: 'var(--text)' }}>{h.title || h.task_title || '—'}</td>
                    <td>{h.project_id || '—'}</td>
                    <td className="hide-mobile">{h.decided_at ? fmtDate(h.decided_at) : '—'}</td>
                    <td>
                      <span className={`badge ${h.action === 'approve' ? 'badge-green' : 'badge-red'}`}>
                        {h.action === 'approve' ? 'Genehmigt' : 'Abgelehnt'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ApprovalCard({ approval, actioning, onAction }) {
  const { projects } = useContext(ProjectsCtx);
  const proj = projects.find(p => p.id === approval.project_id);

  return (
    <div className="card" style={{ borderLeft: '3px solid var(--amber)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 5 }}>
            {proj && <span style={{ fontSize: 13 }}>{proj.icon}</span>}
            <span style={{ fontSize: 10.5, color: 'var(--text3)' }}>{approval.project_id}</span>
            {approval.priority && <PriBadge priority={approval.priority} />}
            {approval.risk && <RiskBadge risk={approval.risk} />}
          </div>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
            {approval.title || approval.task_title || 'Freigabe erforderlich'}
          </div>
          {approval.description && (
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.55 }}>
              {approval.description.slice(0, 160)}{approval.description.length > 160 ? '…' : ''}
            </p>
          )}
          {approval.created_at && (
            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text3)' }}>
              Erstellt: {fmtDate(approval.created_at)}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
          <button
            className="btn btn-primary btn-sm"
            disabled={actioning}
            onClick={() => onAction('approve')}
          >
            {actioning ? <Spin size={12} /> : '✓'} Genehmigen
          </button>
          <button
            className="btn btn-secondary btn-sm"
            disabled={actioning}
            onClick={() => onAction('reject')}
            style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
          >
            ✕ Ablehnen
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   System Status Bar
───────────────────────────────────────────────────────────────── */
function SystemStatusBar({ token }) {
  const [status, setStatus] = useState(null);
  const isMounted = useRef(true);
  useEffect(() => { return () => { isMounted.current = false; }; }, []);

  useEffect(() => {
    apiFetch(token, '/cockpit/status')
      .then(d => { if (isMounted.current) setStatus(d); })
      .catch(() => { if (isMounted.current) setStatus(null); });
    const iv = setInterval(() => {
      apiFetch(token, '/cockpit/status')
        .then(d => { if (isMounted.current) setStatus(d); })
        .catch(() => {});
    }, 30000);
    return () => clearInterval(iv);
  }, [token]);

  if (!status) return null;

  const containers = status.containers || [];

  return (
    <div className="system-status-bar" style={{
      background: 'var(--bg3)', borderBottom: '1px solid var(--border)',
      padding: '6px 20px', display: 'flex', alignItems: 'center', gap: 16,
      flexWrap: 'wrap', fontSize: 11.5,
    }}>
      <span style={{ color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, fontSize: 10 }}>
        System
      </span>
      {containers.map(c => (
        <StatusDot key={c.name} ok={c.healthy !== false} label={c.name} />
      ))}
      {status.pending_approvals != null && (
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5, color: status.pending_approvals > 0 ? 'var(--amber)' : 'var(--text3)' }}>
          {status.pending_approvals > 0 && (
            <span style={{ background: 'var(--amber)', color: '#fff', borderRadius: '50%', width: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700 }}>
              {status.pending_approvals}
            </span>
          )}
          {status.pending_approvals > 0 ? `${status.pending_approvals} offene Freigabe(n)` : 'Keine Freigaben ausstehend'}
        </span>
      )}
      {status.last_deploy && (
        <span style={{ color: 'var(--text3)', fontSize: 10.5 }}>
          Deploy: {fmtDate(status.last_deploy)}
        </span>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Global Stats Bar
───────────────────────────────────────────────────────────────── */
function GlobalStats({ stats }) {
  if (!stats) return null;
  const items = [
    { label: 'total',  val: stats.total_tasks ?? '—' },
    { label: 'aktiv',  val: stats.in_progress ?? '—', color: '#1a56db' },
    { label: 'fertig', val: stats.done ?? '—', color: '#059669' },
    { label: 'Freig.', val: stats.pending_approvals ?? '—', color: stats.pending_approvals > 0 ? '#d97706' : undefined },
  ];

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'nowrap' }}>
      {items.map((item, i) => (
        <React.Fragment key={item.label}>
          {i > 0 && <span style={{ color: 'var(--border)', fontSize: 11, flexShrink: 0 }}>·</span>}
          <span style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'baseline', gap: 3 }}>
            <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: -0.5, color: item.color || 'var(--text)', lineHeight: 1 }}>
              {item.val}
            </span>
            <span style={{ fontSize: 9, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
              {item.label}
            </span>
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Main CockpitPage
───────────────────────────────────────────────────────────────── */
// ── Tab: Sicherheit / 2FA ──────────────────────────────────────────────────────
function SicherheitTab({ token }) {
  const [totpStatus,   setTotpStatus]   = useState(null);
  const [setupData,    setSetupData]    = useState(null);
  const [code,         setCode]         = useState('');
  const [disableCode,  setDisableCode]  = useState('');
  const [showDisable,  setShowDisable]  = useState(false);
  const [msg,          setMsg]          = useState('');
  const [loading,      setLoading]      = useState(false);

  useEffect(() => {
    apiFetch(token, '/cockpit/auth/totp/status').then(setTotpStatus).catch(() => {});
  }, [token]);

  const startSetup = async () => {
    setLoading(true); setMsg('');
    try {
      const d = await apiFetch(token, '/cockpit/auth/totp/setup', { method: 'POST' });
      setSetupData(d);
    } catch (e) { setMsg('Fehler: ' + e.message); }
    finally { setLoading(false); }
  };

  const confirmSetup = async () => {
    if (!code || code.length !== 6) { setMsg('Bitte 6-stelligen Code eingeben'); return; }
    setLoading(true); setMsg('');
    try {
      await apiFetch(token, '/cockpit/auth/totp/confirm', {
        method: 'POST', body: JSON.stringify({ code }),
      });
      setMsg('✅ 2FA erfolgreich aktiviert!');
      setSetupData(null); setCode('');
      setTotpStatus({ enabled: true });
    } catch (e) { setMsg('❌ ' + e.message); }
    finally { setLoading(false); }
  };

  const disable2fa = async () => {
    if (!disableCode || disableCode.length !== 6) { setMsg('Bitte 6-stelligen Code eingeben'); return; }
    setLoading(true);
    try {
      await apiFetch(token, '/cockpit/auth/totp/disable', {
        method: 'DELETE', body: JSON.stringify({ code: disableCode }),
      });
      setTotpStatus({ enabled: false }); setSetupData(null);
      setShowDisable(false); setDisableCode('');
      setMsg('2FA deaktiviert.');
    } catch (e) { setMsg('Fehler: ' + e.message); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* 2FA Status */}
      <div className="card">
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>🔐 Zwei-Faktor-Authentifizierung</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', display: 'flex',
            alignItems: 'center', justifyContent: 'center', fontSize: 24,
            background: totpStatus?.enabled ? '#ecfdf5' : '#fef2f2' }}>
            {totpStatus?.enabled ? '✅' : '⚠️'}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>
              {totpStatus?.enabled ? '2FA ist aktiv' : '2FA ist nicht aktiv'}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text3)', marginTop: 2 }}>
              {totpStatus?.enabled
                ? 'Ihr Konto ist durch einen TOTP-Authenticator geschützt'
                : 'Aktivieren Sie 2FA für zusätzliche Sicherheit'}
            </div>
          </div>
        </div>
        {msg && (
          <div style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, fontSize: 13,
            background: msg.startsWith('✅') ? '#ecfdf5' : msg.startsWith('❌') ? '#fef2f2' : '#fffbeb',
            color: msg.startsWith('✅') ? '#166534' : msg.startsWith('❌') ? '#991b1b' : '#92400e' }}>
            {msg}
          </div>
        )}
        {totpStatus?.enabled ? (
          showDisable ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 12.5, color: 'var(--text3)' }}>
                Bestätigen Sie mit Ihrem Authenticator-Code:
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="field-input" type="text" inputMode="numeric" pattern="[0-9]*"
                  maxLength={6} value={disableCode} onChange={e => setDisableCode(e.target.value)}
                  placeholder="000000" style={{ width: 110, fontFamily: 'var(--mono)', fontSize: 18, textAlign: 'center', letterSpacing: 4 }}
                  onKeyDown={e => e.key === 'Enter' && disable2fa()} />
                <button className="btn" onClick={disable2fa} disabled={loading || disableCode.length !== 6}
                  style={{ background: '#ef4444', color: '#fff', border: 'none' }}>
                  {loading ? 'Lädt…' : 'Deaktivieren'}
                </button>
                <button className="btn btn-ghost" onClick={() => { setShowDisable(false); setDisableCode(''); }}>
                  Abbrechen
                </button>
              </div>
            </div>
          ) : (
            <button className="btn btn-ghost" onClick={() => setShowDisable(true)} disabled={loading}
              style={{ color: '#ef4444', borderColor: '#fecaca' }}>
              2FA deaktivieren
            </button>
          )
        ) : (
          <button className="btn btn-primary" onClick={startSetup} disabled={loading || !!setupData}>
            {loading ? 'Lädt…' : setupData ? 'Setup läuft…' : '🔐 2FA einrichten'}
          </button>
        )}
      </div>

      {/* Setup Flow */}
      {setupData && (
        <div className="card">
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>
            Setup: Google Authenticator / Authy
          </div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12.5, color: 'var(--text3)', marginBottom: 8 }}>
              Schritt 1: Öffnen Sie Ihren Authenticator und scannen Sie diesen QR-Code,
              oder geben Sie den Secret-Key manuell ein.
            </div>
            {/* QR Code via Google Charts API */}
            <div style={{ textAlign: 'center', margin: '14px 0' }}>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(setupData.totp_uri)}`}
                alt="TOTP QR Code"
                style={{ width: 180, height: 180, borderRadius: 8, border: '3px solid var(--border)' }}
              />
            </div>
            <div style={{ padding: '8px 12px', background: 'var(--bg3)', borderRadius: 8,
              fontFamily: 'var(--mono)', fontSize: 13, wordBreak: 'break-all',
              marginBottom: 8, textAlign: 'center', letterSpacing: 2 }}>
              {setupData.secret}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text3)' }}>
              Manuell: Wählen Sie "Schlüssel manuell eingeben" in der App
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12.5, color: 'var(--text3)', marginBottom: 8 }}>
              Schritt 2: Geben Sie den 6-stelligen Code aus der App ein, um die Einrichtung zu bestätigen:
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="field-input" type="text" inputMode="numeric" pattern="[0-9]*"
                maxLength={6} value={code} onChange={e => setCode(e.target.value)}
                placeholder="000000" style={{ flex: 1, fontFamily: 'var(--mono)',
                  fontSize: 20, textAlign: 'center', letterSpacing: 6 }}
                onKeyDown={e => e.key === 'Enter' && confirmSetup()} />
              <button className="btn btn-primary" onClick={confirmSetup} disabled={loading || code.length !== 6}>
                {loading ? '…' : 'Bestätigen'}
              </button>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setSetupData(null)}
            style={{ marginTop: 4 }}>Abbrechen</button>
        </div>
      )}

      {/* Security info */}
      <div className="card" style={{ background: 'var(--bg3)' }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>🛡️ Server-Sicherheit</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12.5, color: 'var(--text2)' }}>
          {[
            ['UFW Firewall', 'Aktiv — nur Ports 22, 80, 443, 8081 erlaubt', '✅'],
            ['fail2ban', 'Aktiv — SSH (3 Versuche), nginx Login (5 Versuche)', '✅'],
            ['Rate Limiting', '30 Req/min API, 5 Req/min Login', '✅'],
            ['Security Headers', 'X-Frame-Options, HSTS, CSP aktiv', '✅'],
            ['HTTPS/TLS', 'Über shared nginx Proxy (Konfiguration extern)', '⚠️'],
            ['Session-Tokens', '7-Tage Bearer Token, urlsafe_b64 256-bit', '✅'],
          ].map(([label, desc, icon]) => (
            <div key={label} style={{ display: 'flex', gap: 10, padding: '6px 0',
              borderBottom: '1px solid var(--border)', alignItems: 'flex-start' }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>{icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{label}</div>
                <div style={{ color: 'var(--text3)', fontSize: 11.5 }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────
   Tab 0 — Claude Code Chat
───────────────────────────────────────────────────────────────── */
function ClaudeCodeTab({ token }) {
  const { projects } = useContext(ProjectsCtx);
  const [project,    setProject]    = useState('bpos');
  const [messages,   setMessages]   = useState([]);
  const [input,      setInput]      = useState('');
  const [loading,    setLoading]    = useState(false);
  const [histLoading, setHistLoading] = useState(false);
  const [taskDraft,  setTaskDraft]  = useState(null);
  const bottomRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const loadHistory = useCallback(async () => {
    setHistLoading(true);
    try {
      const data = await apiFetch(token, `/cockpit/claude-code/${project}`);
      setMessages(data.messages || []);
    } catch {
      setMessages([]);
    } finally { setHistLoading(false); }
  }, [token, project]);

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  async function send(text) {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput('');

    const userMsg = { role: 'user', content: msg, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    const placeholderId = Date.now();
    setMessages(prev => [...prev, { role: 'assistant', content: '…', _id: placeholderId, _loading: true }]);

    try {
      const data = await apiFetch(token, `/cockpit/claude-code/${project}`, {
        method: 'POST',
        body: JSON.stringify({ message: msg }),
      });
      setMessages(prev => prev
        .filter(m => m._id !== placeholderId)
        .concat([{ role: 'assistant', content: data.response, timestamp: new Date().toISOString(),
          tasks_added: data.tasks_added || (data.task_added ? [data.task_added] : []) }])
      );
    } catch (e) {
      setMessages(prev => prev
        .filter(m => m._id !== placeholderId)
        .concat([{ role: 'assistant', content: `Fehler: ${e.message}`, _error: true }])
      );
    } finally { setLoading(false); }
  }

  async function clearChat() {
    if (!window.confirm('Chat-Verlauf löschen?')) return;
    await apiFetch(token, `/cockpit/claude-code/${project}`, { method: 'DELETE' });
    setMessages([]);
  }

  // Parse [TASK] suggestions from assistant messages
  function extractTaskSuggestion(content) {
    const m = content.match(/\[TASK\]\s+([^|]+)\|\s*(P[123])\s*\|\s*(.+)/);
    if (!m) return null;
    return { title: m[1].trim(), priority: m[2], description: m[3].trim(), risk: 'MEDIUM' };
  }

  const QUICK = [
    'Was sind die nächsten Prioritäten in diesem Projekt?',
    'Analysiere offene Blocker und schlage Lösungen vor',
    'Erstelle einen konkreten Implementierungsplan für den nächsten Task',
    'Was fehlt noch bis zum MVP?',
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 500 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>⌨️ Claude Code</span>
        <div style={{ display: 'flex', gap: 6 }}>
          {projects.filter(p => !p.placeholder && !p.scoping).map(p => (
            <button key={p.id} onClick={() => { setProject(p.id); setMessages([]); }}
              style={{
                padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                border: `1px solid ${project === p.id ? p.color : 'var(--border)'}`,
                background: project === p.id ? `${p.color}18` : 'var(--bg3)',
                color: project === p.id ? p.color : 'var(--text3)',
              }}>
              {p.icon} {p.label}
            </button>
          ))}
        </div>
        <button className="btn btn-ghost btn-sm" onClick={clearChat} style={{ marginLeft: 'auto', color: 'var(--text3)' }}>
          🗑 Verlauf löschen
        </button>
      </div>

      {/* Chat area */}
      <div style={{
        flex: 1, overflowY: 'auto', background: 'var(--bg3)',
        borderRadius: 10, padding: '12px 8px', marginBottom: 12,
        border: '1px solid var(--border)', minHeight: 300, maxHeight: 480,
      }}>
        {histLoading && (
          <div style={{ textAlign: 'center', padding: 20 }}><Spin size={18} /></div>
        )}
        {!histLoading && messages.length === 0 && (
          <div style={{ padding: '20px 16px' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
              ⌨️ Claude Code — Projekt-Assistent
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text2)', marginBottom: 10, lineHeight: 1.6 }}>
              Ich habe Zugriff auf CLAUDE.md, BACKLOG.md und die letzten Logs des Projekts.
              Frag mich was auch immer du zum Projekt wissen oder planen möchtest.
            </div>
            <div style={{ padding: '8px 12px', background: 'var(--bg)', borderRadius: 7, border: '1px solid var(--border)', fontSize: 11.5, color: 'var(--text3)', marginBottom: 12, lineHeight: 1.7 }}>
              💡 <strong style={{ color: 'var(--text2)' }}>Tasks direkt ins Kanban:</strong> Wenn ich einen Task vorschlage, erscheint ein Button zum Hinzufügen.
              Du kannst auch selbst sagen: <em>"Füge Task hinzu: [Titel]"</em> — ich antworte im Format<br />
              <code style={{ color: '#60a5fa', fontSize: 11 }}>[TASK] Titel | P1 | Beschreibung</code> → wird automatisch ins BACKLOG gespeichert.
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {QUICK.map(q => (
                <button key={q} onClick={() => send(q)} style={{
                  background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8,
                  padding: '6px 12px', fontSize: 12, color: 'var(--text2)', cursor: 'pointer',
                }}>{q}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((msg, i) => {
          const isUser = msg.role === 'user';
          const suggestion = !isUser && !msg._loading ? extractTaskSuggestion(msg.content) : null;
          return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column',
              alignItems: isUser ? 'flex-end' : 'flex-start', padding: '4px 8px', marginBottom: 4 }}>
              <div style={{
                maxWidth: '80%', padding: '9px 13px', borderRadius: isUser ? '12px 12px 3px 12px' : '12px 12px 12px 3px',
                background: isUser ? 'var(--accent)' : 'var(--bg2)',
                color: isUser ? '#fff' : 'var(--text)',
                border: isUser ? 'none' : '1px solid var(--border)',
                fontSize: 12.5, lineHeight: 1.55,
                ...(msg._error ? { borderColor: 'var(--red)', color: 'var(--red)' } : {}),
              }}>
                {msg._loading
                  ? <span style={{ display: 'flex', alignItems: 'center', gap: 7, color: 'var(--text3)', fontStyle: 'italic' }}>
                      <Spin size={11} /> Claude Code denkt…
                    </span>
                  : <span style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</span>
                }
                {msg.tasks_added?.length > 0 && (
                  <div style={{ marginTop: 8, padding: '6px 10px', background: '#d1fae5',
                    borderRadius: 5, fontSize: 11, color: '#059669' }}>
                    ✅ {msg.tasks_added.length} Task{msg.tasks_added.length > 1 ? 's' : ''} automatisch ins BACKLOG gespeichert
                    {msg.tasks_added.length <= 5 && (
                      <ul style={{ margin: '4px 0 0 0', paddingLeft: 16 }}>
                        {msg.tasks_added.map(t => <li key={t}>{t}</li>)}
                      </ul>
                    )}
                  </div>
                )}
              </div>
              <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 2, paddingLeft: 4 }}>
                {isUser ? 'Du' : 'Claude Code'}{msg.timestamp && ` · ${fmtTime(msg.timestamp)}`}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Task draft indicator */}
      {taskDraft && (
        <div style={{ background: '#fef3c7', border: '1px solid #d97706', borderRadius: 7,
          padding: '7px 12px', fontSize: 12, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: '#d97706' }}>📎 Task wird mit nächster Nachricht angelegt:</span>
          <strong style={{ color: '#92400e' }}>{taskDraft.title}</strong>
          <button onClick={() => setTaskDraft(null)} style={{ marginLeft: 'auto', background: 'none',
            border: 'none', cursor: 'pointer', color: '#d97706', fontSize: 13 }}>✕</button>
        </div>
      )}

      {/* Input */}
      <div style={{ display: 'flex', gap: 8, paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Frag Claude Code über das Projekt… (Enter = Senden, Shift+Enter = Zeilenumbruch)"
          disabled={loading}
          style={{
            flex: 1, padding: '9px 12px', borderRadius: 8,
            border: '1px solid var(--border)', background: 'var(--bg2)',
            fontSize: 13, color: 'var(--text)', resize: 'none',
            fontFamily: 'var(--font)', minHeight: 44, maxHeight: 120,
            outline: 'none', lineHeight: 1.5,
          }}
          rows={2}
        />
        <button className="btn btn-primary" onClick={() => send()} disabled={loading || !input.trim()}
          style={{ flexShrink: 0, alignSelf: 'flex-end', height: 44 }}>
          {loading ? <Spin size={14} /> : '↑'}
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Tab 7 — Server Health
───────────────────────────────────────────────────────────────── */
function GaugeBar({ percent, color }) {
  const c = percent > 85 ? '#dc2626' : percent > 65 ? '#d97706' : color || '#059669';
  return (
    <div style={{ background: 'var(--bg4)', borderRadius: 4, height: 6, overflow: 'hidden', flex: 1 }}>
      <div style={{ width: `${Math.min(percent, 100)}%`, height: '100%', background: c, borderRadius: 4, transition: 'width 0.4s' }} />
    </div>
  );
}

function MetricCard({ icon, label, value, sub, percent, color }) {
  return (
    <div className="card" style={{ minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 20 }}>{icon}</span>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.7, color: 'var(--text3)' }}>{label}</span>
      </div>
      <div style={{ fontSize: 24, fontWeight: 800, fontFamily: 'var(--mono)', color: 'var(--text)', letterSpacing: -1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{sub}</div>}
      {percent != null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <GaugeBar percent={percent} color={color} />
          <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text3)', width: 32, textAlign: 'right' }}>{percent}%</span>
        </div>
      )}
    </div>
  );
}

function ServerHealthTab({ token }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [supData, setSupData] = useState(null);
  const [supLoading, setSupLoading] = useState(false);
  const [extData, setExtData] = useState(null);
  const isMounted = useRef(true);
  useEffect(() => { return () => { isMounted.current = false; }; }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await apiFetch(token, '/cockpit/server-health');
      if (!isMounted.current) return;
      setData(res);
      setLastRefresh(new Date());
    } catch (e) {
      if (isMounted.current) setError(e.message);
    } finally { if (isMounted.current) setLoading(false); }
  }, [token]);

  const loadSupabase = useCallback(async () => {
    setSupLoading(true);
    try {
      const res = await apiFetch(token, '/cockpit/supabase-health');
      if (isMounted.current) setSupData(res);
    } catch(e) { console.warn("[dev-dash]", e?.message || e); }
    finally { if (isMounted.current) setSupLoading(false); }
  }, [token]);

  const loadExtended = useCallback(async () => {
    try {
      const res = await apiFetch(token, '/cockpit/health-extended');
      if (isMounted.current) setExtData(res);
    } catch(e) { console.warn("[dev-dash]", e?.message || e); }
  }, [token]);

  useEffect(() => {
    load();
    loadSupabase();
    loadExtended();
    const iv = setInterval(() => { load(); loadSupabase(); loadExtended(); }, 30000);
    return () => clearInterval(iv);
  }, [load, loadSupabase, loadExtended]);

  if (loading && !data) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Skeleton KPI row */}
      <div className="metric-cards-row">
        {[1,2,3,4].map(i => (
          <div key={i} className="skeleton-card" style={{ minWidth: 0 }}>
            <div className="skeleton skeleton-line" style={{ width: 40, height: 10, marginBottom: 10 }} />
            <div className="skeleton skeleton-line" style={{ width: 80, height: 22, marginBottom: 8 }} />
            <div className="skeleton skeleton-line" style={{ width: 60, height: 8, marginBottom: 10 }} />
            <div className="skeleton" style={{ height: 4, borderRadius: 2 }} />
          </div>
        ))}
      </div>
      {/* Skeleton container list */}
      <div className="skeleton-card">
        <div className="skeleton skeleton-line" style={{ width: 140, height: 12, marginBottom: 14 }} />
        {[1,2,3].map(i => (
          <div key={i} className="skeleton skeleton-line" style={{ height: 32, marginBottom: 6 }} />
        ))}
      </div>
    </div>
  );
  if (error) return (
    <div className="alert alert-err">
      <div style={{ fontWeight: 600, marginBottom: 4 }}>⚠️ Server Health nicht erreichbar</div>
      <div style={{ fontSize: 12 }}>{error}</div>
    </div>
  );
  if (!data) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>🖥️ Server Health</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
            Uptime: <strong>{data.uptime?.display}</strong>
            &nbsp;·&nbsp;Load: <strong style={{ fontFamily: 'var(--mono)' }}>{data.load_avg?.map(v => v.toFixed(2)).join(' / ')}</strong>
            {lastRefresh && <>&nbsp;·&nbsp;Stand: {lastRefresh.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Europe/Berlin' })}</>}
          </div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>
          {loading ? <Spin size={12} /> : '↻'} Refresh
        </button>
      </div>

      {/* KPI Metric Cards */}
      <div className="metric-cards-row">
        <MetricCard
          icon="⚡" label="CPU"
          value={`${data.cpu?.percent}%`}
          sub={`${data.cpu?.cores} Kerne`}
          percent={data.cpu?.percent}
        />
        <MetricCard
          icon="🧠" label="RAM"
          value={`${data.memory?.used_gb} GB`}
          sub={`von ${data.memory?.total_gb} GB`}
          percent={data.memory?.percent}
        />
        <MetricCard
          icon="💾" label="Disk"
          value={`${data.disk?.used_gb} GB`}
          sub={`von ${data.disk?.total_gb} GB`}
          percent={data.disk?.percent}
        />
        <MetricCard
          icon="🌐" label="Netzwerk"
          value={`↑${data.network?.sent_gb} GB`}
          sub={`↓${data.network?.recv_gb} GB empfangen`}
          color="#1a56db"
        />
      </div>

      {/* Docker Containers */}
      <div className="card">
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          🐳 Docker Container
          <span style={{ fontWeight: 400, color: 'var(--text3)', fontSize: 11 }}>
            · {(data.containers || []).filter(c => c.healthy).length}/{(data.containers || []).length} aktiv
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {(data.containers || []).map(c => (
            <div key={c.name} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '6px 10px', background: 'var(--bg3)',
              borderRadius: 6, border: '1px solid var(--border)',
            }}>
              <span style={{
                width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                background: c.healthy ? '#059669' : '#dc2626',
                boxShadow: c.healthy ? '0 0 5px #05966666' : '0 0 5px #dc262666',
              }} />
              <span style={{ flex: 1, fontSize: 12.5, fontFamily: 'var(--mono)', fontWeight: 500, color: 'var(--text)' }}>
                {c.name}
              </span>
              <span style={{ fontSize: 11, color: c.healthy ? '#059669' : '#dc2626' }}>
                {c.status}
              </span>
            </div>
          ))}
          {(!data.containers || data.containers.length === 0) && (
            <div className="empty-state" style={{ padding: '24px 0' }}>
              <div className="empty-icon">🐳</div>
              <div className="empty-text">Keine Container</div>
              <div className="empty-hint">Docker nicht aktiv oder nicht erreichbar.</div>
            </div>
          )}
        </div>
      </div>

      {/* SSL Certificates */}
      <div className="card">
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 12 }}>🔒 SSL-Zertifikate</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 }}>
          {(data.ssl || []).map(s => {
            const color = s.days == null ? '#9ca3af' : s.days < 7 ? '#ef4444' : s.days < 30 ? '#f59e0b' : '#10b981';
            const bg    = s.days == null ? 'var(--bg3)' : s.days < 7 ? '#ef444414' : s.days < 30 ? '#f59e0b14' : '#10b98114';
            return (
              <div key={s.domain} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 12px', background: bg,
                borderRadius: 7, border: `1px solid ${color}44`,
              }}>
                <span style={{ fontSize: 14 }}>{s.days == null ? '❓' : s.ok ? '✅' : '⚠️'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.domain}
                  </div>
                  <div style={{ fontSize: 10.5, color }}>
                    {s.days == null ? 'Nicht erreichbar' : `${s.days} Tage verbleibend`}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Supabase Services */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700 }}>☁️ Supabase Services</div>
          {supData?.checked_at && (
            <span style={{ fontSize: 10, color: 'var(--text3)' }}>
              {supLoading ? <Spin size={10} /> : `Stand: ${supData.checked_at.slice(11, 16)}`}
            </span>
          )}
        </div>
        {!supData && supLoading && <div style={{ textAlign: 'center', padding: 16 }}><Spin size={16} /></div>}
        {supData && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
            {(supData.supabase || []).map(s => {
              const ok = s.ok;
              const color = ok ? '#10b981' : '#ef4444';
              const bg    = ok ? '#10b98114' : '#ef444414';
              return (
                <div key={s.project_id} style={{
                  padding: '10px 14px', background: bg,
                  borderRadius: 8, border: `1px solid ${color}44`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 16 }}>{ok ? '✅' : '⚠️'}</span>
                    <div>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text)' }}>{s.label}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                        {s.url.replace('https://', '').split('.')[0]}
                      </div>
                    </div>
                    {s.latency_ms != null && (
                      <span style={{ marginLeft: 'auto', fontSize: 10.5, fontFamily: 'var(--mono)',
                        color: s.latency_ms > 2000 ? '#d97706' : 'var(--text3)' }}>
                        {s.latency_ms}ms
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {[
                      { key: 'api',       label: 'REST API' },
                      { key: 'auth',      label: 'Auth' },
                      { key: 'functions', label: 'Edge Fn' },
                    ].map(({ key, label }) => (
                      <span key={key} style={{
                        fontSize: 10.5, padding: '2px 7px', borderRadius: 8,
                        background: s[key] ? '#05966922' : '#dc262622',
                        color: s[key] ? '#059669' : '#dc2626',
                        border: `1px solid ${s[key] ? '#05966644' : '#dc262644'}`,
                        fontWeight: 600,
                      }}>
                        {s[key] ? '✓' : '✗'} {label}
                      </span>
                    ))}
                  </div>
                  {s.error && (
                    <div style={{ marginTop: 6, fontSize: 10.5, color: '#dc2626',
                      fontFamily: 'var(--mono)', wordBreak: 'break-all' }}>
                      {s.error}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── HTTP Reachability ───────────────────────────────────── */}
      <div className="card">
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 12 }}>🌐 HTTP Erreichbarkeit</div>
        {!extData && <div style={{ textAlign: 'center', padding: 16 }}><Spin size={16} /></div>}
        {extData && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
            {(extData.http || []).map(h => {
              const ok    = h.ok;
              const color = ok ? '#10b981' : '#ef4444';
              const bg    = ok ? '#10b98114' : '#ef444414';
              return (
                <div key={h.url} style={{ padding: '8px 12px', background: bg, borderRadius: 7, border: `1px solid ${color}44` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14 }}>{ok ? '✅' : '⚠️'}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{h.label}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {h.status ? `HTTP ${h.status}` : h.error || 'Nicht erreichbar'}
                      </div>
                    </div>
                    {h.latency_ms != null && (
                      <span style={{ fontSize: 10.5, fontFamily: 'var(--mono)', color: h.latency_ms > 2000 ? '#d97706' : color, flexShrink: 0 }}>
                        {h.latency_ms}ms
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Claude Budget + Orchestrator + Postgres ──────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>

        {/* Claude Budget */}
        <div className="card">
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 12 }}>🤖 Claude API Budget</div>
          {extData?.claude ? (() => {
            const c = extData.claude;
            const warnColor = c.warning ? '#d97706' : '#059669';
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1, background: 'var(--bg3)', borderRadius: 6, padding: '8px 12px' }}>
                    <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Heute</div>
                    <div style={{ fontSize: 20, fontWeight: 800, fontFamily: 'var(--mono)', color: c.warning ? '#d97706' : 'var(--text)' }}>
                      {c.today_eur?.toFixed(2)} €
                    </div>
                    <div style={{ fontSize: 10.5, color: 'var(--text3)' }}>von {c.budget_daily} € Budget</div>
                  </div>
                  <div style={{ flex: 1, background: 'var(--bg3)', borderRadius: 6, padding: '8px 12px' }}>
                    <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Dieser Monat</div>
                    <div style={{ fontSize: 20, fontWeight: 800, fontFamily: 'var(--mono)', color: 'var(--text)' }}>
                      {c.month_eur?.toFixed(2)} €
                    </div>
                    <div style={{ fontSize: 10.5, color: 'var(--text3)' }}>von {c.budget_monthly} € Budget</div>
                  </div>
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--text3)', marginBottom: 4 }}>
                    <span>Tages-Budget</span><span style={{ color: warnColor }}>{c.daily_pct}%</span>
                  </div>
                  <div style={{ background: 'var(--bg4)', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.min(c.daily_pct, 100)}%`, height: '100%', background: warnColor, borderRadius: 4, transition: 'width 0.4s' }} />
                  </div>
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--text3)', marginBottom: 4 }}>
                    <span>Monats-Budget</span><span style={{ color: c.monthly_pct > 80 ? '#d97706' : '#059669' }}>{c.monthly_pct}%</span>
                  </div>
                  <div style={{ background: 'var(--bg4)', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.min(c.monthly_pct, 100)}%`, height: '100%', background: c.monthly_pct > 80 ? '#d97706' : '#059669', borderRadius: 4, transition: 'width 0.4s' }} />
                  </div>
                </div>
                {c.warning && (
                  <div style={{ fontSize: 11, background: '#fef3c7', color: '#92400e', padding: '6px 10px', borderRadius: 6, border: '1px solid #fcd34d' }}>
                    ⚠️ Tages-Budget zu {c.daily_pct}% ausgeschöpft
                  </div>
                )}
              </div>
            );
          })() : <div style={{ textAlign: 'center', padding: 16 }}><Spin size={16} /></div>}
        </div>

        {/* Orchestrator Status */}
        <div className="card">
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 12 }}>⚙️ Overnight Orchestrator</div>
          {extData?.orchestrator ? (() => {
            const o = extData.orchestrator;
            const statusColor = { completed: '#059669', partial: '#d97706', running: '#4f6ef7', unknown: '#9ca3af', error: '#dc2626' }[o.status] || '#9ca3af';
            const statusLabel = { completed: 'Abgeschlossen', partial: 'Teilweise', running: 'Läuft…', unknown: 'Unbekannt', error: 'Fehler' }[o.status] || o.status;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: statusColor, flexShrink: 0,
                    boxShadow: `0 0 6px ${statusColor}88` }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: statusColor }}>{statusLabel}</div>
                    {o.last_run && <div style={{ fontSize: 10.5, color: 'var(--text3)' }}>Letzter Run: {o.last_run}</div>}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                  {[
                    { label: '✅ Erfolg',    value: o.succeeded,          color: '#059669' },
                    { label: '❌ Fehler',    value: o.failed,             color: o.failed > 0 ? '#dc2626' : 'var(--text3)' },
                    { label: '⏳ Approvals', value: o.pending_approvals,  color: o.pending_approvals > 0 ? '#d97706' : 'var(--text3)' },
                  ].map(({ label, value, color }) => (
                    <div key={label} style={{ background: 'var(--bg3)', borderRadius: 6, padding: '8px 10px', textAlign: 'center' }}>
                      <div style={{ fontSize: 18, fontWeight: 800, fontFamily: 'var(--mono)', color }}>{value ?? '—'}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{label}</div>
                    </div>
                  ))}
                </div>
                {o.status === 'unknown' && (
                  <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center', padding: '8px 0' }}>
                    Kein Run-Log gefunden
                  </div>
                )}
              </div>
            );
          })() : <div style={{ textAlign: 'center', padding: 16 }}><Spin size={16} /></div>}
        </div>

        {/* Postgres DB Health */}
        <div className="card">
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 12 }}>🗄️ Postgres (p4a-audit)</div>
          {extData?.postgres ? (() => {
            const pg = extData.postgres;
            if (!pg.ok) return (
              <div style={{ fontSize: 11.5, color: '#dc2626', background: '#fee2e2', padding: '8px 12px', borderRadius: 6 }}>
                ⚠️ {pg.error || 'Nicht erreichbar'}
              </div>
            );
            return (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[
                  { label: 'DB-Größe',     value: pg.size_pretty,           icon: '💾' },
                  { label: 'Verbindungen', value: pg.connections,            icon: '🔗' },
                  { label: 'Tabellen',     value: pg.tables,                 icon: '📋' },
                  { label: 'Status',       value: pg.ok ? 'Online' : 'Offline', icon: pg.ok ? '✅' : '⚠️' },
                ].map(({ label, value, icon }) => (
                  <div key={label} style={{ background: 'var(--bg3)', borderRadius: 6, padding: '8px 12px' }}>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 3 }}>{icon} {label}</div>
                    <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'var(--mono)', color: 'var(--text)' }}>{value ?? '—'}</div>
                  </div>
                ))}
              </div>
            );
          })() : <div style={{ textAlign: 'center', padding: 16 }}><Spin size={16} /></div>}
        </div>

      </div>

    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Tab — Sessions & Prozesse
───────────────────────────────────────────────────────────────── */
const STATUS_META = {
  running:    { color: '#059669', bg: '#d1fae5', icon: '▶', label: 'Läuft' },
  qa_running: { color: '#1a56db', bg: '#dbeafe', icon: '🔍', label: 'QA läuft' },
  done:       { color: '#059669', bg: '#d1fae5', icon: '✓', label: 'Fertig' },
  failed:     { color: '#dc2626', bg: '#fee2e2', icon: '✗', label: 'Fehler' },
  starting:   { color: '#d97706', bg: '#fef3c7', icon: '⏳', label: 'Startet' },
  waiting:    { color: '#d97706', bg: '#fef3c7', icon: '⏸', label: 'Wartet auf Approval' },
  idle:       { color: '#6b7280', bg: 'var(--bg3)', icon: '○', label: 'Bereit' },
};

function QaBadge({ qa }) {
  const ok = qa?.status === 'pass';
  const fail = qa?.status === 'fail';
  const color = ok ? '#059669' : fail ? '#dc2626' : '#9ca3af';
  const bg    = ok ? '#d1fae5' : fail ? '#fee2e2' : 'var(--bg3)';
  const label = ok ? 'QA ✓' : fail ? `QA ✗${qa.issue_count ? ` (${qa.issue_count})` : ''}` : 'QA ?';
  return (
    <span style={{
      fontSize: 10.5, padding: '2px 7px', borderRadius: 8,
      background: bg, color, border: `1px solid ${color}33`, fontWeight: 600,
    }}>{label}</span>
  );
}

function SessionsTab({ token }) {
  const { projects } = useContext(ProjectsCtx);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [newTaskForm, setNewTaskForm] = useState({ show: false, project: '', title: '', priority: 'P1', description: '' });
  const [newApprForm, setNewApprForm] = useState({ show: false, project: '', title: '', priority: 'P2', risk: 'MEDIUM', description: '' });
  const [actionMsg,  setActionMsg]  = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [killingPids, setKillingPids] = useState(new Set());
  const isMounted = useRef(true);
  useEffect(() => { return () => { isMounted.current = false; }; }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiFetch(token, '/cockpit/sessions');
      if (!isMounted.current) return;
      setData(d);
    } catch (e) { if (isMounted.current) setActionMsg({ ok: false, text: e.message }); }
    finally { if (isMounted.current) setLoading(false); }
  }, [token]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 20000);
    return () => clearInterval(iv);
  }, [load]);

  async function approveFromDash(approvalId, action) {
    try {
      await apiFetch(token, `/cockpit/approvals/${approvalId}/${action}`, { method: 'POST' });
      setActionMsg({ ok: true, text: `Freigabe ${action === 'approve' ? 'erteilt' : 'abgelehnt'}.` });
      setTimeout(() => { setActionMsg(null); load(); }, 1500);
    } catch (e) { setActionMsg({ ok: false, text: e.message }); }
  }

  async function addPriorityTask() {
    const f = newTaskForm;
    if (!f.project || !f.title.trim()) return;
    try {
      await apiFetch(token, `/cockpit/registry/add-task/${f.project}`, {
        method: 'POST',
        body: JSON.stringify({ title: f.title, priority: f.priority, description: f.description, risk: 'LOW' }),
      });
      setActionMsg({ ok: true, text: `Task "${f.title}" in ${f.project} angelegt.` });
      setNewTaskForm({ show: false, project: '', title: '', priority: 'P1', description: '' });
      setTimeout(() => { setActionMsg(null); load(); }, 1500);
    } catch (e) { setActionMsg({ ok: false, text: e.message }); }
  }

  async function killProcess(pid) {
    if (!window.confirm(`Prozess ${pid} wirklich beenden?`)) return;
    setKillingPids(prev => new Set(prev).add(pid));
    try {
      const res = await apiFetch(token, `/cockpit/processes/${pid}`, { method: 'DELETE' });
      setActionMsg({ ok: true, text: `Prozess ${pid} beendet (${res.method}).` });
      setTimeout(() => { setActionMsg(null); load(); }, 2000);
    } catch (e) {
      setActionMsg({ ok: false, text: `Kill fehlgeschlagen: ${e.message}` });
    } finally {
      setKillingPids(prev => { const s = new Set(prev); s.delete(pid); return s; });
    }
  }

  if (!data && loading) return <div style={{ textAlign: 'center', padding: 40 }}><Spin size={24} /></div>;
  if (!data) return null;

  const allProcesses = data.processes || [];
  const claudeProcs  = allProcesses.filter(p => p.cmd?.includes('claude'));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {actionMsg && (
        <div className={`alert ${actionMsg.ok ? 'alert-ok' : 'alert-err'}`}>{actionMsg.text}</div>
      )}

      {/* ── Header ─────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 12, color: 'var(--text3)' }}>
          Stand: {data.generated_at?.slice(11, 16)} · Cron: alle 2h (nächste: {data.sessions?.[0]?.next_run?.display})
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setNewTaskForm(f => ({ ...f, show: true }))}>
            + Priority Task
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => setNewApprForm(f => ({ ...f, show: true }))}>
            + Freigabe
          </button>
          <button className="btn btn-secondary btn-sm" onClick={load}>
            {loading ? <Spin size={11} /> : '↻'} Refresh
          </button>
        </div>
      </div>

      {/* ── Active Claude Processes ────────────────────────────── */}
      <div className="card">
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
          ⚡ Aktive Claude-Prozesse
          <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text3)' }}>({claudeProcs.length} erkannt)</span>
        </div>
        {claudeProcs.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text3)', fontStyle: 'italic' }}>
            Keine laufenden Claude-Prozesse erkannt. (Erkennbar sobald Orchestrator oder claude-terminal aktiv ist)
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {claudeProcs.map((p, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '6px 10px', background: 'var(--bg3)',
                border: '1px solid var(--border)', borderRadius: 6,
              }}>
                <span style={{
                  width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                  background: p.stat?.startsWith('S') ? '#d97706' : '#059669',
                  boxShadow: '0 0 5px rgba(5,150,105,0.4)',
                }} />
                <span style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)', width: 36 }}>
                  {p.pid}
                </span>
                <span style={{
                  fontSize: 10.5, padding: '1px 5px', borderRadius: 4,
                  background: p.container === 'host' ? 'var(--bg4)' : 'var(--accent-light)',
                  color: p.container === 'host' ? 'var(--text3)' : 'var(--accent)',
                  fontWeight: 600,
                }}>
                  {p.container}
                </span>
                <span style={{ fontSize: 11.5, color: 'var(--text)', fontFamily: 'var(--mono)', flex: 1,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.cmd}
                </span>
                <span style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)', marginRight: 6 }}>{p.stat}</span>
                <button
                  onClick={() => killProcess(p.pid)}
                  disabled={killingPids.has(p.pid)}
                  title={`Prozess ${p.pid} beenden`}
                  style={{
                    padding: '2px 7px', fontSize: 11, fontWeight: 700,
                    background: killingPids.has(p.pid) ? 'var(--bg4)' : '#7f1d1d',
                    color: killingPids.has(p.pid) ? 'var(--text3)' : '#fca5a5',
                    border: '1px solid #991b1b', borderRadius: 4, cursor: 'pointer',
                    lineHeight: 1.4, flexShrink: 0,
                  }}
                >
                  {killingPids.has(p.pid) ? '...' : '✕ Kill'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Project Sessions (2-column grid) ──────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 12 }}>
        {(data.sessions || []).map(s => {
          const proj     = projects.find(p => p.id === s.project_id);
          const stateKey = s.is_running ? (s.state?.status || 'starting') : s.pending_approvals?.length > 0 ? 'waiting' : 'idle';
          const sm       = STATUS_META[stateKey] || STATUS_META.idle;

          return (
            <div key={s.project_id} className="card" style={{
              borderLeft: `3px solid ${s.is_running ? (proj?.color || 'var(--accent)') : 'var(--border)'}`,
            }}>
              {/* Card header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 18 }}>{s.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{s.label}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                    <span style={{
                      fontSize: 10.5, padding: '1px 6px', borderRadius: 6,
                      background: sm.bg, color: sm.color, fontWeight: 600,
                    }}>
                      {sm.icon} {sm.label}
                    </span>
                    <QaBadge qa={s.qa} />
                    {s.todo_count > 0 && (
                      <span style={{ fontSize: 10.5, color: 'var(--text3)' }}>
                        {s.todo_count} TODO
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Running task */}
              {s.is_running && s.state?.task && (
                <div style={{
                  padding: '8px 10px', background: 'var(--accent-light)',
                  border: '1px solid var(--accent)', borderRadius: 7, marginBottom: 8,
                }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 3 }}>
                    Aktueller Task
                  </div>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{s.state.task}</div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                    {s.state.priority && <PriBadge priority={s.state.priority} />}
                    {s.state.risk && <RiskBadge risk={s.state.risk} />}
                    {s.state.started_at && (
                      <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 'auto' }}>
                        seit {s.state.started_at.slice(11, 16)}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Pending approvals */}
              {(s.pending_approvals || []).map(a => (
                <div key={a.id} style={{
                  padding: '8px 10px', background: '#fef3c7',
                  border: '1px solid #d97706', borderRadius: 7, marginBottom: 8,
                }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 3 }}>
                    Freigabe erforderlich
                  </div>
                  <div style={{ fontSize: 12, color: '#1c1917', fontWeight: 600, marginBottom: 6 }}>{a.title}</div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <PriBadge priority={a.priority} />
                    <RiskBadge risk={a.risk} />
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 5 }}>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => approveFromDash(a.id, 'approve')}
                        style={{ fontSize: 11, padding: '3px 10px' }}
                      >
                        ✓ Freigeben
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => approveFromDash(a.id, 'reject')}
                        style={{ fontSize: 11, padding: '3px 10px', color: 'var(--red)', borderColor: 'var(--red)' }}
                      >
                        ✗ Ablehnen
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {/* TODO queue */}
              {s.todo_queue?.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 5 }}>
                    Nächste Tasks
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {s.todo_queue.map((t, i) => (
                      <div key={t.id || i} onClick={() => setSelectedTask({ ...t, _project: s.project_id })} style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '4px 8px', background: 'var(--bg3)',
                        border: '1px solid var(--border)', borderRadius: 5, cursor: 'pointer',
                      }}>
                        <span style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)', width: 14, flexShrink: 0 }}>
                          {i + 1}.
                        </span>
                        <PriBadge priority={t.priority} />
                        <span style={{ fontSize: 12, color: 'var(--text)', flex: 1,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {t.title}
                        </span>
                        {t.risk && t.risk !== 'LOW' && <RiskBadge risk={t.risk} />}
                      </div>
                    ))}
                    {s.todo_count > 6 && (
                      <div style={{ fontSize: 10.5, color: 'var(--text3)', paddingLeft: 8 }}>
                        + {s.todo_count - 6} weitere Tasks
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* QA issues */}
              {s.qa?.status === 'fail' && s.qa.issues?.length > 0 && (
                <div style={{ marginTop: 8, padding: '6px 8px', background: '#fee2e2', borderRadius: 6, border: '1px solid #dc262633' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#dc2626', marginBottom: 3 }}>QA Issues</div>
                  {s.qa.issues.slice(0, 2).map((issue, i) => (
                    <div key={i} style={{ fontSize: 11, color: '#7f1d1d', marginTop: 1 }}>
                      {issue.severity === 'critical' ? '🔴' : issue.severity === 'high' ? '🟠' : '🟡'} {issue.detail}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── New Priority Task Modal ────────────────────────────── */}
      {newTaskForm.show && (
        <Modal onClose={() => setNewTaskForm(f => ({ ...f, show: false }))}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Priority Task einplanen</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => setNewTaskForm(f => ({ ...f, show: false }))}>✕</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="form-group">
              <label className="field-label">Projekt *</label>
              <select className="field-input" value={newTaskForm.project}
                onChange={e => setNewTaskForm(f => ({ ...f, project: e.target.value }))}>
                <option value="">— Projekt wählen —</option>
                {projects.filter(p => !p.placeholder && !p.scoping).map(p => <option key={p.id} value={p.id}>{p.icon} {p.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="field-label">Task-Titel *</label>
              <input className="field-input" value={newTaskForm.title}
                onChange={e => setNewTaskForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Kurzer, präziser Titel" />
            </div>
            <div className="form-group">
              <label className="field-label">Priorität</label>
              <select className="field-input" value={newTaskForm.priority}
                onChange={e => setNewTaskForm(f => ({ ...f, priority: e.target.value }))}>
                <option value="P1">P1 — Sofort (nächste Session)</option>
                <option value="P2">P2 — Hoch</option>
                <option value="P3">P3 — Normal</option>
              </select>
            </div>
            <div className="form-group">
              <label className="field-label">Beschreibung / Kontext</label>
              <textarea className="field-input form-textarea" rows={3}
                value={newTaskForm.description}
                onChange={e => setNewTaskForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Was soll der Orchestrator tun?" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button className="btn btn-primary" onClick={addPriorityTask}
              disabled={!newTaskForm.project || !newTaskForm.title.trim()}>
              ▶ Task einplanen
            </button>
            <button className="btn btn-secondary" onClick={() => setNewTaskForm(f => ({ ...f, show: false }))}>
              Abbrechen
            </button>
          </div>
        </Modal>
      )}

      {/* ── New Approval Modal ─────────────────────────────────── */}
      {newApprForm.show && (
        <Modal onClose={() => setNewApprForm(f => ({ ...f, show: false }))}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Freigabe anfordern</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => setNewApprForm(f => ({ ...f, show: false }))}>✕</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="form-group">
              <label className="field-label">Projekt *</label>
              <select className="field-input" value={newApprForm.project}
                onChange={e => setNewApprForm(f => ({ ...f, project: e.target.value }))}>
                <option value="">— Projekt wählen —</option>
                {projects.filter(p => !p.placeholder && !p.scoping).map(p => <option key={p.id} value={p.id}>{p.icon} {p.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="field-label">Titel *</label>
              <input className="field-input" value={newApprForm.title}
                onChange={e => setNewApprForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Wofür wird die Freigabe benötigt?" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="field-label">Priorität</label>
                <select className="field-input" value={newApprForm.priority}
                  onChange={e => setNewApprForm(f => ({ ...f, priority: e.target.value }))}>
                  <option value="P1">P1 — Kritisch</option>
                  <option value="P2">P2 — Hoch</option>
                  <option value="P3">P3 — Normal</option>
                </select>
              </div>
              <div className="form-group">
                <label className="field-label">Risiko</label>
                <select className="field-input" value={newApprForm.risk}
                  onChange={e => setNewApprForm(f => ({ ...f, risk: e.target.value }))}>
                  <option value="HIGH">HIGH</option>
                  <option value="MEDIUM">MEDIUM</option>
                  <option value="LOW">LOW</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="field-label">Beschreibung</label>
              <textarea className="field-input form-textarea" rows={2}
                value={newApprForm.description}
                onChange={e => setNewApprForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Details zur Freigabe" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button
              className="btn btn-primary"
              disabled={!newApprForm.project || !newApprForm.title.trim()}
              onClick={async () => {
                try {
                  await apiFetch(token, '/cockpit/approvals/create', {
                    method: 'POST',
                    body: JSON.stringify({
                      project_id: newApprForm.project, title: newApprForm.title,
                      priority: newApprForm.priority, risk: newApprForm.risk,
                      description: newApprForm.description,
                    }),
                  });
                  setActionMsg({ ok: true, text: 'Freigabe angelegt.' });
                  setNewApprForm({ show: false, project: '', title: '', priority: 'P2', risk: 'MEDIUM', description: '' });
                  setTimeout(() => { setActionMsg(null); load(); }, 1500);
                } catch (e) { setActionMsg({ ok: false, text: e.message }); }
              }}
            >
              + Freigabe anlegen
            </button>
            <button className="btn btn-secondary" onClick={() => setNewApprForm(f => ({ ...f, show: false }))}>
              Abbrechen
            </button>
          </div>
        </Modal>
      )}
      {selectedTask && (
        <TaskDetailModal task={selectedTask} token={token} onClose={() => setSelectedTask(null)} onRefresh={load} />
      )}
    </div>
  );
}


// ── Claude Nutzungs-Tab ────────────────────────────────────────────────────────
function ClaudeUsageTab({ token }) {
  const [data, setData]         = useState(null);
  const [tokData, setTokData]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [section, setSection]   = useState('kosten');
  const [budget, setBudget]     = useState(null);
  const [budgetEdit, setBudgetEdit] = useState(false);
  const [budgetDay, setBudgetDay]   = useState('');
  const [budgetMonth, setBudgetMonth] = useState('');
  const [savingBudget, setSavingBudget] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [activity, tokens, bgt] = await Promise.all([
        apiFetch(token, '/cockpit/claude/usage'),
        apiFetch(token, '/cockpit/claude/token-usage').catch(() => null),
        apiFetch(token, '/cockpit/claude/budget').catch(() => null),
      ]);
      setData(activity);
      setTokData(tokens);
      setBudget(bgt);
      if (bgt) { setBudgetDay(String(bgt.daily_eur)); setBudgetMonth(String(bgt.monthly_eur)); }
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  async function saveBudget() {
    setSavingBudget(true);
    try {
      await apiFetch(token, '/cockpit/claude/budget', {
        method: 'POST',
        body: JSON.stringify({ daily_eur: parseFloat(budgetDay)||30, monthly_eur: parseFloat(budgetMonth)||500 }),
      });
      setBudgetEdit(false);
      load();
    } finally { setSavingBudget(false); }
  }

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)' }}><Spin size={20} /> Lade Nutzungsdaten…</div>;
  if (error)   return <div style={{ padding: 24, color: '#dc2626' }}>{error}</div>;

  const fmtK  = n => n == null ? '–' : n >= 1000000 ? (n/1000000).toFixed(1)+'M' : n >= 1000 ? (n/1000).toFixed(1)+'K' : String(n);
  const fmtEur = n => n == null ? '–' : `${Number(n).toFixed(2)} €`;

  const tokDays   = Object.entries(tokData?.daily || {}).sort(([a],[b]) => a.localeCompare(b)).slice(-14);
  const maxCostDay = Math.max(...tokDays.map(([,v]) => v.cost_eur || 0), 0.01);
  const days      = Object.entries(data?.daily_activity || {}).sort(([a],[b]) => a.localeCompare(b)).slice(-14);
  const maxDay    = Math.max(...days.map(([,v]) => v), 1);
  const projEntries = Object.entries(data?.project_counts || {}).sort(([,a],[,b]) => b - a);
  const maxProj   = Math.max(...projEntries.map(([,v]) => v), 1);

  const bgt = tokData?.budget || budget || {};
  const todayEur = tokData?.today?.cost_eur || 0;
  const monthEur = tokData?.month?.cost_eur || 0;
  const projEur  = tokData?.projected_month_eur || 0;
  const dailyPct  = bgt.daily_eur   ? Math.min(100, Math.round(todayEur / bgt.daily_eur * 100))   : null;
  const monthlyPct = bgt.monthly_eur ? Math.min(100, Math.round(monthEur / bgt.monthly_eur * 100)) : null;

  const TabBtn = ({ id, label }) => (
    <button onClick={() => setSection(id)} style={{
      padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
      background: section === id ? '#1a56db' : 'var(--bg3)',
      color: section === id ? '#fff' : 'var(--text3)', transition: 'all .15s',
    }}>{label}</button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 16 }}>

      {/* Section switcher */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <TabBtn id="kosten"   label="💶 Kosten (EUR)" />
          <TabBtn id="tokens"   label="🪙 Tokens" />
          <TabBtn id="activity" label="💬 Aktivität" />
        </div>
        <button className="btn btn-secondary btn-sm" onClick={load}>↻</button>
      </div>

      {/* Budget-Warnung */}
      {tokData?.budget_warning && (
        <div style={{ padding: '10px 14px', background: '#7c2d12', borderRadius: 8, border: '1px solid #ea580c',
          fontSize: 12, color: '#fed7aa', display: 'flex', alignItems: 'center', gap: 8 }}>
          ⚠️ {tokData.budget_warning}
        </div>
      )}

      {/* Disclaimer */}
      <div style={{ padding: '8px 12px', background: 'var(--bg3)', borderRadius: 8, border: '1px solid var(--border)',
        fontSize: 11, color: 'var(--text3)', display: 'flex', gap: 8 }}>
        <span>ℹ️</span>
        <span><strong style={{ color: 'var(--text2)' }}>Nur Dashboard-API-Calls erfasst</strong> — Claude Code CLI (Terminal-Sessions, tägliche 50–100 €) werden direkt von Anthropic abgerechnet und sind hier <em>nicht</em> enthalten. Echte Gesamtkosten → <a href="https://console.anthropic.com/settings/billing" target="_blank" rel="noreferrer" style={{ color: '#60a5fa' }}>Anthropic Console</a></span>
      </div>

      {/* ── KOSTEN SECTION ── */}
      {section === 'kosten' && (
        <>
          {/* KPI Row EUR */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {[
              { label: 'Heute (Dashboard)',   val: fmtEur(todayEur),   sub: `${tokData?.today?.calls||0} Aufrufe`,   icon: '📅', color: dailyPct >= 80 ? '#dc2626' : dailyPct >= 50 ? '#d97706' : '#059669' },
              { label: 'Woche (Dashboard)',   val: fmtEur(tokData?.week?.cost_eur),  sub: `${tokData?.week?.calls||0} Aufrufe`,  icon: '📊', color: '#1a56db' },
              { label: 'Monat (Dashboard)',   val: fmtEur(monthEur),   sub: `${tokData?.month?.calls||0} Aufrufe`,  icon: '🗓️', color: '#7c3aed' },
              { label: 'Prognose Monat',      val: fmtEur(projEur),    sub: 'linear hochgerechnet',                  icon: '📈', color: projEur > (bgt.monthly_eur||500) ? '#dc2626' : '#d97706' },
            ].map(k => (
              <div key={k.label} className="card" style={{ flex: '1 1 150px', textAlign: 'center', padding: '14px 12px' }}>
                <div style={{ fontSize: 20 }}>{k.icon}</div>
                <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: -1, color: k.color, lineHeight: 1.1, marginTop: 4 }}>{k.val}</div>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3, textTransform: 'uppercase', letterSpacing: 0.5 }}>{k.label}</div>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>{k.sub}</div>
              </div>
            ))}
          </div>

          {/* Budget-Balken */}
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>💶 Budget-Übersicht (nur Dashboard-Calls)</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setBudgetEdit(e => !e)}>
                {budgetEdit ? 'Abbrechen' : '✏️ Budget anpassen'}
              </button>
            </div>
            {budgetEdit ? (
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <label style={{ fontSize: 11, color: 'var(--text2)' }}>
                  Tages-Budget (€)
                  <input value={budgetDay} onChange={e => setBudgetDay(e.target.value)}
                    style={{ display: 'block', marginTop: 4, padding: '5px 8px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 13, width: 90 }} />
                </label>
                <label style={{ fontSize: 11, color: 'var(--text2)' }}>
                  Monats-Budget (€)
                  <input value={budgetMonth} onChange={e => setBudgetMonth(e.target.value)}
                    style={{ display: 'block', marginTop: 4, padding: '5px 8px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 13, width: 100 }} />
                </label>
                <button className="btn btn-primary btn-sm" onClick={saveBudget} disabled={savingBudget}>
                  {savingBudget ? '…' : 'Speichern'}
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { label: `Heute: ${fmtEur(todayEur)} von ${fmtEur(bgt.daily_eur)} Tagesbudget`, pct: dailyPct },
                  { label: `Monat: ${fmtEur(monthEur)} von ${fmtEur(bgt.monthly_eur)} Monatsbudget`, pct: monthlyPct },
                ].map(({ label, pct }) => (
                  <div key={label}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text2)', marginBottom: 4 }}>
                      <span>{label}</span><span>{pct != null ? `${pct}%` : '–'}</span>
                    </div>
                    <div style={{ height: 8, background: 'var(--bg3)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 4, transition: 'width .3s',
                        width: `${pct||0}%`,
                        background: pct >= 80 ? '#dc2626' : pct >= 50 ? '#d97706' : '#059669',
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tageskosten Chart (EUR) */}
          <div className="card">
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 12, color: 'var(--text)' }}>Tageskosten — letzte 14 Tage (€, Dashboard-Calls)</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 90 }}>
              {tokDays.length > 0 ? tokDays.map(([day, v]) => {
                const eur = v.cost_eur || 0;
                return (
                  <div key={day} title={`${day}: ${fmtEur(eur)} (${(v.calls||0)} Aufrufe)`}
                    style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                    <div style={{ fontSize: 8, color: 'var(--text3)', marginBottom: 1 }}>
                      {eur > 0 ? fmtEur(eur) : ''}
                    </div>
                    <div style={{
                      width: '100%', borderRadius: '3px 3px 0 0',
                      height: `${Math.max(4, (eur / maxCostDay) * 65)}px`,
                      background: eur >= (bgt.daily_eur||30) * 0.8 ? '#dc2626' : '#1a56db',
                      opacity: 0.85,
                    }} />
                    <div style={{ fontSize: 8, color: 'var(--text3)', transform: 'rotate(-45deg)', whiteSpace: 'nowrap' }}>{day.slice(5)}</div>
                  </div>
                );
              }) : (
                <div style={{ color: 'var(--text3)', fontSize: 12, padding: '20px 0' }}>Noch keine Daten</div>
              )}
            </div>
          </div>

          {/* Kosten je Quelle */}
          <div className="card">
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 12, color: 'var(--text)' }}>Kosten je Quelle — laufender Monat</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {Object.entries(tokData?.by_source || {}).sort(([,a],[,b]) => (b.cost_eur||0)-(a.cost_eur||0)).slice(0, 6).map(([src, v]) => {
                const allEur = Object.values(tokData?.by_source||{}).reduce((s,x) => s+(x.cost_eur||0), 0) || 1;
                return (
                  <div key={src} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 85, fontSize: 11, color: 'var(--text2)', flexShrink: 0 }}>{src}</div>
                    <div style={{ flex: 1, background: 'var(--bg3)', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                      <div style={{ width: `${((v.cost_eur||0)/allEur)*100}%`, height: '100%', background: '#1a56db', borderRadius: 4 }} />
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', width: 55, textAlign: 'right' }}>{fmtEur(v.cost_eur)}</div>
                  </div>
                );
              })}
              {Object.keys(tokData?.by_source || {}).length === 0 && (
                <div style={{ color: 'var(--text3)', fontSize: 12 }}>Noch keine Daten</div>
              )}
            </div>
          </div>

          {/* Recent Calls mit EUR-Kosten */}
          {tokData?.recent?.length > 0 && (
            <div className="card">
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10, color: 'var(--text)' }}>Letzte API-Aufrufe</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      {['Zeit', 'Quelle', 'Projekt', 'Modell', 'Input', 'Output', 'Kosten'].map(h => (
                        <th key={h} style={{ padding: '4px 8px', textAlign: ['Zeit','Quelle','Projekt','Modell'].includes(h) ? 'left' : 'right',
                          color: 'var(--text3)', fontWeight: 500, fontSize: 10 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tokData.recent.slice(0, 15).map((r, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '4px 8px', color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: 9.5 }}>{r.ts ? fmtDate(r.ts) + ' ' + fmtTime(r.ts) : '–'}</td>
                        <td style={{ padding: '4px 8px', color: '#1a56db', fontFamily: 'var(--mono)', fontSize: 9.5 }}>{r.source||'–'}</td>
                        <td style={{ padding: '4px 8px', color: 'var(--text2)', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.project||'–'}</td>
                        <td style={{ padding: '4px 8px', color: 'var(--text3)', fontSize: 9.5, maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(r.model||'–').replace('claude-','').replace('-20250514','')}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', color: 'var(--text3)' }}>{(r.input_tokens||0).toLocaleString()}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', color: 'var(--text3)' }}>{(r.output_tokens||0).toLocaleString()}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600, color: '#d97706' }}>{fmtEur(r.cost_eur)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── TOKENS SECTION ── */}
      {section === 'tokens' && (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {[
              { label: 'Tokens heute',  val: fmtK(tokData?.today?.total),   sub: `${tokData?.today?.calls||0} Aufrufe`,  icon: '⚡', color: '#1a56db' },
              { label: 'Tokens Woche',  val: fmtK(tokData?.week?.total),    sub: `${tokData?.week?.calls||0} Aufrufe`,   icon: '📅', color: '#059669' },
              { label: 'Tokens Monat',  val: fmtK(tokData?.month?.total),   sub: `${tokData?.month?.calls||0} Aufrufe`,  icon: '📊', color: '#7c3aed' },
              { label: 'Gesamt',        val: fmtK(tokData?.all_time?.total), sub: `${tokData?.all_time?.calls||0} Aufrufe`,icon: '📈', color: '#d97706' },
            ].map(k => (
              <div key={k.label} className="card" style={{ flex: '1 1 150px', textAlign: 'center', padding: '14px 12px' }}>
                <div style={{ fontSize: 20 }}>{k.icon}</div>
                <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: -1, color: k.color, lineHeight: 1.1, marginTop: 4 }}>{k.val}</div>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3, textTransform: 'uppercase', letterSpacing: 0.5 }}>{k.label}</div>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>{k.sub}</div>
              </div>
            ))}
          </div>
          <div className="card">
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 12, color: 'var(--text)' }}>Token-Verbrauch — letzte 14 Tage</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 80 }}>
              {tokDays.length > 0 ? tokDays.map(([day, v]) => (
                <div key={day} title={`${day}: ${(v.total||0).toLocaleString()} Tokens`}
                  style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                  <div style={{ width: '100%', background: '#1a56db', height: `${Math.max(4, ((v.total||0) / Math.max(...tokDays.map(([,x]) => x.total||0),1)) * 72)}px`, borderRadius: '3px 3px 0 0', opacity: 0.85 }} />
                  <div style={{ fontSize: 8, color: 'var(--text3)', transform: 'rotate(-45deg)', whiteSpace: 'nowrap' }}>{day.slice(5)}</div>
                </div>
              )) : <div style={{ color: 'var(--text3)', fontSize: 12 }}>Noch keine Daten</div>}
            </div>
          </div>
        </>
      )}

      {/* ── ACTIVITY SECTION ── */}
      {section === 'activity' && (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {[
              { label: 'Nachrichten gesamt',    val: data?.total_messages, icon: '💬' },
              { label: 'Aktive Prozesse',        val: data?.active_processes?.length || 0, icon: '⚡' },
              { label: 'Sessions (gespeichert)', val: data?.sessions?.length || 0, icon: '🗂️' },
              { label: 'Projekte aktiv (30d)',   val: Object.keys(data?.project_counts || {}).length, icon: '📁' },
            ].map(k => (
              <div key={k.label} className="card" style={{ flex: '1 1 160px', textAlign: 'center', padding: '14px 12px' }}>
                <div style={{ fontSize: 22 }}>{k.icon}</div>
                <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: -1, color: 'var(--text)', lineHeight: 1.1 }}>{k.val}</div>
                <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 4, textTransform: 'uppercase', letterSpacing: 0.6 }}>{k.label}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="card">
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 12, color: 'var(--text)' }}>Aktivität — letzte 14 Tage</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 80 }}>
                {days.map(([day, count]) => (
                  <div key={day} title={`${day}: ${count}`} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                    <div style={{ width: '100%', background: '#1a56db', height: `${Math.max(4, (count / maxDay) * 72)}px`, borderRadius: '3px 3px 0 0', opacity: 0.85 }} />
                    <div style={{ fontSize: 8, color: 'var(--text3)', transform: 'rotate(-45deg)', whiteSpace: 'nowrap' }}>{day.slice(5)}</div>
                  </div>
                ))}
                {days.length === 0 && <div style={{ color: 'var(--text3)', fontSize: 12 }}>Keine Daten</div>}
              </div>
            </div>
            <div className="card">
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 12, color: 'var(--text)' }}>Nachrichten je Projekt (30d)</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {projEntries.slice(0, 8).map(([proj, count]) => (
                  <div key={proj} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 90, fontSize: 11, color: 'var(--text2)', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{proj}</div>
                    <div style={{ flex: 1, background: 'var(--bg3)', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                      <div style={{ width: `${(count / maxProj) * 100}%`, height: '100%', background: '#1a56db', borderRadius: 4 }} />
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', width: 28, textAlign: 'right' }}>{count}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {data?.active_processes?.length > 0 && (
            <div className="card">
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10, color: 'var(--text)' }}>Laufende Claude Prozesse</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {data.active_processes.map(p => (
                  <div key={p.pid} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '6px 10px', background: 'var(--bg3)', borderRadius: 6, border: '1px solid var(--border)', fontFamily: 'var(--mono)', fontSize: 11.5 }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#059669', flexShrink: 0, boxShadow: '0 0 5px #05966666' }} />
                    <span style={{ color: 'var(--text3)', width: 50 }}>PID {p.pid}</span>
                    <span style={{ color: '#d97706', width: 45 }}>CPU {p.cpu}%</span>
                    <span style={{ color: '#1a56db', width: 45 }}>MEM {p.mem}%</span>
                    <span style={{ color: 'var(--text2)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.cmd}</span>
                    <span style={{ color: 'var(--text3)' }}>seit {p.start}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="card">
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10, color: 'var(--text)' }}>Letzte Nachrichten</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {data?.recent?.slice(0, 10).map((m, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '6px 10px', background: 'var(--bg3)', borderRadius: 6, fontSize: 11.5 }}>
                  <span style={{ color: 'var(--text3)', flexShrink: 0, fontFamily: 'var(--mono)', fontSize: 10 }}>
                    {new Date(m.timestamp).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Berlin' })} {new Date(m.timestamp).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' })}
                  </span>
                  <span style={{ color: '#1a56db', flexShrink: 0, fontFamily: 'var(--mono)', fontSize: 10, width: 70, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.project}</span>
                  <span style={{ color: 'var(--text2)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.display}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}


// ── Live Dashboard Tab ─────────────────────────────────────────────────────────

function Sparkline({ data, color = '#5b6af0', height = 48, width = 220 }) {
  if (!data || data.length < 2) return <svg width={width} height={height} />;
  const max = Math.max(...data, 1);
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - (v / max) * (height - 4) - 2;
    return `${x},${y}`;
  });
  const area = `M0,${height} L${pts.join(' L')} L${width},${height} Z`;
  const line = `M${pts.join(' L')}`;
  return (
    <svg width={width} height={height} style={{ overflow: 'visible' }}>
      <defs>
        <linearGradient id={`sg-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#sg-${color.replace('#','')})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="1.8"
            strokeLinecap="round" strokeLinejoin="round" />
      {/* Latest dot */}
      <circle cx={(data.length - 1) / (data.length - 1) * width}
              cy={height - (data[data.length-1] / max) * (height - 4) - 2}
              r="3" fill={color} />
    </svg>
  );
}

/* ── Orchestrator Schedule Timeline ─────────────────────────────────────── */
function ScheduleTimeline({ token }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const isMounted = useRef(true);
  useEffect(() => { return () => { isMounted.current = false; }; }, []);

  const load = useCallback(() => {
    apiFetch(token, '/cockpit/schedule')
      .then(d => { if (isMounted.current) { setData(d); setLoading(false); } })
      .catch(() => { if (isMounted.current) setLoading(false); });
  }, [token]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
  }, [load]);

  const PROJECT_COLORS = {
    'p4a-audit':   '#1a56db',
    'dev-dash':    '#0891b2',
    'prism-built': '#059669',
    'sales-coach': '#7c3aed',
  };
  const PRIO_COLORS = { P1: '#f05b5b', P2: '#f0c05b', P3: '#5b6af0' };

  if (loading) return (
    <div className="card" style={{ padding: 14, background: 'var(--bg2)' }}>
      <div style={{ fontSize: 11, color: 'var(--text3)' }}>Lade Schedule…</div>
    </div>
  );
  if (!data) return null;

  const allTasks  = data.tasks || [];
  const openTasks = allTasks.filter(t => t.status !== 'done');
  const doneCount = data.done_count || 0;
  const total     = data.total || allTasks.length;
  const pct       = total > 0 ? Math.round((doneCount / total) * 100) : 0;

  // ETA per open task: position 0 → next_run, position 1 → next_run + 1h, etc.
  function etaFor(positionIndex) {
    const minsTotal = (data.mins_until || 0) + positionIndex * 60;
    const h = Math.floor(minsTotal / 60);
    const m = minsTotal % 60;
    if (h === 0) return `in ${m} min`;
    if (m === 0) return `in ${h}h`;
    return `in ${h}h ${m}m`;
  }

  // Absolute clock time for each open task
  function etaTimestamp(positionIndex) {
    const now   = new Date();
    const nextH = new Date(now);
    nextH.setMinutes(0, 0, 0);
    nextH.setHours(nextH.getHours() + 1);
    const ms = nextH.getTime() + positionIndex * 3_600_000;
    return new Date(ms).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  }

  return (
    <div className="card" style={{ padding: 14, background: 'var(--bg2)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)',
          textTransform: 'uppercase', letterSpacing: 1 }}>Offene Tasks</div>
        <div style={{ flex: 1, height: 4, background: 'var(--bg3)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: '#5bf0a0',
            transition: 'width 0.5s ease', borderRadius: 2 }} />
        </div>
        <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: '#5bf0a0', whiteSpace: 'nowrap' }}>
          {doneCount}/{total} · nächster Run {data.next_run} ({data.mins_until} min)
        </span>
      </div>

      {/* Open tasks list */}
      {openTasks.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', padding: '10px 0' }}>
          ✓ Alle Tasks erledigt
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {openTasks.map((t, i) => {
            const isNext   = i === 0;
            const blocked  = t.status === 'blocked';
            const isWip    = t.status === 'wip';
            const projCol  = PROJECT_COLORS[t.project] || '#888';
            const prioCol  = PRIO_COLORS[t.priority]   || '#888';
            const dotCol   = blocked ? '#f05b5b' : isWip ? '#5b6af0' : (isNext ? '#f0c05b' : 'var(--border2)');
            const etaLabel = etaFor(i);
            const etaTime  = etaTimestamp(i);

            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 7,
                padding: '5px 8px', borderRadius: 5,
                background: isNext ? 'rgba(240,192,91,0.07)' : 'transparent',
                border: isNext ? '1px solid rgba(240,192,91,0.2)' : '1px solid transparent',
              }}>
                {/* Dot */}
                <div style={{
                  width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                  background: dotCol,
                  boxShadow: isNext ? `0 0 6px ${dotCol}` : 'none',
                  animation: isNext ? 'pulse-dot 1.5s infinite' : 'none',
                }} />

                {/* ETA */}
                <span style={{
                  fontSize: 10.5, fontFamily: 'var(--mono)', flexShrink: 0,
                  color: isNext ? '#f0c05b' : 'var(--text3)',
                  minWidth: 68,
                }}>{etaTime} <span style={{ opacity: 0.7 }}>({etaLabel})</span></span>

                {/* Priority badge */}
                <span style={{
                  fontSize: 9, fontWeight: 700, padding: '1px 4px', borderRadius: 3,
                  background: `${prioCol}22`, color: prioCol, flexShrink: 0,
                }}>{t.priority}</span>

                {/* Project badge */}
                <span style={{
                  fontSize: 9, fontWeight: 700, padding: '1px 4px', borderRadius: 3,
                  background: `${projCol}22`, color: projCol,
                  border: `1px solid ${projCol}33`, flexShrink: 0,
                  textTransform: 'uppercase',
                }}>{t.project}</span>

                {/* Task name */}
                <span style={{
                  fontSize: 11.5, flex: 1, minWidth: 0,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  color: isNext ? 'var(--text)' : 'var(--text2)',
                  fontWeight: isNext ? 600 : 400,
                }}>{t.name}</span>

                {/* Status flags */}
                {isWip && (
                  <span style={{ fontSize: 10, color: '#5b6af0', flexShrink: 0, fontWeight: 700 }}>▶ läuft</span>
                )}
                {blocked && (
                  <span style={{ fontSize: 10, color: '#f05b5b', flexShrink: 0 }}>⊘ blocked</span>
                )}
                {t.last_log?.result === 'failed' && (
                  <span style={{ fontSize: 10, color: '#f05b5b', flexShrink: 0 }}>✗ Fehler</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function LiveTab({ token }) {
  const HIST = 60; // data points to keep (60 × 3s = 3min window)
  const cpu  = useRef([]);
  const mem  = useRef([]);
  const netS = useRef(null); // baseline for delta
  const netR = useRef(null);
  const netSentHist = useRef([]);
  const netRecvHist = useRef([]);

  const [snap, setSnap]     = useState(null);
  const [tick, setTick]     = useState(0);   // force re-render
  const [pulse, setPulse]   = useState(false);
  const prevEvents = useRef([]);
  const isMounted = useRef(true);
  useEffect(() => { return () => { isMounted.current = false; }; }, []);

  const push = (arr, val) => {
    arr.current = [...arr.current.slice(-(HIST - 1)), val];
  };

  const load = useCallback(async () => {
    try {
      const data = await apiFetch(token, '/cockpit/live');
      if (!isMounted.current) return;
      setSnap(data);

      push(cpu, data.cpu_pct);
      push(mem, data.mem_pct);

      // Net delta MB/s
      if (netS.current !== null) {
        const ds = Math.max(0, (data.net_sent_mb - netS.current) * (1000 / 3));
        const dr = Math.max(0, (data.net_recv_mb - netR.current) * (1000 / 3));
        push(netSentHist, ds);
        push(netRecvHist, dr);
      }
      netS.current = data.net_sent_mb;
      netR.current = data.net_recv_mb;

      // Pulse wenn neue Events
      const newFirst = data.events?.[0]?.ts;
      if (newFirst && newFirst !== prevEvents.current[0]) {
        setPulse(true);
        setTimeout(() => { if (isMounted.current) setPulse(false); }, 800);
      }
      prevEvents.current = (data.events || []).map(e => e.ts);

      setTick(t => t + 1);
    } catch(e) { console.warn("[dev-dash]", e?.message || e); }
  }, [token]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 3000);
    return () => clearInterval(iv);
  }, [load]);

  const fmtAgo = s => {
    if (s < 60)  return `${s}s`;
    if (s < 3600) return `${Math.floor(s/60)}m`;
    return `${Math.floor(s/3600)}h`;
  };

  const gauge = (pct, color) => {
    const r = 22, circ = 2 * Math.PI * r;
    const dash = (pct / 100) * circ;
    return (
      <svg width={54} height={54} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={27} cy={27} r={r} fill="none" stroke="var(--bg3)" strokeWidth={5} />
        <circle cx={27} cy={27} r={r} fill="none" stroke={color} strokeWidth={5}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 0.6s ease' }} />
      </svg>
    );
  };

  const cpuColor  = (snap?.cpu_pct || 0) > 80 ? '#f05b5b' : (snap?.cpu_pct || 0) > 50 ? '#f0c05b' : '#5bf0a0';
  const memColor  = (snap?.mem_pct || 0) > 85 ? '#f05b5b' : (snap?.mem_pct || 0) > 65 ? '#f0c05b' : '#5b6af0';
  const netColor  = '#f0c05b';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#5bf0a0',
          boxShadow: '0 0 8px #5bf0a0', animation: 'pulse-dot 2s infinite' }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Live System Monitor</span>
        <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 4 }}>
          · 3s refresh · {snap ? new Date(snap.ts + 'Z').toLocaleTimeString('de-DE', { timeZone: 'Europe/Berlin' }) : '—'}
        </span>
      </div>

      {/* ── Sparkline Row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>

        {/* CPU */}
        <div className="card" style={{ padding: 14, background: 'var(--bg2)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1 }}>CPU</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: cpuColor, fontFamily: 'var(--mono)' }}>
                {snap?.cpu_pct ?? '—'}%
              </div>
            </div>
            {gauge(snap?.cpu_pct || 0, cpuColor)}
          </div>
          <Sparkline data={cpu.current} color={cpuColor} width={160} />
        </div>

        {/* RAM */}
        <div className="card" style={{ padding: 14, background: 'var(--bg2)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1 }}>RAM</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: memColor, fontFamily: 'var(--mono)' }}>
                {snap?.mem_pct ?? '—'}%
              </div>
              <div style={{ fontSize: 10, color: 'var(--text3)' }}>{snap?.mem_used_gb} GB</div>
            </div>
            {gauge(snap?.mem_pct || 0, memColor)}
          </div>
          <Sparkline data={mem.current} color={memColor} width={160} />
        </div>

        {/* Network */}
        <div className="card" style={{ padding: 14, background: 'var(--bg2)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1 }}>Netzwerk</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: netColor, fontFamily: 'var(--mono)' }}>
                ↑ {snap?.net_sent_mb ?? '—'} MB
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#5bf0c0', fontFamily: 'var(--mono)' }}>
                ↓ {snap?.net_recv_mb ?? '—'} MB
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'right' }}>
              <div>gesamt seit Boot</div>
            </div>
          </div>
          <Sparkline data={netRecvHist.current} color="#5bf0c0" width={160} />
        </div>
      </div>

      {/* ── Orchestrator Pulse ── */}
      <div className="card" style={{ padding: 14, background: 'var(--bg2)' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase',
          letterSpacing: 1, marginBottom: 10 }}>Orchestrator Status</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {(snap?.orchestrators || []).map(o => (
            <div key={o.project} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '5px 10px', borderRadius: 6,
              background: o.running ? 'rgba(91,240,160,0.1)' : 'var(--bg3)',
              border: `1px solid ${o.running ? '#5bf0a0' : 'var(--border)'}`,
            }}>
              <div style={{
                width: 7, height: 7, borderRadius: '50%',
                background: o.running ? '#5bf0a0' : '#55556a',
                boxShadow: o.running ? '0 0 6px #5bf0a0' : 'none',
                animation: o.running ? 'pulse-dot 1.5s infinite' : 'none',
              }} />
              <span style={{ fontSize: 11, fontWeight: 600, color: o.running ? '#5bf0a0' : 'var(--text3)' }}>
                {o.project}
              </span>
              {o.task && (
                <span style={{ fontSize: 10, color: 'var(--text3)', maxWidth: 140,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  · {o.task}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── Activity Feed ── */}
      <div className="card" style={{ padding: 14, background: 'var(--bg2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1 }}>
            Claude API Activity
          </div>
          {pulse && (
            <div style={{ fontSize: 10, color: '#5bf0a0', fontWeight: 700, animation: 'fadeout 0.8s forwards' }}>
              ● Neue Aktivität
            </div>
          )}
        </div>
        {(!snap?.events || snap.events.length === 0) ? (
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>Keine Aktivität</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {snap.events.map((ev, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '5px 8px', borderRadius: 5,
                background: i === 0 && pulse ? 'rgba(91,240,160,0.07)' : 'transparent',
                transition: 'background 0.5s',
              }}>
                <span style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)',
                  minWidth: 30, textAlign: 'right' }}>
                  {fmtAgo(ev.ago_s)}
                </span>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#5b6af0',
                  flexShrink: 0 }} />
                <span style={{ fontSize: 11, color: 'var(--text2)', flex: 1 }}>{ev.label}</span>
                <span style={{ fontSize: 10, fontFamily: 'var(--mono)', color: '#5bf0a0' }}>
                  {ev.tokens > 0 ? `${(ev.tokens/1000).toFixed(1)}k` : ''}
                </span>
                <span style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--text3)' }}>
                  {ev.cost_eur > 0 ? `€${ev.cost_eur}` : ''}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Orchestrator Schedule ── */}
      <ScheduleTimeline token={token} />

      <style>{`
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
        }
        @keyframes fadeout {
          0% { opacity: 1; } 100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}


// ── Project Setup Result Panel ─────────────────────────────────────────────────
function SetupResultPanel({ setup, onClose }) {
  const [checkedSteps, setCheckedSteps] = useState({});
  const toggleStep = (i) => setCheckedSteps(prev => ({ ...prev, [i]: !prev[i] }));

  return (
    <div style={{
      flexShrink: 0, background: 'rgba(245,158,11,0.06)', border: '2px solid rgba(245,158,11,0.4)',
      borderRadius: 10, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 20 }}>🚀</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 14, color: '#f59e0b' }}>Projekt aufgesetzt!</div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{setup.vision}</div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 18, padding: 0 }}>×</button>
      </div>

      {/* Project meta */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {[
          { label: 'Stack', val: setup.stack },
          { label: 'Domain', val: setup.domain },
          { label: 'MVP', val: setup.mvp },
        ].filter(f => f.val).map(f => (
          <div key={f.label} style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 10px', fontSize: 11 }}>
            <span style={{ color: 'var(--text3)' }}>{f.label}: </span>
            <span style={{ color: 'var(--text)' }}>{f.val}</span>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {/* Auto-done */}
        {setup.auto_done?.length > 0 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--green)', marginBottom: 6 }}>✅ Automatisch erledigt</div>
            {setup.auto_done.map((item, i) => (
              <div key={i} style={{ fontSize: 11, color: 'var(--text2)', padding: '2px 0', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                <span style={{ color: 'var(--green)', flexShrink: 0 }}>✓</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        )}

        {/* Manual steps */}
        {setup.manual_steps?.length > 0 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#f59e0b', marginBottom: 6 }}>🔲 Manuelle Schritte</div>
            {setup.manual_steps.map((step, i) => (
              <label key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer', padding: '3px 0' }}>
                <input
                  type="checkbox"
                  checked={!!checkedSteps[i]}
                  onChange={() => toggleStep(i)}
                  style={{ marginTop: 1, flexShrink: 0, accentColor: '#f59e0b' }}
                />
                <span style={{
                  fontSize: 11, color: checkedSteps[i] ? 'var(--text3)' : 'var(--text2)',
                  textDecoration: checkedSteps[i] ? 'line-through' : 'none',
                  fontFamily: step.includes('/') || step.includes('mkdir') || step.includes('git') ? 'var(--font-mono, monospace)' : 'inherit',
                }}>
                  {step}
                </span>
              </label>
            ))}
            <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 6 }}>
              {Object.values(checkedSteps).filter(Boolean).length}/{setup.manual_steps.length} erledigt
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ── Nuggets Panel ──────────────────────────────────────────────────────────────
function NuggetsPanel({ token, projectId }) {
  const [nuggets, setNuggets]     = useState([]);
  const [newText, setNewText]     = useState('');
  const [loading, setLoading]     = useState(false);
  const [open, setOpen]           = useState(false);

  const load = useCallback(() => {
    apiFetch(token, `/cockpit/nuggets/${projectId}`)
      .then(d => setNuggets(d.nuggets || []))
      .catch(() => {});
  }, [token, projectId]);

  useEffect(() => { load(); }, [load]);

  const addNugget = async () => {
    if (!newText.trim()) return;
    setLoading(true);
    try {
      await apiFetch(token, `/cockpit/nuggets/${projectId}`, {
        method: 'POST',
        body: JSON.stringify({ text: newText.trim() }),
      });
      setNewText('');
      load();
    } finally { setLoading(false); }
  };

  const deleteNugget = async (text) => {
    await apiFetch(token, `/cockpit/nuggets/${projectId}`, {
      method: 'DELETE',
      body: JSON.stringify({ text }),
    });
    load();
  };

  return (
    <div style={{ background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8, color: '#f59e0b' }}
      >
        <span style={{ fontSize: 14 }}>💡</span>
        <span style={{ fontSize: 12, fontWeight: 700 }}>Ideen-Nuggets</span>
        <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 4 }}>{nuggets.length} gespeichert</span>
        <span style={{ marginLeft: 'auto', fontSize: 11 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ padding: '0 12px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {nuggets.length === 0 && (
            <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center', padding: '8px 0' }}>
              Noch keine Nuggets. Ideen hier parken — der Orchestrator liest sie beim Scoping.
            </div>
          )}
          {nuggets.map((n, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, background: 'var(--bg3)', borderRadius: 6, padding: '6px 8px', fontSize: 12 }}>
              <span style={{ flex: 1, color: 'var(--text2)', lineHeight: 1.4 }}>{n.text}</span>
              <button onClick={() => deleteNugget(n.text)} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 13, padding: 0, flexShrink: 0 }}>×</button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            <input
              value={newText}
              onChange={e => setNewText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addNugget(); }}
              placeholder="Idee / Gedanke als Nugget hinzufügen…"
              style={{ flex: 1, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '5px 10px', fontSize: 12, color: 'var(--text)' }}
            />
            <button onClick={addNugget} disabled={loading || !newText.trim()} style={{
              background: newText.trim() ? '#f59e0b' : 'var(--bg3)', border: 'none', color: newText.trim() ? '#000' : 'var(--text3)',
              borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: newText.trim() ? 'pointer' : 'not-allowed',
            }}>
              + Hinzu
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Task Generator Tab ─────────────────────────────────────────────────────────
function TaskGeneratorTab({ token }) {
  const { projects } = useContext(ProjectsCtx);
  // Projekteliste aus Context (ohne Frontend-Platzhalter)
  const PROJECTS_LIST = projects.filter(p => !p.placeholder);

  // All hooks first — React rules require consistent hook ordering
  const [project, setProject]       = useState('bpos');
  const [messages, setMessages]     = useState([]);
  const [input, setInput]           = useState('');
  const [loading, setLoading]           = useState(false);
  const [recording, setRecording]       = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [tasksAdded, setTasksAdded]     = useState([]);
  const [attachments, setAttachments]   = useState([]); // {name, type, data, preview}
  const [projectSetup, setProjectSetup] = useState(null); // setup result after "Projekt aufsetzen"
  const [experts, setExperts]           = useState([]); // {id, role, description} — loaded per project via useEffect
  const [showExpertForm, setShowExpertForm] = useState(false);
  const [newExpertRole, setNewExpertRole]   = useState('');
  const [newExpertDesc, setNewExpertDesc]   = useState('');
  const [validProjects, setValidProjects]   = useState(null); // null = loading, Set = loaded
  const [projectError, setProjectError]     = useState(null); // error message if project not in backend
  const bottomRef                       = useRef(null);
  const mediaRecorderRef                = useRef(null);
  const chunksRef                       = useRef([]);
  const fileInputRef                    = useRef(null);
  const abortControllerRef              = useRef(null);  // iOS PWA: abort stale fetch on foreground
  const loadingRef                      = useRef(false); // tracks loading inside event listeners

  // Derived state — after all hooks
  const currentProject = PROJECTS_LIST.find(p => p.id === project) || PROJECTS_LIST[0];
  const isScopingMode  = currentProject?.scoping ?? false;

  // Load valid projects from backend once on mount
  useEffect(() => {
    apiFetch(token, '/cockpit/projects')
      .then(data => setValidProjects(new Set(Object.keys(data))))
      .catch(() => setValidProjects(new Set())); // fail open — don't block UI
  }, [token]);

  // Load history + experts when project changes
  useEffect(() => {
    setMessages([]);
    setTasksAdded([]);
    setProjectSetup(null);
    setProjectError(null);
    try { setExperts(JSON.parse(localStorage.getItem(`taskgen-experts-${project}`) || '[]')); } catch { setExperts([]); }
    apiFetch(token, `/cockpit/task-gen/${project}`)
      .then(d => setMessages(d.messages || []))
      .catch(e => {
        if (e.message.includes('404')) {
          setProjectError(`Projekt "${project}" ist im Backend nicht konfiguriert. Nachrichten werden nicht gespeichert!`);
        }
      });
  }, [project, token]);

  // Experts werden nur im localStorage dieses Browsers gespeichert (kein Backend-Sync)
  useEffect(() => {
    localStorage.setItem(`taskgen-experts-${project}`, JSON.stringify(experts));
  }, [experts, project]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Keep loadingRef in sync so the visibilitychange handler can read it without stale closure
  useEffect(() => { loadingRef.current = loading; }, [loading]);

  // iOS PWA fix: when app returns from background (>8s hidden), reload history.
  // Only intervenes after a real background event — ignores keyboard/notification flickers.
  useEffect(() => {
    let hiddenAt = null;
    let pollTimer = null;
    const stopPoll = () => { if (pollTimer) { clearInterval(pollTimer); pollTimer = null; } };

    const reload = async () => {
      try {
        const d = await apiFetch(token, `/cockpit/task-gen/${project}`);
        return d.messages || [];
      } catch { return null; }
    };

    const onVisibilityChange = async () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      if (document.visibilityState !== 'visible') return;

      const hiddenMs = hiddenAt ? Date.now() - hiddenAt : 0;
      hiddenAt = null;

      // Ignore brief focus losses (keyboard open/close, notifications) — < 8 seconds
      if (hiddenMs < 8000) return;

      // Real background: abort stale fetch and reload from backend
      abortControllerRef.current?.abort();
      stopPoll();

      const msgs = await reload();
      if (msgs === null) { setLoading(false); return; }
      setMessages(msgs);

      // If last msg is from user, Claude is still processing — poll every 5s
      if (msgs.length > 0 && msgs[msgs.length - 1]?.role === 'user') {
        setLoading(true);
        let attempts = 0;
        pollTimer = setInterval(async () => {
          attempts++;
          if (attempts > 60) { setLoading(false); stopPoll(); return; } // 5min max
          const fresh = await reload();
          if (!fresh) return;
          if (fresh.length > msgs.length || fresh[fresh.length - 1]?.role === 'assistant') {
            setMessages(fresh);
            setLoading(false);
            stopPoll();
          }
        }, 5000);
      } else {
        setLoading(false);
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => { document.removeEventListener('visibilitychange', onVisibilityChange); stopPoll(); };
  }, [project, token]);

  if (!currentProject) return (
    <div style={{ padding: 24, color: 'var(--text3)', fontSize: 13 }}>Lade Projekte…</div>
  );

  // Web Speech API Fallback (browser-nativ, kein API-Key nötig)
  const startBrowserSpeech = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert('Spracherkennung wird in diesem Browser nicht unterstützt.'); return; }
    const recognition = new SR();
    recognition.lang = 'de-DE';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    setRecording(true);
    recognition.onresult = (e) => {
      const text = e.results[0][0].transcript;
      setInput(prev => prev ? prev + ' ' + text : text);
    };
    recognition.onerror = () => { setRecording(false); };
    recognition.onend  = () => { setRecording(false); };
    recognition.start();
  };

  const toggleRecording = () => {
    if (recording) {
      mediaRecorderRef.current?.stop();
      return;
    }
    startBrowserSpeech();
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const newAtts = await Promise.all(files.map(file => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const dataUrl = ev.target.result; // "data:image/png;base64,..."
        const base64 = dataUrl.split(',')[1];
        resolve({
          name: file.name,
          type: file.type || 'application/octet-stream',
          data: base64,
          preview: file.type.startsWith('image/') ? dataUrl : null,
        });
      };
      reader.readAsDataURL(file);
    })));
    setAttachments(prev => [...prev, ...newAtts]);
    e.target.value = '';
  };

  const removeAttachment = (idx) => setAttachments(prev => prev.filter((_, i) => i !== idx));

  const send = async (text) => {
    const msg = (text || input).trim();
    if ((!msg && attachments.length === 0) || loading) return;
    if (projectError) {
      alert(`Dieses Projekt ist im Backend nicht konfiguriert.\nNachrichten können nicht gespeichert werden.\n\nBitte zuerst das Projekt in cockpit.py eintragen und Backend neu starten.`);
      return;
    }
    const currentAttachments = [...attachments];
    setInput('');
    setAttachments([]);
    setLoading(true);

    const attSummary = currentAttachments.length
      ? `\n📎 ${currentAttachments.map(a => a.name).join(', ')}`
      : '';
    const userMsg = {
      role: 'user',
      content: msg + attSummary,
      timestamp: new Date().toISOString(),
      attachmentPreviews: currentAttachments.filter(a => a.preview).map(a => ({ name: a.name, preview: a.preview })),
    };
    setMessages(prev => [...prev, userMsg]);

    abortControllerRef.current = new AbortController();
    try {
      const data = await apiFetch(token, `/cockpit/task-gen/${project}`, {
        method: 'POST',
        signal: abortControllerRef.current.signal,
        body: JSON.stringify({
          message: msg,
          attachments: currentAttachments.map(({ name, type, data }) => ({ name, type, data })),
          experts: experts.length > 0 ? experts.map(({ role, description }) => ({ role, description })) : undefined,
        }),
      });
      setMessages(prev => [...prev, {
        role: 'assistant', content: data.response,
        timestamp: new Date().toISOString(),
        tasks_added: data.tasks_added || [],
      }]);
      if (data.tasks_added?.length > 0) {
        setTasksAdded(prev => [...prev, ...data.tasks_added]);
      }
      if (data.project_setup) {
        setProjectSetup(data.project_setup);
      }
    } catch (e) {
      // AbortError = visibilitychange handler took over, it will reload history
      if (e.name === 'AbortError') return;
      setMessages(prev => [...prev, { role: 'error', content: e.message }]);
    } finally {
      setLoading(false);
    }
  };

  const addExpert = () => {
    if (!newExpertRole.trim()) return;
    setExperts(prev => [...prev, { id: Date.now(), role: newExpertRole.trim(), description: newExpertDesc.trim() }]);
    setNewExpertRole('');
    setNewExpertDesc('');
    setShowExpertForm(false);
  };
  const removeExpert = (id) => setExperts(prev => prev.filter(e => e.id !== id));

  const generateTasks = () => isScopingMode
    ? send('Projekt aufsetzen — erstelle jetzt den vollständigen [PROJEKT-SETUP] Block, die [CLAUDE-MD], die [MANUAL-STEPS] und alle initialen [TASK] Einträge.')
    : send('Tasks generieren — erstelle jetzt alle besprochenen Tasks im BACKLOG-Format.');

  const clearHistory = async () => {
    if (!window.confirm('Gesprächs-History löschen?')) return;
    await apiFetch(token, `/cockpit/task-gen/${project}`, { method: 'DELETE' });
    setMessages([]);
    setTasksAdded([]);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 12, minHeight: 0 }}>

      {/* Header — always visible, never scrolls away */}
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>🧠 Task Generator</h2>
          <p style={{ margin: 0, fontSize: 11, color: 'var(--text3)' }}>Idee besprechen → Tasks generieren → Orchestrator</p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={project}
            onChange={e => setProject(e.target.value)}
            style={{ background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: 6, padding: '5px 10px', fontSize: 12 }}
          >
            <optgroup label="── Aktive Projekte">
              {PROJECTS_LIST.filter(p => !p.scoping).map(p => (
                <option key={p.id} value={p.id}>{p.icon} {p.label}</option>
              ))}
            </optgroup>
            <optgroup label="── In Planung (Scoping)">
              {PROJECTS_LIST.filter(p => p.scoping).map(p => (
                <option key={p.id} value={p.id}>{p.icon} {p.label}</option>
              ))}
            </optgroup>
          </select>
          {isScopingMode && (
            <span style={{ fontSize: 11, background: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 5, padding: '3px 8px', fontWeight: 600 }}>
              📋 Scoping
            </span>
          )}
          <button onClick={generateTasks} disabled={loading || messages.length < 2} style={{
            background: messages.length >= 2 ? (isScopingMode ? '#f59e0b' : 'var(--green)') : 'var(--bg3)',
            color: messages.length >= 2 ? '#000' : 'var(--text3)',
            border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: messages.length >= 2 ? 'pointer' : 'not-allowed',
          }}>
            {isScopingMode ? '🚀 Projekt aufsetzen' : '✅ Tasks generieren'}
          </button>
          <button onClick={clearHistory} style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text3)', borderRadius: 6, padding: '5px 10px', fontSize: 11, cursor: 'pointer' }}>
            🗑 Löschen
          </button>
        </div>
      </div>

      {/* Project not configured warning */}
      {projectError && (
        <div style={{
          flexShrink: 0, background: 'rgba(239,68,68,0.12)', border: '1px solid #ef4444',
          borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#ef4444' }}>Projekt nicht im Backend konfiguriert</div>
            <div style={{ fontSize: 11.5, color: 'var(--text2)', marginTop: 2 }}>
              {projectError} — Bitte einen Admin informieren, damit das Projekt in der Backend-Konfiguration eingetragen wird.
            </div>
          </div>
        </div>
      )}

      {/* Experten Panel */}
      <div style={{ flexShrink: 0, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
            🧑‍💼 Experten
          </span>
          <span title="Experten werden nur im lokalen Browser gespeichert — nicht serverübergreifend" style={{ fontSize: 10, color: 'var(--text3)', background: 'var(--bg3)', borderRadius: 4, padding: '1px 5px', cursor: 'help' }}>
            💾 lokal
          </span>
          {experts.map(e => (
            <div key={e.id} title={e.description || e.role} style={{
              display: 'flex', alignItems: 'center', gap: 5,
              background: 'var(--bg3)', border: '1px solid var(--accent)', borderRadius: 20,
              padding: '3px 10px 3px 8px', fontSize: 11.5, color: 'var(--text)',
            }}>
              <span style={{ fontWeight: 600 }}>{e.role}</span>
              <button onClick={() => removeExpert(e.id)} style={{
                background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer',
                padding: 0, lineHeight: 1, fontSize: 13, marginLeft: 2,
              }}>×</button>
            </div>
          ))}
          {!showExpertForm && (
            <button onClick={() => setShowExpertForm(true)} style={{
              background: 'none', border: '1px dashed var(--border)', borderRadius: 20,
              color: 'var(--text3)', padding: '3px 10px', fontSize: 11, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              + Experte hinzufügen
            </button>
          )}
        </div>
        {showExpertForm && (
          <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <input
              value={newExpertRole}
              onChange={e => setNewExpertRole(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addExpert(); if (e.key === 'Escape') setShowExpertForm(false); }}
              placeholder="Rolle (z.B. Security Expert)"
              autoFocus
              style={{
                background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6,
                padding: '5px 10px', fontSize: 12, color: 'var(--text)', width: 180,
              }}
            />
            <textarea
              value={newExpertDesc}
              onChange={e => setNewExpertDesc(e.target.value)}
              placeholder="Rollenbeschreibung (optional)"
              rows={2}
              style={{
                background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6,
                padding: '5px 10px', fontSize: 12, color: 'var(--text)', flex: 1, minWidth: 180, resize: 'vertical',
              }}
            />
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={addExpert} style={{
                background: 'var(--accent)', border: 'none', borderRadius: 6,
                color: '#fff', padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
              }}>✓</button>
              <button onClick={() => { setShowExpertForm(false); setNewExpertRole(''); setNewExpertDesc(''); }} style={{
                background: 'none', border: '1px solid var(--border)', borderRadius: 6,
                color: 'var(--text3)', padding: '5px 10px', fontSize: 12, cursor: 'pointer',
              }}>✕</button>
            </div>
          </div>
        )}
      </div>

      {/* Tasks-added Banner */}
      {tasksAdded.length > 0 && (
        <div style={{ flexShrink: 0, background: 'rgba(91,240,91,0.1)', border: '1px solid var(--green)', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
          <strong style={{ color: 'var(--green)' }}>✅ {tasksAdded.length} Task{tasksAdded.length > 1 ? 's' : ''} in BACKLOG geschrieben:</strong>
          <ul style={{ margin: '4px 0 0 0', paddingLeft: 18 }}>
            {tasksAdded.map((t, i) => <li key={i} style={{ color: 'var(--text2)' }}>{t.title} ({t.priority})</li>)}
          </ul>
        </div>
      )}

      {/* Ideen-Nuggets Panel — only in scoping mode */}
      {isScopingMode && <NuggetsPanel token={token} projectId={project} />}

      {/* Project Setup Result — shown after "Projekt aufsetzen" */}
      {projectSetup && <SetupResultPanel setup={projectSetup} onClose={() => setProjectSetup(null)} />}

      {/* Chat area */}
      <div style={{
        flex: 1, overflowY: 'auto', background: 'var(--bg2)', borderRadius: 10,
        border: '1px solid var(--border)', padding: '14px 14px 28px', display: 'flex', flexDirection: 'column', gap: 10,
        minHeight: 200,
      }}>
        {messages.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '12px 4px' }}>
            {/* Step 1 — Kontext laden */}
            <div style={{
              background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10,
              padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', background: 'var(--accent)',
                color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 700, flexShrink: 0,
              }}>1</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Kontext laden & Ausgangslage klären</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                  Claude liest CLAUDE.md, BACKLOG.md und Logs — und führt den ersten Schritt aus.
                </div>
              </div>
              <button
                onClick={() => send('Bitte lese den aktuellen Projektkontext (CLAUDE.md, BACKLOG.md, Logs) und fasse die Ausgangslage zusammen: Was ist bereits implementiert? Was fehlt noch? Führe dann den ersten empfohlenen Schritt aus und schlage konkrete nächste Maßnahmen vor.')}
                disabled={loading}
                style={{
                  background: 'var(--accent)', border: 'none', borderRadius: 6, color: '#fff',
                  padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                ▶ Ausführen
              </button>
            </div>

            {/* Step 2 — Idee beschreiben */}
            <div style={{
              background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10,
              padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, opacity: 0.6,
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', background: 'var(--bg2)',
                border: '1px solid var(--border)', color: 'var(--text3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 700, flexShrink: 0,
              }}>2</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Feature / Idee beschreiben</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                  {isScopingMode
                    ? 'Beschreibe Vision, Zielgruppe und MVP — wir entwickeln gemeinsam die Architektur.'
                    : 'Beschreibe was gebaut werden soll — per Text oder Sprache.'}
                </div>
              </div>
            </div>

            {/* Step 3 — Tasks generieren */}
            <div style={{
              background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10,
              padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, opacity: 0.4,
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', background: 'var(--bg2)',
                border: '1px solid var(--border)', color: 'var(--text3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 700, flexShrink: 0,
              }}>3</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                  {isScopingMode ? '🚀 Projekt aufsetzen' : '✅ Tasks generieren'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                  {isScopingMode
                    ? 'Vollständigen Projekt-Setup mit CLAUDE.md und initialen Tasks erstellen.'
                    : 'Alle besprochenen Features in BACKLOG-Tasks umwandeln.'}
                </div>
              </div>
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
            {/* Image previews for user messages */}
            {m.attachmentPreviews?.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4, maxWidth: '85%', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                {m.attachmentPreviews.map((ap, j) => (
                  <img key={j} src={ap.preview} alt={ap.name} title={ap.name}
                    style={{ maxWidth: 160, maxHeight: 120, borderRadius: 6, border: '1px solid var(--border)', objectFit: 'cover' }}
                  />
                ))}
              </div>
            )}
            <div style={{
              maxWidth: '85%', padding: '8px 12px', borderRadius: m.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
              background: m.role === 'user' ? 'var(--accent)' : m.role === 'error' ? 'rgba(240,91,91,0.15)' : 'var(--bg3)',
              border: m.role === 'error' ? '1px solid var(--red)' : '1px solid var(--border)',
              color: m.role === 'user' ? '#fff' : 'var(--text)',
              fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap',
            }}>
              {m.content}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 3 }}>
              <span style={{ fontSize: 10, color: 'var(--text3)' }}>{fmtTime(m.timestamp)}</span>
              {m.tasks_added?.length > 0 && (
                <span style={{ fontSize: 10, color: 'var(--green)', fontWeight: 600 }}>
                  +{m.tasks_added.length} Task{m.tasks_added.length > 1 ? 's' : ''} gespeichert
                </span>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: '12px 12px 12px 2px', padding: '8px 14px', fontSize: 13, color: 'var(--text3)' }}>
              <span className="typing-dots">···</span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Attachment previews */}
      {attachments.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {attachments.map((a, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 5,
              background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6,
              padding: '3px 8px 3px 4px', fontSize: 11, maxWidth: 180,
            }}>
              {a.preview
                ? <img src={a.preview} alt={a.name} style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 3 }} />
                : <span style={{ fontSize: 16 }}>📄</span>
              }
              <span style={{ color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 100 }}>{a.name}</span>
              <button onClick={() => removeAttachment(i)} style={{
                background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer',
                padding: 0, lineHeight: 1, fontSize: 13, flexShrink: 0,
              }}>×</button>
            </div>
          ))}
        </div>
      )}

      {/* Input area — paddingBottom respects iPhone home indicator */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,application/pdf,.txt,.md,.csv,.json,.log"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        <button
          onClick={toggleRecording}
          disabled={transcribing}
          title={recording ? 'Aufnahme stoppen' : 'Audio aufnehmen (Whisper)'}
          style={{
            width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
            background: recording ? 'var(--red)' : transcribing ? 'var(--bg3)' : 'var(--bg3)',
            border: `2px solid ${recording ? 'var(--red)' : 'var(--border)'}`,
            color: recording ? '#fff' : 'var(--text2)',
            fontSize: 16, cursor: transcribing ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: recording ? 'pulse 1s infinite' : 'none',
          }}
        >
          {transcribing ? '⏳' : '🎙️'}
        </button>
        <button
          onClick={() => fileInputRef.current?.click()}
          title="Datei / Screenshot anhängen"
          style={{
            width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
            background: attachments.length > 0 ? 'rgba(91,106,240,0.25)' : 'var(--bg3)',
            border: `2px solid ${attachments.length > 0 ? 'var(--accent)' : 'var(--border)'}`,
            color: attachments.length > 0 ? 'var(--accent)' : 'var(--text2)',
            fontSize: 18, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          📎
        </button>
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={
            recording ? '🔴 Aufnahme läuft — Mikrofon erneut klicken zum Stoppen…'
            : transcribing ? '⏳ Transkribiere via Whisper…'
            : attachments.length > 0 ? `${attachments.length} Datei(en) bereit — Kommentar eingeben oder direkt senden…`
            : 'Idee eingeben, Mikrofon oder 📎 Datei anhängen… (Enter = senden)'
          }
          rows={2}
          disabled={transcribing}
          style={{
            flex: 1, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8,
            color: 'var(--text)', padding: '8px 12px', fontSize: 13, resize: 'none',
            fontFamily: 'var(--font)', lineHeight: 1.5,
            outline: recording ? '2px solid var(--red)' : 'none',
          }}
        />
        <button
          onClick={() => send()}
          disabled={((!input.trim() && attachments.length === 0)) || loading}
          style={{
            height: 40, padding: '0 16px', borderRadius: 8,
            background: (input.trim() || attachments.length > 0) && !loading ? 'var(--accent)' : 'var(--bg3)',
            border: 'none', color: (input.trim() || attachments.length > 0) && !loading ? '#fff' : 'var(--text3)',
            fontSize: 18, cursor: (input.trim() || attachments.length > 0) && !loading ? 'pointer' : 'not-allowed',
          }}
        >
          ➤
        </button>
      </div>
    </div>
  );
}


// ── Ideen-Log Tab ──────────────────────────────────────────────────────────────
function IdeenlogTab({ token }) {
  const [ideen, setIdeen]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [selected, setSelected]     = useState(null);   // {filename, content}
  const [view, setView]             = useState('list'); // 'list' | 'detail' | 'new'
  const [form, setForm]             = useState({ title: '', content: '', tags: '' });
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState(null);

  const loadList = useCallback(() => {
    setLoading(true);
    apiFetch(token, '/cockpit/ideenlog')
      .then(d => { setIdeen(d.ideen || []); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [token]);

  useEffect(() => { loadList(); }, [loadList]);

  const openIdee = async (filename) => {
    try {
      const d = await apiFetch(token, `/cockpit/ideenlog/${filename}`);
      setSelected(d);
      setView('detail');
    } catch (e) { setError(e.message); }
  };

  const createIdee = async () => {
    if (!form.title.trim() || !form.content.trim()) return;
    setSaving(true);
    try {
      const tags = form.tags.split(',').map(t => t.trim()).filter(Boolean);
      await apiFetch(token, '/cockpit/ideenlog', {
        method: 'POST',
        body: JSON.stringify({ title: form.title.trim(), content: form.content.trim(), tags }),
      });
      setForm({ title: '', content: '', tags: '' });
      setView('list');
      loadList();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)' }}>Lade Ideen…</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 12 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>💡 Ideen-Log</h2>
          <p style={{ margin: 0, fontSize: 11, color: 'var(--text3)' }}>Rohe Ideen und Konzepte festhalten — ohne Bewertung, ohne Druck</p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {view !== 'list' && (
            <button onClick={() => { setView('list'); setSelected(null); }} style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text2)', borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer' }}>
              ← Zurück
            </button>
          )}
          {view === 'list' && (
            <button onClick={() => { setForm({ title: '', content: '', tags: '' }); setView('new'); }} style={{ background: 'var(--accent)', border: 'none', color: '#fff', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              + Neue Idee
            </button>
          )}
        </div>
      </div>

      {error && <div style={{ color: 'var(--red)', fontSize: 12 }}>Fehler: {error}</div>}

      {/* List view */}
      {view === 'list' && (
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {ideen.length === 0 && (
            <div style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 13, marginTop: 60 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>💡</div>
              <div style={{ fontWeight: 600 }}>Noch keine Ideen erfasst</div>
              <div style={{ fontSize: 11, marginTop: 6 }}>Kein Filter, kein Urteil — einfach aufschreiben was im Kopf ist.</div>
            </div>
          )}
          {ideen.map(idee => (
            <button key={idee.filename} onClick={() => openIdee(idee.filename)} style={{
              background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px',
              cursor: 'pointer', textAlign: 'left', color: 'var(--text)', transition: 'border-color 0.15s',
            }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontWeight: 700, fontSize: 13, flex: 1, color: 'var(--text)' }}>{idee.title}</span>
                {idee.datum && <span style={{ fontSize: 10, color: 'var(--text3)', flexShrink: 0 }}>{idee.datum}</span>}
              </div>
              {idee.preview && (
                <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.4,
                  overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
                  WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                  {idee.preview}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Detail view */}
      {view === 'detail' && selected && (
        <div style={{ flex: 1, overflowY: 'auto', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
          <pre style={{ margin: 0, fontSize: 13, color: 'var(--text)', lineHeight: 1.6, whiteSpace: 'pre-wrap', fontFamily: 'var(--font)' }}>
            {selected.content}
          </pre>
        </div>
      )}

      {/* New idea form */}
      {view === 'new' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="Titel der Idee…"
            style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 14, fontWeight: 700, color: 'var(--text)' }}
          />
          <textarea
            value={form.content}
            onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
            placeholder="Beschreibe die Idee frei — Kontext, Motivation, erste Gedanken…"
            rows={10}
            style={{ flex: 1, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: 'var(--text)', resize: 'none', lineHeight: 1.5, fontFamily: 'var(--font)' }}
          />
          <input
            value={form.tags}
            onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
            placeholder="Tags (kommagetrennt): ai, saas, mvp, …"
            style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 12px', fontSize: 12, color: 'var(--text)' }}
          />
          <button onClick={createIdee} disabled={saving || !form.title.trim() || !form.content.trim()} style={{
            background: form.title.trim() && form.content.trim() ? 'var(--accent)' : 'var(--bg3)',
            border: 'none', color: form.title.trim() && form.content.trim() ? '#fff' : 'var(--text3)',
            borderRadius: 8, padding: '10px', fontSize: 13, fontWeight: 700,
            cursor: form.title.trim() && form.content.trim() ? 'pointer' : 'not-allowed',
          }}>
            {saving ? '⏳ Speichern…' : '💾 Idee speichern'}
          </button>
        </div>
      )}
    </div>
  );
}


// ── Status Tab ─────────────────────────────────────────────────────────────────
function StatusTab({ token }) {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);

  useEffect(() => {
    setLoading(true);
    apiFetch(token, '/cockpit/backlogs')
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [token]);

  if (loading) return <div className="card" style={{ color: 'var(--text3)', padding: 32, textAlign: 'center' }}>Lade Status…</div>;
  if (error)   return <div className="card" style={{ color: 'var(--red)', padding: 32 }}>Fehler: {error}</div>;
  if (!data)   return null;

  const projects = Object.entries(data);
  const totalAll  = projects.reduce((s, [, p]) => s + (p.counts?.total || 0), 0);
  const totalDone = projects.reduce((s, [, p]) => s + (p.counts?.done  || 0), 0);
  const totalTodo = projects.reduce((s, [, p]) => s + (p.counts?.todo  || 0), 0);
  const totalPct  = totalAll > 0 ? Math.round(totalDone / totalAll * 100) : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Gesamtfortschritt ── */}
      <div className="card">
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: 'var(--text)' }}>
          Gesamtfortschritt — alle Projekte
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <div style={{ flex: 1, height: 10, background: 'var(--surface2)', borderRadius: 5, overflow: 'hidden' }}>
            <div style={{ width: `${totalPct}%`, height: '100%', background: 'var(--green)', borderRadius: 5, transition: 'width .4s' }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--green)', minWidth: 44 }}>{totalPct}%</span>
        </div>
        <div style={{ display: 'flex', gap: 20, fontSize: 12, color: 'var(--text3)' }}>
          <span>✅ Done: <b style={{ color: 'var(--green)' }}>{totalDone}</b></span>
          <span>📋 TODO: <b style={{ color: 'var(--amber)' }}>{totalTodo}</b></span>
          <span>📦 Gesamt: <b style={{ color: 'var(--text)' }}>{totalAll}</b></span>
        </div>
      </div>

      {/* ── Pro-Projekt Cards ── */}
      {projects.map(([id, proj]) => {
        const c    = proj.counts || {};
        const pct  = c.total > 0 ? Math.round(c.done / c.total * 100) : 0;
        const p1s  = (proj.by_status?.TODO  || []).filter(t => t.priority === 'P1').slice(0, 4);
        const p2s  = (proj.by_status?.TODO  || []).filter(t => t.priority === 'P2').slice(0, 3);
        const wips = (proj.by_status?.IN_PROGRESS || []).slice(0, 3);

        return (
          <div key={id} className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 18 }}>{proj.icon}</span>
              <span style={{ fontWeight: 700, fontSize: 13.5 }}>{proj.label}</span>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text3)' }}>
                {c.done}/{c.total} done · {c.todo} offen {c.in_progress > 0 ? `· ${c.in_progress} laufend` : ''}
              </span>
            </div>

            {/* Fortschrittsbalken */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ flex: 1, height: 8, background: 'var(--surface2)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? 'var(--green)' : 'var(--accent)', borderRadius: 4, transition: 'width .4s' }} />
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, color: pct === 100 ? 'var(--green)' : 'var(--accent)', minWidth: 36 }}>{pct}%</span>
            </div>

            {/* In Progress */}
            {wips.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--amber)', marginBottom: 4 }}>⚡ IN PROGRESS</div>
                {wips.map(t => (
                  <div key={t.id} onClick={() => setSelectedTask({ ...t, _project: id })} style={{ fontSize: 11.5, color: 'var(--text2)', padding: '2px 4px', cursor: 'pointer', borderRadius: 4 }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    › {t.title}
                  </div>
                ))}
              </div>
            )}

            {/* P1 Tasks */}
            {p1s.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--red)', marginBottom: 4 }}>🔴 Nächste P1</div>
                {p1s.map(t => (
                  <div key={t.id} onClick={() => setSelectedTask({ ...t, _project: id })} style={{ fontSize: 11.5, color: 'var(--text2)', padding: '2px 4px', cursor: 'pointer', borderRadius: 4 }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    › {t.title}
                  </div>
                ))}
              </div>
            )}

            {/* P2 Tasks */}
            {p2s.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--amber)', marginBottom: 4 }}>🟡 Nächste P2</div>
                {p2s.map(t => (
                  <div key={t.id} onClick={() => setSelectedTask({ ...t, _project: id })} style={{ fontSize: 11.5, color: 'var(--text3)', padding: '2px 4px', cursor: 'pointer', borderRadius: 4 }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    › {t.title}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {selectedTask && (
        <TaskDetailModal task={selectedTask} token={token} onClose={() => setSelectedTask(null)}
          onRefresh={() => apiFetch(token, '/cockpit/backlogs').then(d => setData(d)).catch(() => {})} />
      )}
    </div>
  );
}


// ── Orchestrator Admin-Tab ─────────────────────────────────────────────────────
function OrchestratorAdminTab({ token }) {
  const { projects } = useContext(ProjectsCtx);
  const [cfg, setCfg]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState(null);
  const [error, setError]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await apiFetch(token, '/cockpit/orchestrator/config');
      setCfg(d);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  async function save() {
    setSaving(true); setMsg(null);
    try {
      await apiFetch(token, '/cockpit/orchestrator/config', { method: 'POST', body: JSON.stringify(cfg) });
      setMsg({ type: 'ok', text: 'Gespeichert — wirkt beim nächsten Orchestrator-Lauf.' });
    } catch(e) { setMsg({ type: 'err', text: e.message }); }
    finally { setSaving(false); }
  }

  function setField(key, val) { setCfg(c => ({ ...c, [key]: val })); }
  function setKeywords(key, val) {
    setField(key, val.split('\n').map(s => s.trim()).filter(Boolean));
  }
  function setProjectEnabled(pid, val) {
    setCfg(c => ({ ...c, projects_enabled: { ...c.projects_enabled, [pid]: val } }));
  }

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)' }}><Spin size={20} /></div>;
  if (error)   return <div style={{ padding: 24, color: '#dc2626' }}>{error}</div>;
  if (!cfg)    return null;

  const labelStyle = { fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 4 };
  const inputStyle = { width: '100%', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '7px 10px', fontSize: 13, fontFamily: 'var(--mono)', boxSizing: 'border-box' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)' }}>Orchestrator Konfiguration</div>
          <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 2 }}>Änderungen wirken beim nächsten Cron-Lauf (alle 2h)</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
          {saving ? <Spin size={12} /> : '💾'} Speichern
        </button>
      </div>

      {msg && (
        <div style={{ padding: '10px 14px', borderRadius: 7, background: msg.type === 'ok' ? '#d1fae5' : '#fee2e2', color: msg.type === 'ok' ? '#065f46' : '#991b1b', fontSize: 12.5 }}>
          {msg.text}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Nachtmodus */}
        <div className="card">
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 14 }}>Nachtmodus</div>
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Start (Uhr)</div>
              <input type="number" min="0" max="23" style={inputStyle} value={cfg.night_start ?? 7}
                onChange={e => setField('night_start', parseInt(e.target.value))} />
              <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 4 }}>MEDIUM/HIGH Tasks nicht vor dieser Uhrzeit</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Ende (Uhr)</div>
              <input type="number" min="0" max="23" style={inputStyle} value={cfg.night_end ?? 22}
                onChange={e => setField('night_end', parseInt(e.target.value))} />
              <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 4 }}>MEDIUM/HIGH Tasks nicht nach dieser Uhrzeit</div>
            </div>
          </div>
        </div>

        {/* Timeouts */}
        <div className="card">
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 14 }}>Timeouts</div>
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Claude Timeout (Sek)</div>
              <input type="number" min="300" max="7200" style={inputStyle} value={cfg.claude_timeout ?? 1800}
                onChange={e => setField('claude_timeout', parseInt(e.target.value))} />
              <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 4 }}>Max. Laufzeit pro Task (aktuell {Math.round((cfg.claude_timeout ?? 1800)/60)} Min)</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Approval Timeout (Sek)</div>
              <input type="number" min="300" max="86400" style={inputStyle} value={cfg.approval_timeout ?? 7200}
                onChange={e => setField('approval_timeout', parseInt(e.target.value))} />
              <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 4 }}>Wartezeit auf Freigabe ({Math.round((cfg.approval_timeout ?? 7200)/3600)} Std)</div>
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <div style={labelStyle}>Poll-Intervall Freigabe (Sek)</div>
            <input type="number" min="10" max="300" style={{ ...inputStyle, width: '50%' }} value={cfg.approval_poll_interval ?? 30}
              onChange={e => setField('approval_poll_interval', parseInt(e.target.value))} />
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* HIGH Risk Keywords */}
        <div className="card">
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
            <span style={{ background: '#fee2e2', color: '#991b1b', padding: '2px 6px', borderRadius: 4 }}>HIGH</span>
            {' '}Risk Keywords
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>Tasks mit diesen Begriffen benötigen immer eine Freigabe (einer pro Zeile)</div>
          <textarea rows={8} style={{ ...inputStyle, resize: 'vertical' }}
            value={(cfg.risk_high_keywords || []).join('\n')}
            onChange={e => setKeywords('risk_high_keywords', e.target.value)} />
        </div>

        {/* MEDIUM Risk Keywords */}
        <div className="card">
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
            <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: 4 }}>MEDIUM</span>
            {' '}Risk Keywords
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>Tasks mit diesen Begriffen benötigen tagsüber eine Freigabe (einer pro Zeile)</div>
          <textarea rows={8} style={{ ...inputStyle, resize: 'vertical' }}
            value={(cfg.risk_medium_keywords || []).join('\n')}
            onChange={e => setKeywords('risk_medium_keywords', e.target.value)} />
        </div>
      </div>

      {/* Projects Enable/Disable */}
      <div className="card">
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 12 }}>Projekte — Orchestrator aktiv/inaktiv</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {Object.entries(cfg.projects_enabled || {}).map(([pid, enabled]) => (
            <label key={pid} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 14px', borderRadius: 7,
              background: enabled ? '#d1fae5' : 'var(--bg3)',
              border: `1px solid ${enabled ? '#059669' : 'var(--border)'}`,
              cursor: 'pointer', fontSize: 12.5, fontWeight: 600,
              color: enabled ? '#065f46' : 'var(--text3)',
            }}>
              <input type="checkbox" checked={!!enabled} onChange={e => setProjectEnabled(pid, e.target.checked)}
                style={{ width: 14, height: 14, accentColor: '#059669' }} />
              {pid}
            </label>
          ))}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8 }}>
          Hinweis: Deaktivierung hier verhindert noch nicht den Cron — dafür muss die crontab angepasst werden.
        </div>
      </div>

      {/* Cron Info */}
      <div className="card" style={{ background: 'var(--bg3)' }}>
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Cron-Schedule (read-only)</div>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text2)', lineHeight: 1.8 }}>
          <div>0 */2 * * * → alle 2 Stunden (prism-built, nfon-audit, p4a-audit, sales-coach)</div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
          Cron-Änderungen müssen direkt per SSH in /etc/cron.d/ gemacht werden.
        </div>
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────
   PRISM — Unified Compliance Intelligence
   P4A (Audit) + P4I (ISO Implementation) joint dashboard
───────────────────────────────────────────────────────────────── */

// P4A finding → ISO control mapping
const FINDING_CTRL_MAP = {
  'ITGC-ACC-01': { std: 'iso27001', id: 'A.8.2'  },
  'ITGC-CHG-03': { std: 'iso27001', id: 'A.8.32' },
  'ITGC-BCK-01': { std: 'iso27001', id: 'A.8.13' },
};

// Three demo controls for the interactive showcase inside "Einzelne Managementsysteme"
const PRISM_DEMO_IDS = ['A.5.1', 'A.8.2', 'A.6.3'];

const IMPL_META = {
  implementiert:  { bg: '#064e3b', text: '#34d399', label: 'Implementiert',  dot: '#34d399' },
  in_bearbeitung: { bg: '#3b2300', text: '#fb923c', label: 'In Bearbeitung', dot: '#fb923c' },
  ausstehend:     { bg: '#1c2333', text: '#6b7280', label: 'Ausstehend',     dot: '#4b5563' },
};
const AUDIT_META = {
  compliant:   { bg: '#064e3b', text: '#34d399', label: 'Konform',        dot: '#34d399' },
  finding:     { bg: '#450a0a', text: '#f87171', label: 'Feststellung',   dot: '#f87171' },
  not_audited: { bg: '#1c2333', text: '#6b7280', label: 'Nicht geprüft', dot: '#4b5563' },
};
const STANDARD_META = {
  iso27001: { label: 'ISO 27001:2022', color: '#4f46e5', short: '27001' },
  iso9001:  { label: 'ISO 9001:2015',  color: '#059669', short: '9001'  },
};

// Seed-based fallback impl status (deterministic per control)
function seedImpl(std, id) {
  let h = 0; const k = std + id;
  for (let i = 0; i < k.length; i++) h = (h * 31 + k.charCodeAt(i)) & 0xffff;
  const r = h % 100;
  return r < 35 ? 'implementiert' : r < 56 ? 'in_bearbeitung' : 'ausstehend';
}

function ImplBadge({ status }) {
  const m = IMPL_META[status] || IMPL_META.ausstehend;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: m.bg, color: m.text,
      padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: m.dot, flexShrink: 0 }} />
      {m.label}
    </span>
  );
}

function AuditBadge({ status }) {
  const m = AUDIT_META[status] || AUDIT_META.not_audited;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: m.bg, color: m.text,
      padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: m.dot, flexShrink: 0 }} />
      {m.label}
    </span>
  );
}

// ── PRISM Health Score Engine ─────────────────────────────────────────────
// Erweiterbar: nimmt beliebig viele Standards entgegen, gewichtet nach Control-Anzahl

const PRISM_SEVERITY_WEIGHTS = { high: 3, medium: 1.5, low: 0.5 };

const PRISM_MATURITY = [
  { min: 90, label: 'Zertifizierbar', color: '#34d399', bg: '#064e3b', glow: '#34d39944' },
  { min: 75, label: 'Optimiert',      color: '#a3e635', bg: '#1a2e05', glow: '#a3e63544' },
  { min: 55, label: 'Gesteuert',      color: '#facc15', bg: '#2d2200', glow: '#facc1544' },
  { min: 35, label: 'Definiert',      color: '#fb923c', bg: '#3b1f00', glow: '#fb923c44' },
  { min: 15, label: 'Wiederholbar',   color: '#f87171', bg: '#3b0f0f', glow: '#f8717144' },
  { min:  0, label: 'Initial',        color: '#9ca3af', bg: '#1e2235', glow: '#9ca3af33' },
];

function getMaturity(score) {
  return PRISM_MATURITY.find(l => score >= l.min) || PRISM_MATURITY[PRISM_MATURITY.length - 1];
}

/**
 * calcPrismHealth — erweiterbar für beliebig viele Standards.
 * @param {Object} p4iData   { iso27001: { controls }, iso9001: { controls }, ... }
 * @param {Function} getImplFn  (std, id) => 'implementiert'|'in_bearbeitung'|'ausstehend'
 * @param {Function} getAuditFn (std, id) => 'compliant'|'finding'|'not_audited'
 * @param {Object}   findingMap { 'std:id': findingObj }
 */
function calcPrismHealth(p4iData, getImplFn, getAuditFn, findingMap) {
  const standardScores = {};
  let totalControls = 0, weightedSum = 0;
  const dims = { impl: 0, audit: 0, compliance: 0, risk: 0 }; // global accumulators
  let globalImpl = 0, globalAuditN = 0, globalCompliant = 0, globalControls = 0;

  for (const [std, stdData] of Object.entries(p4iData || {})) {
    const controls = stdData?.controls || [];
    if (!controls.length) continue;

    let impl = 0, inProgress = 0, audited = 0, compliant = 0, findingW = 0;
    for (const c of controls) {
      const is = getImplFn(std, c.control_id);
      const as = getAuditFn(std, c.control_id);
      if (is === 'implementiert')  impl++;
      if (is === 'in_bearbeitung') inProgress++;
      if (as === 'compliant') { audited++; compliant++; }
      if (as === 'finding')   audited++;
      const f = findingMap[`${std}:${c.control_id}`];
      if (f) findingW += PRISM_SEVERITY_WEIGHTS[f.severity] || 1;
    }

    const n = controls.length;
    const implRate      = (impl + inProgress * 0.4) / n;        // partial credit
    const auditCov      = audited / n;
    const compRate      = audited > 0 ? compliant / audited : 0;
    const maxW          = n * PRISM_SEVERITY_WEIGHTS.high * 0.1; // tolerance baseline
    const riskFreedom   = Math.max(0, 1 - findingW / Math.max(maxW, 1));

    const score = Math.round(
      implRate    * 35 +
      auditCov    * 25 +
      compRate    * 30 +
      riskFreedom * 10
    );

    standardScores[std] = {
      score: Math.min(100, score),
      dims: {
        impl:      Math.round(implRate    * 100),
        audit:     Math.round(auditCov    * 100),
        compliance: Math.round(compRate   * 100),
        risk:      Math.round(riskFreedom * 100),
      },
      counts: { total: n, impl, inProgress, audited, compliant, findings: audited - compliant },
    };

    totalControls  += n;
    weightedSum    += Math.min(100, score) * n;
    globalImpl     += impl + inProgress * 0.4;
    globalAuditN   += audited;
    globalCompliant+= compliant;
    globalControls += n;
  }

  const overall = totalControls > 0 ? Math.round(weightedSum / totalControls) : 0;
  const globalDims = {
    impl:       Math.round((globalImpl      / Math.max(1, globalControls)) * 100),
    audit:      Math.round((globalAuditN    / Math.max(1, globalControls)) * 100),
    compliance: Math.round((globalCompliant / Math.max(1, globalAuditN))   * 100) || 0,
    risk:       overall > 0 ? Math.round(
      Object.values(standardScores).reduce((s, v) => s + v.dims.risk * v.counts.total, 0) /
      Math.max(1, totalControls)
    ) : 0,
  };

  return {
    overall,
    maturity: getMaturity(overall),
    dims: globalDims,
    standardScores,
    totalControls,
  };
}

// ── SVG Gauge ────────────────────────────────────────────────────────────────
function PrismGauge({ score, color, size = 140 }) {
  const cx = size / 2, cy = size * 0.58, r = size * 0.36;
  const toRad = d => (d * Math.PI) / 180;
  const arc = (cx, cy, r, a1, a2) => {
    const s = { x: cx + r * Math.cos(toRad(a1)), y: cy + r * Math.sin(toRad(a1)) };
    const e = { x: cx + r * Math.cos(toRad(a2)), y: cy + r * Math.sin(toRad(a2)) };
    const lg = ((a2 - a1 + 360) % 360) > 180 ? 1 : 0;
    return `M ${s.x.toFixed(2)} ${s.y.toFixed(2)} A ${r} ${r} 0 ${lg} 1 ${e.x.toFixed(2)} ${e.y.toFixed(2)}`;
  };
  const START = 145, TOTAL = 250;
  const filledEnd = START + (score / 100) * TOTAL;
  const sw = size * 0.072;
  return (
    <svg width={size} height={size * 0.72} style={{ overflow: 'visible' }}>
      <path d={arc(cx, cy, r, START, START + TOTAL)} fill="none" stroke="#1e2235" strokeWidth={sw} strokeLinecap="round" />
      {score > 0 && (
        <path d={arc(cx, cy, r, START, filledEnd)} fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round"
          style={{ filter: `drop-shadow(0 0 4px ${color}88)` }} />
      )}
      <text x={cx} y={cy - r * 0.08} textAnchor="middle" fill={color} fontSize={size * 0.2} fontWeight="800" fontFamily="Inter,sans-serif">{score}</text>
      <text x={cx} y={cy + r * 0.28} textAnchor="middle" fill="#6b7280" fontSize={size * 0.085} fontFamily="Inter,sans-serif">/100</text>
    </svg>
  );
}

// ── Health Dimension Bar ─────────────────────────────────────────────────────
function DimBar({ label, value, color, weight, hint }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block', boxShadow: `0 0 4px ${color}88` }} />
          <span style={{ fontSize: 11, color: 'var(--text2)', fontWeight: 500 }}>{label}</span>
          <span style={{ fontSize: 9, color: 'var(--text3)', background: 'var(--bg)', padding: '1px 5px', borderRadius: 4 }}>×{weight}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {hint && <span style={{ fontSize: 9, color: 'var(--text3)' }}>{hint}</span>}
          <span style={{ fontSize: 13, fontWeight: 700, color, minWidth: 32, textAlign: 'right', transition: 'all 0.4s' }}>{value}%</span>
        </div>
      </div>
      <div style={{ height: 5, background: '#1e2235', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 3, background: `linear-gradient(90deg, ${color}88, ${color})`,
          width: `${value}%`, transition: 'width 0.6s ease',
          boxShadow: value > 0 ? `0 0 6px ${color}55` : 'none',
        }} />
      </div>
    </div>
  );
}

// ── PRISM Health Panel ───────────────────────────────────────────────────────
function PrismHealthPanel({ health }) {
  const { overall, maturity, dims, standardScores } = health;
  const stds = Object.entries(standardScores);

  return (
    <div style={{
      background: 'linear-gradient(135deg, #080e1a 0%, #0f172a 40%, #080e1a 100%)',
      border: `1px solid ${maturity.glow}`,
      borderRadius: 14, padding: '20px 22px', marginBottom: 18,
      boxShadow: `0 0 32px ${maturity.glow}, inset 0 1px 0 #ffffff08`,
    }}>
      {/* Title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#a5b4fc', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          ◆ PRISM Health Score
        </span>
        <span style={{ fontSize: 9, color: 'var(--text3)', background: 'var(--bg)', padding: '1px 6px', borderRadius: 4 }}>
          {health.totalControls} Controls · {stds.length} Standards
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: `${140}px 1fr`, gap: 20, alignItems: 'start' }}>
        {/* Gauge */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <PrismGauge score={overall} color={maturity.color} size={140} />
          <div style={{
            marginTop: 4, padding: '4px 14px', borderRadius: 20,
            background: maturity.bg, border: `1px solid ${maturity.color}44`,
            fontSize: 11, fontWeight: 700, color: maturity.color,
            letterSpacing: '0.05em', textAlign: 'center',
          }}>{maturity.label}</div>
        </div>

        {/* Dimensions */}
        <div>
          <DimBar label="Implementierungsgrad" value={dims.impl}       color="#10b981" weight="35%" hint="P4I" />
          <DimBar label="Prüfabdeckung"         value={dims.audit}      color="#3b82f6" weight="25%" hint="P4A" />
          <DimBar label="Konformitätsrate"       value={dims.compliance} color="#a78bfa" weight="30%" hint="P4A×P4I" />
          <DimBar label="Risikofreiheit"         value={dims.risk}       color="#f59e0b" weight="10%" hint="Findings" />

          {/* Per-standard breakdown */}
          {stds.length > 0 && (
            <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
              {stds.map(([std, ss]) => {
                const sm = STANDARD_META[std] || { label: std, color: '#6b7280' };
                const m  = getMaturity(ss.score);
                return (
                  <div key={std} style={{
                    flex: 1, minWidth: 100,
                    background: '#0f172a', border: `1px solid ${sm.color}33`,
                    borderRadius: 8, padding: '8px 12px',
                  }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: sm.color, marginBottom: 5 }}>{sm.label}</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 4 }}>
                      <span style={{ fontSize: 22, fontWeight: 800, color: m.color, transition: 'all 0.4s' }}>{ss.score}</span>
                      <span style={{ fontSize: 10, color: 'var(--text3)' }}>/100</span>
                      <span style={{ fontSize: 9, color: m.color, marginLeft: 4, background: m.bg, padding: '1px 5px', borderRadius: 4 }}>{m.label}</span>
                    </div>
                    {/* Mini-bars */}
                    {[
                      { v: ss.dims.impl,       c: '#10b981', l: 'Impl.' },
                      { v: ss.dims.audit,      c: '#3b82f6', l: 'Audit' },
                      { v: ss.dims.compliance, c: '#a78bfa', l: 'Konf.' },
                    ].map(b => (
                      <div key={b.l} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
                        <span style={{ fontSize: 8, color: 'var(--text3)', width: 28 }}>{b.l}</span>
                        <div style={{ flex: 1, height: 3, background: '#1e2235', borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{ height: '100%', background: b.c, width: `${b.v}%`, transition: 'width 0.6s ease', borderRadius: 2 }} />
                        </div>
                        <span style={{ fontSize: 8, color: b.c, width: 22, textAlign: 'right' }}>{b.v}%</span>
                      </div>
                    ))}
                    <div style={{ fontSize: 9, color: 'var(--text3)', marginTop: 4 }}>
                      {ss.counts.impl}/{ss.counts.total} impl · {ss.counts.findings} findings
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PrismTab({ token }) {
  const [viewMode, setViewMode] = useState('einzeln'); // 'einzeln' | 'unified'
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshing, setRefreshing]   = useState(false);

  // Live impl/audit state: { iso27001: { 'A.5.1': {status} }, iso9001: {...} }
  const [implSt, setImplSt]     = useState({});
  const [auditSt, setAuditSt]   = useState({});
  const [auditing, setAuditing] = useState({}); // 'std:id' → bool

  // Unified Matrix UI state
  const [uStd, setUStd]               = useState('iso27001');
  const [filterDomain, setFilterDomain] = useState('');
  const [filterImpl, setFilterImpl]     = useState('');
  const [filterAudit, setFilterAudit]   = useState('');
  const [search, setSearch]             = useState('');
  const [panel, setPanel]               = useState(null); // selected control for side panel

  // Build finding lookup
  const findingMap = React.useMemo(() => {
    const m = {};
    for (const f of (data?.p4a?.findings || [])) {
      const mapping = FINDING_CTRL_MAP[f.control_id];
      if (mapping) m[`${mapping.std}:${mapping.id}`] = f;
    }
    return m;
  }, [data]);

  const loadData = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    apiFetch(token, '/cockpit/prism/data')
      .then(d => {
        setData(d);
        const impl = {}, audit = {};
        for (const std of ['iso27001', 'iso9001']) {
          impl[std]  = d.p4i?.[std]?.impl_status   || {};
          audit[std] = d.p4i?.[std]?.audit_results  || {};
        }
        setImplSt(impl);
        setAuditSt(audit);
        setLastRefresh(new Date());
        setLoading(false);
        setRefreshing(false);
        setError(null);
      })
      .catch(e => { setError(e.message); setLoading(false); setRefreshing(false); });
  }, [token]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    const t = setInterval(() => loadData(true), 30000);
    return () => clearInterval(t);
  }, [loadData]);

  // ── Helpers ─────────────────────────────────────────────────────
  const getImpl = (std, id) => implSt[std]?.[id]?.status || seedImpl(std, id);

  const getAudit = (std, id) => {
    const real = auditSt[std]?.[id]?.audit_status;
    if (real && real !== 'error') return real;
    if (findingMap[`${std}:${id}`]) return 'finding';
    let h = 0; const k = std + id + 'a';
    for (let i = 0; i < k.length; i++) h = (h * 31 + k.charCodeAt(i)) & 0xffff;
    return (h % 100) < 22 ? 'compliant' : 'not_audited';
  };

  const calcStats = (std) => {
    const controls = data?.p4i?.[std]?.controls || [];
    const impl = { implementiert: 0, in_bearbeitung: 0, ausstehend: 0 };
    const audit = { compliant: 0, finding: 0, not_audited: 0 };
    for (const c of controls) {
      const is = getImpl(std, c.control_id);
      impl[is] = (impl[is] || 0) + 1;
      const as = getAudit(std, c.control_id);
      audit[as] = (audit[as] || 0) + 1;
    }
    const total = controls.length;
    const rate = total > 0
      ? Math.round(((impl.implementiert + audit.compliant) / (total * 2)) * 100)
      : 0;
    return { impl, audit, total, rate };
  };

  const setImpl = async (std, id, status) => {
    // Optimistic update
    setImplSt(prev => ({ ...prev, [std]: { ...(prev[std] || {}), [id]: { status } } }));
    if (status !== 'implementiert') {
      setAuditSt(prev => {
        const s = { ...(prev[std] || {}) }; delete s[id];
        return { ...prev, [std]: s };
      });
    }
    try {
      await apiFetch(token, '/cockpit/prism/impl', {
        method: 'POST',
        body: JSON.stringify({ standard: std, control_id: id, status }),
      });
    } catch { loadData(); }
  };

  const runAudit = async (std, control) => {
    const key = `${std}:${control.control_id}`;
    if (auditing[key]) return;
    setAuditing(prev => ({ ...prev, [key]: true }));
    try {
      const r = await apiFetch(token, '/cockpit/prism/audit', {
        method: 'POST',
        body: JSON.stringify({ standard: std, control_id: control.control_id, control_name: control.control_name, domain: control.domain }),
      });
      setAuditSt(prev => ({ ...prev, [std]: { ...(prev[std] || {}), [control.control_id]: r } }));
      if (panel?.control_id === control.control_id) setPanel(p => ({ ...p, _refreshed: Date.now() }));
    } catch (e) {
      setAuditSt(prev => ({ ...prev, [std]: { ...(prev[std] || {}), [control.control_id]: { audit_status: 'error', comment: e.message } } }));
    } finally {
      setAuditing(prev => ({ ...prev, [key]: false }));
    }
  };

  // ── Loading / Error ──────────────────────────────────────────────
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 320, flexDirection: 'column', gap: 16 }}>
      <Spin size={28} />
      <div style={{ color: 'var(--text3)', fontSize: 13 }}>PRISM-Daten werden geladen…</div>
    </div>
  );
  if (error) return (
    <div style={{ padding: 24, color: '#f87171', background: '#450a0a', borderRadius: 8, fontSize: 13 }}>
      Fehler: {error}
    </div>
  );

  const stats27 = calcStats('iso27001');
  const stats9  = calcStats('iso9001');
  const sessions = data?.p4a?.sessions || [];
  const findings = data?.p4a?.findings || [];

  // ── Header ──────────────────────────────────────────────────────
  return (
    <div>
      {/* Top Bar */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
        border: '1px solid #312e81', borderRadius: 12, padding: '16px 20px',
        marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, flexWrap: 'wrap',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <span style={{ fontSize: 18 }}>◆</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: '#e0e7ff', letterSpacing: '0.04em' }}>PRISM</span>
            <span style={{ fontSize: 10, fontWeight: 700, background: '#4f46e5', color: '#e0e7ff', padding: '1px 6px', borderRadius: 8, letterSpacing: '0.1em' }}>LIVE</span>
            {refreshing && <Spin size={12} />}
          </div>
          <div style={{ fontSize: 11, color: '#6b7280' }}>
            P4A: {sessions.length} Sessions · {findings.length} Findings &nbsp;|&nbsp;
            P4I: ISO 27001 {stats27.total} Ctrl · ISO 9001 {stats9.total} Ctrl
            {lastRefresh && <span style={{ marginLeft: 8 }}>· {lastRefresh.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => loadData(true)} style={{
            padding: '5px 12px', background: 'transparent', border: '1px solid #312e81',
            borderRadius: 7, color: '#818cf8', fontSize: 11, cursor: 'pointer',
          }}>↺ Refresh</button>
          <div style={{
            display: 'flex', background: '#1e1b4b', border: '1px solid #312e81',
            borderRadius: 10, padding: 3, gap: 2,
          }}>
            {[
              { id: 'einzeln', label: 'Einzelne Systeme' },
              { id: 'unified', label: '◆ Unified Matrix'  },
            ].map(m => (
              <button key={m.id} onClick={() => setViewMode(m.id)} style={{
                padding: '6px 14px', borderRadius: 7, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 600, transition: 'all 0.15s',
                background: viewMode === m.id ? '#4f46e5' : 'transparent',
                color: viewMode === m.id ? '#fff' : '#818cf8',
                boxShadow: viewMode === m.id ? '0 2px 8px #4f46e566' : 'none',
              }}>{m.label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════
          EINZELNE SYSTEME VIEW
      ════════════════════════════════════════════════ */}
      {viewMode === 'einzeln' && (
        <div>
          {/* System Cards Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>

            {/* P4I Card */}
            <div style={{
              background: 'var(--surface)', border: '1px solid #05966933',
              borderRadius: 12, overflow: 'hidden',
              boxShadow: '0 0 0 1px #05966911',
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #052e16, #064e3b)',
                borderBottom: '1px solid #05966944',
                padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: 'linear-gradient(135deg, #059669, #10b981)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 800, color: '#fff', fontSize: 14,
                }}>I</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#d1fae5' }}>P4I — PRISM-4-ISO</div>
                  <div style={{ fontSize: 10, color: '#6ee7b7' }}>ISO-Implementierungsmanagement</div>
                </div>
                <a href="https://p4i-mvp.secaas.it" target="_blank" rel="noreferrer" style={{
                  marginLeft: 'auto', fontSize: 10, color: '#34d399',
                  textDecoration: 'none', background: '#064e3b',
                  padding: '3px 8px', borderRadius: 5, border: '1px solid #34d39933',
                }}>↗ Öffnen</a>
              </div>
              <div style={{ padding: '14px 16px' }}>
                {[
                  { std: 'iso27001', stats: stats27 },
                  { std: 'iso9001',  stats: stats9  },
                ].map(({ std, stats }) => {
                  const sm = STANDARD_META[std];
                  const pct = stats.total > 0 ? Math.round((stats.impl.implementiert / stats.total) * 100) : 0;
                  return (
                    <div key={std} style={{ marginBottom: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: sm.color }}>{sm.label}</span>
                        <span style={{ fontSize: 11, color: 'var(--text3)' }}>{stats.impl.implementiert}/{stats.total} implementiert</span>
                      </div>
                      {/* Progress bar */}
                      <div style={{ height: 5, background: 'var(--border)', borderRadius: 3, marginBottom: 8, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%', borderRadius: 3, background: sm.color,
                          width: `${pct}%`, transition: 'width 0.6s ease',
                        }} />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
                        {[
                          { key: 'impl.implementiert',  label: 'Impl.',     val: stats.impl.implementiert,  color: '#34d399' },
                          { key: 'impl.in_bearbeitung', label: 'In Bearb.', val: stats.impl.in_bearbeitung, color: '#fb923c' },
                          { key: 'audit.compliant',     label: 'Konform',   val: stats.audit.compliant,     color: '#60a5fa' },
                          { key: 'audit.finding',       label: 'Findings',  val: stats.audit.finding,       color: '#f87171' },
                        ].map(s => (
                          <div key={s.key} style={{
                            background: 'var(--bg)', borderRadius: 6, padding: '6px 8px',
                            border: '1px solid var(--border)', textAlign: 'center',
                            cursor: 'pointer',
                          }} onClick={() => { setUStd(std); setViewMode('unified'); setFilterImpl(s.key.startsWith('impl.') ? s.key.split('.')[1] : ''); setFilterAudit(s.key.startsWith('audit.') ? s.key.split('.')[1] : ''); }}>
                            <div style={{ fontSize: 16, fontWeight: 700, color: s.color, transition: 'all 0.3s' }}>{s.val}</div>
                            <div style={{ fontSize: 9, color: 'var(--text3)' }}>{s.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* P4A Card */}
            <div style={{
              background: 'var(--surface)', border: '1px solid #1a56db33',
              borderRadius: 12, overflow: 'hidden',
              boxShadow: '0 0 0 1px #1a56db11',
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #0c1a3a, #1e3a5f)',
                borderBottom: '1px solid #1a56db44',
                padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: 'linear-gradient(135deg, #1a56db, #3b82f6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 800, color: '#fff', fontSize: 14,
                }}>A</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#dbeafe' }}>P4A — PRISM-4-AUDIT</div>
                  <div style={{ fontSize: 10, color: '#93c5fd' }}>Revisionssichere Audit-Plattform</div>
                </div>
                <a href="https://p4a-mvp.secaas.it" target="_blank" rel="noreferrer" style={{
                  marginLeft: 'auto', fontSize: 10, color: '#60a5fa',
                  textDecoration: 'none', background: '#1e3a5f',
                  padding: '3px 8px', borderRadius: 5, border: '1px solid #3b82f633',
                }}>↗ Öffnen</a>
              </div>
              <div style={{ padding: '14px 16px' }}>
                {/* Session Stats */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 }}>
                  {[
                    { label: 'Sessions',     val: sessions.length,                                    color: '#60a5fa' },
                    { label: 'Aktiv',        val: sessions.filter(s => s.status === 'Aktiv').length,  color: '#34d399' },
                    { label: 'Feststellungen', val: findings.length,                                  color: findings.length > 0 ? '#f87171' : '#34d399' },
                  ].map(s => (
                    <div key={s.label} style={{
                      background: 'var(--bg)', borderRadius: 6, padding: '8px 10px',
                      border: '1px solid var(--border)', textAlign: 'center',
                    }}>
                      <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.val}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)' }}>{s.label}</div>
                    </div>
                  ))}
                </div>
                {/* Findings */}
                {findings.length > 0 && (
                  <div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                      Offene Feststellungen
                    </div>
                    {findings.map(f => (
                      <div key={f.id} style={{
                        background: 'var(--bg)', borderRadius: 6, padding: '7px 10px',
                        border: '1px solid #f8717122', marginBottom: 5,
                        display: 'flex', alignItems: 'center', gap: 8,
                      }}>
                        <span style={{
                          background: f.severity === 'high' ? '#450a0a' : f.severity === 'medium' ? '#3b2300' : '#1c2333',
                          color: f.severity === 'high' ? '#f87171' : f.severity === 'medium' ? '#fb923c' : '#6b7280',
                          padding: '1px 5px', borderRadius: 4, fontSize: 9, fontWeight: 700,
                        }}>{(f.severity || 'LOW').toUpperCase()}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {f.finding_ref} · {f.control_name}
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--text3)' }}>{f.category}</div>
                        </div>
                        {FINDING_CTRL_MAP[f.control_id] && (
                          <button onClick={() => {
                            const m = FINDING_CTRL_MAP[f.control_id];
                            setUStd(m.std);
                            setViewMode('unified');
                            setSearch(m.id);
                          }} style={{
                            padding: '2px 7px', fontSize: 9, cursor: 'pointer',
                            background: 'transparent', border: '1px solid var(--border)',
                            borderRadius: 4, color: 'var(--text3)',
                          }}>→ Matrix</button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {/* Active Sessions */}
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                    Aktive Prüfungen
                  </div>
                  {sessions.filter(s => s.status === 'Aktiv').map(s => (
                    <div key={s.id} style={{
                      background: 'var(--bg)', borderRadius: 6, padding: '7px 10px',
                      border: '1px solid #34d39922', marginBottom: 5,
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#34d399', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</div>
                        <div style={{ fontSize: 10, color: 'var(--text3)' }}>{s.audit_type} · {s.auditor}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Live Demo Controls — P4I ←→ P4A Bridge */}
          <div style={{
            background: 'linear-gradient(135deg, #0a1628, #1e1b4b)',
            border: '1px solid #312e81', borderRadius: 12, padding: '16px 18px', marginBottom: 16,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#e0e7ff', marginBottom: 2 }}>
                  ⚡ Live Integration Demo
                </div>
                <div style={{ fontSize: 11, color: '#818cf8' }}>
                  Implementiere einen Control in P4I → P4A startet sofort den Audit → KPIs oben aktualisieren sich live
                </div>
              </div>
              <button onClick={async () => {
                await apiFetch(token, '/cockpit/prism/reset', { method: 'DELETE' }).catch(() => {});
                loadData();
              }} style={{
                padding: '5px 12px', background: 'transparent', border: '1px solid #4f46e5',
                borderRadius: 7, color: '#818cf8', fontSize: 11, cursor: 'pointer',
              }}>↺ Reset</button>
            </div>

            {PRISM_DEMO_IDS.map(cid => {
              const std = 'iso27001';
              const control = (data?.p4i?.iso27001?.controls || []).find(c => c.control_id === cid);
              if (!control) return null;
              const iStatus  = getImpl(std, cid);
              const aStatus  = getAudit(std, cid);
              const aResult  = auditSt[std]?.[cid];
              const isRunning = auditing[`${std}:${cid}`];
              const finding  = findingMap[`${std}:${cid}`];
              const iMeta = IMPL_META[iStatus] || IMPL_META.ausstehend;
              const aMeta = AUDIT_META[aStatus] || AUDIT_META.not_audited;
              const bridgeActive = iStatus === 'implementiert';

              return (
                <div key={cid} style={{
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 10, marginBottom: 10, overflow: 'hidden',
                }}>
                  {/* Header */}
                  <div style={{
                    background: 'var(--bg)', borderBottom: '1px solid var(--border)',
                    padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#818cf8', fontFamily: 'monospace' }}>{cid}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', flex: 1 }}>{control.control_name}</span>
                    <span style={{ fontSize: 10, color: 'var(--text3)' }}>{control.domain}</span>
                  </div>
                  {/* Body: P4I | Bridge | P4A */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 48px 1fr' }}>
                    {/* P4I */}
                    <div style={{ padding: '12px 14px', borderRight: '1px solid var(--border)' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#10b981', marginBottom: 8, letterSpacing: '0.05em' }}>P4I IMPLEMENTIERUNG</div>
                      <div style={{ marginBottom: 8 }}><ImplBadge status={iStatus} /></div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {iStatus !== 'in_bearbeitung' && iStatus !== 'implementiert' && (
                          <button onClick={() => setImpl(std, cid, 'in_bearbeitung')} style={{
                            padding: '4px 9px', background: '#3b2300', color: '#fb923c',
                            border: '1px solid #fb923c44', borderRadius: 5, fontSize: 10, cursor: 'pointer', fontWeight: 600,
                          }}>🔄 In Bearbeitung</button>
                        )}
                        {iStatus !== 'implementiert' && (
                          <button onClick={async () => {
                            await setImpl(std, cid, 'implementiert');
                            setTimeout(() => runAudit(std, control), 400);
                          }} style={{
                            padding: '4px 9px', background: '#064e3b', color: '#34d399',
                            border: '1px solid #34d39944', borderRadius: 5, fontSize: 10, cursor: 'pointer', fontWeight: 600,
                          }}>✓ Implementiert</button>
                        )}
                        {iStatus === 'implementiert' && (
                          <button onClick={() => setImpl(std, cid, 'ausstehend')} style={{
                            padding: '4px 9px', background: 'transparent', color: 'var(--text3)',
                            border: '1px solid var(--border)', borderRadius: 5, fontSize: 10, cursor: 'pointer',
                          }}>↺ Zurück</button>
                        )}
                      </div>
                    </div>
                    {/* Bridge */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '8px 0' }}>
                      <div style={{ width: 2, flex: 1, background: bridgeActive ? 'linear-gradient(180deg,#34d399,#818cf8)' : 'var(--border)', transition: 'background 0.5s' }} />
                      <span style={{ fontSize: 14, color: bridgeActive ? '#818cf8' : 'var(--border)', transition: 'color 0.5s', lineHeight: 1 }}>◆</span>
                      <div style={{ width: 2, flex: 1, background: aResult ? (aResult.audit_status === 'compliant' ? 'linear-gradient(180deg,#818cf8,#34d399)' : 'linear-gradient(180deg,#818cf8,#f87171)') : 'var(--border)', transition: 'background 0.5s' }} />
                    </div>
                    {/* P4A */}
                    <div style={{ padding: '12px 14px', borderLeft: '1px solid var(--border)' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#3b82f6', marginBottom: 8, letterSpacing: '0.05em' }}>P4A AUDIT</div>
                      {isRunning ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#818cf8', fontSize: 11 }}><Spin size={12} /><span>Audit läuft…</span></div>
                      ) : aResult ? (
                        <div>
                          <div style={{ marginBottom: 6 }}><AuditBadge status={aResult.audit_status} /></div>
                          <div style={{ fontSize: 10, color: 'var(--text2)', lineHeight: 1.5, background: 'var(--bg)', borderRadius: 5, padding: '6px 8px', border: `1px solid ${aMeta.dot}22` }}>
                            {aResult.comment?.slice(0, 120)}{aResult.comment?.length > 120 ? '…' : ''}
                          </div>
                        </div>
                      ) : iStatus === 'implementiert' ? (
                        <button onClick={() => runAudit(std, control)} style={{
                          padding: '4px 10px', background: '#1e3a5f', color: '#93c5fd',
                          border: '1px solid #3b82f644', borderRadius: 5, fontSize: 10, cursor: 'pointer', fontWeight: 600,
                        }}>▶ Audit starten</button>
                      ) : (
                        <span style={{ fontSize: 10, color: 'var(--text3)', fontStyle: 'italic' }}>Wartet auf Implementierung…</span>
                      )}
                      {finding && !aResult && (
                        <div style={{ marginTop: 6, fontSize: 10, color: '#f87171' }}>⚠ P4A-Feststellung {finding.finding_ref} vorhanden</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <div style={{ textAlign: 'center' }}>
            <button onClick={() => setViewMode('unified')} style={{
              padding: '8px 20px', background: 'transparent', border: '1px solid #312e81',
              borderRadius: 8, color: '#818cf8', fontSize: 12, cursor: 'pointer',
            }}>◆ Alle Controls in der Unified Matrix →</button>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════
          UNIFIED MATRIX VIEW
      ════════════════════════════════════════════════ */}
      {viewMode === 'unified' && (
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          {/* Main table */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Health Panel */}
            {(() => {
              const health = calcPrismHealth(data?.p4i, getImpl, getAudit, findingMap);
              return <PrismHealthPanel health={health} />;
            })()}
            {/* Standard + Filter bar */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Standard switcher */}
              <div style={{ display: 'flex', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 7, padding: 2, gap: 2 }}>
                {Object.entries(STANDARD_META).map(([std, sm]) => (
                  <button key={std} onClick={() => { setUStd(std); setFilterDomain(''); setFilterImpl(''); setFilterAudit(''); }} style={{
                    padding: '5px 12px', borderRadius: 5, border: 'none', cursor: 'pointer',
                    fontSize: 11, fontWeight: 600,
                    background: uStd === std ? sm.color : 'transparent',
                    color: uStd === std ? '#fff' : 'var(--text3)',
                  }}>{sm.label}</button>
                ))}
              </div>
              <input placeholder="Suchen…" value={search} onChange={e => setSearch(e.target.value)} style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 7, padding: '5px 10px', fontSize: 12, color: 'var(--text)', flex: '1 1 140px',
              }} />
              {/* Domain filter */}
              <select value={filterDomain} onChange={e => setFilterDomain(e.target.value)} style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 7, padding: '5px 8px', fontSize: 11, color: 'var(--text)',
              }}>
                <option value="">Alle Domains</option>
                {[...new Set((data?.p4i?.[uStd]?.controls || []).map(c => c.domain))].filter(Boolean).map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <select value={filterImpl} onChange={e => setFilterImpl(e.target.value)} style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 7, padding: '5px 8px', fontSize: 11, color: 'var(--text)',
              }}>
                <option value="">Alle P4I-Status</option>
                <option value="implementiert">Implementiert</option>
                <option value="in_bearbeitung">In Bearbeitung</option>
                <option value="ausstehend">Ausstehend</option>
              </select>
              <select value={filterAudit} onChange={e => setFilterAudit(e.target.value)} style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 7, padding: '5px 8px', fontSize: 11, color: 'var(--text)',
              }}>
                <option value="">Alle Audit-Status</option>
                <option value="compliant">Konform</option>
                <option value="finding">Feststellung</option>
                <option value="not_audited">Nicht geprüft</option>
              </select>
            </div>

            {/* Inline standard score strip */}
            {(() => {
              const health = calcPrismHealth(data?.p4i, getImpl, getAudit, findingMap);
              const ss = health.standardScores[uStd];
              if (!ss) return null;
              const m = getMaturity(ss.score);
              return (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  background: 'var(--surface)', border: `1px solid ${m.color}33`,
                  borderRadius: 8, padding: '8px 14px', marginBottom: 10,
                }}>
                  <span style={{ fontSize: 20, fontWeight: 800, color: m.color, transition: 'all 0.4s' }}>{ss.score}</span>
                  <span style={{ fontSize: 10, background: m.bg, color: m.color, padding: '2px 7px', borderRadius: 6, fontWeight: 700 }}>{m.label}</span>
                  <div style={{ flex: 1, display: 'flex', gap: 10 }}>
                    {[
                      { l: 'Impl.',   v: ss.dims.impl,       c: '#10b981' },
                      { l: 'Audit',   v: ss.dims.audit,      c: '#3b82f6' },
                      { l: 'Konf.',   v: ss.dims.compliance, c: '#a78bfa' },
                      { l: 'Risiko',  v: ss.dims.risk,       c: '#f59e0b' },
                    ].map(d => (
                      <div key={d.l} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: d.c, flexShrink: 0 }} />
                        <span style={{ fontSize: 10, color: 'var(--text3)' }}>{d.l}</span>
                        <span style={{ fontSize: 10, fontWeight: 700, color: d.c }}>{d.v}%</span>
                      </div>
                    ))}
                  </div>
                  <span style={{ fontSize: 10, color: 'var(--text3)' }}>{ss.counts.impl}/{ss.counts.total} impl · {ss.counts.findings} findings</span>
                </div>
              );
            })()}

            {/* Table */}
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              {/* Table Header */}
              <div style={{
                display: 'grid', gridTemplateColumns: '70px 130px 1fr 140px 140px',
                background: 'var(--bg)', borderBottom: '1px solid var(--border)',
                padding: '7px 14px',
              }}>
                {['ID', 'Domain', 'Control', 'P4I Status', 'P4A Audit'].map(h => (
                  <div key={h} style={{ fontSize: 9, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</div>
                ))}
              </div>

              {/* Rows */}
              <div style={{ maxHeight: 480, overflowY: 'auto' }}>
                {(() => {
                  const controls = data?.p4i?.[uStd]?.controls || [];
                  const rows = controls.filter(c => {
                    if (filterDomain && c.domain !== filterDomain) return false;
                    if (filterImpl && getImpl(uStd, c.control_id) !== filterImpl) return false;
                    if (filterAudit && getAudit(uStd, c.control_id) !== filterAudit) return false;
                    if (search) {
                      const s = search.toLowerCase();
                      if (!c.control_id.toLowerCase().includes(s) && !c.control_name.toLowerCase().includes(s)) return false;
                    }
                    return true;
                  });

                  if (rows.length === 0) return (
                    <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
                      Keine Controls gefunden
                    </div>
                  );

                  return rows.map((c, i) => {
                    const impl  = getImpl(uStd, c.control_id);
                    const audit = getAudit(uStd, c.control_id);
                    const isSelected = panel?.control_id === c.control_id && panel?.std === uStd;

                    return (
                      <div key={c.control_id}
                        onClick={() => setPanel(isSelected ? null : { ...c, std: uStd })}
                        style={{
                          display: 'grid', gridTemplateColumns: '70px 130px 1fr 140px 140px',
                          padding: '8px 14px',
                          borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none',
                          background: isSelected ? '#4f46e511' : i % 2 === 0 ? 'transparent' : '#ffffff03',
                          cursor: 'pointer', transition: 'background 0.1s',
                          borderLeft: isSelected ? '3px solid #4f46e5' : '3px solid transparent',
                        }}
                        onMouseEnter={e => !isSelected && (e.currentTarget.style.background = '#ffffff07')}
                        onMouseLeave={e => !isSelected && (e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : '#ffffff03')}
                      >
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#818cf8', fontFamily: 'monospace' }}>{c.control_id}</div>
                        <div style={{ fontSize: 10, color: 'var(--text3)', paddingRight: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.domain}</div>
                        <div style={{ fontSize: 12, color: 'var(--text)', paddingRight: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.control_name}</div>
                        <div><ImplBadge status={impl} /></div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <AuditBadge status={audit} />
                          {findingMap[`${uStd}:${c.control_id}`] && <span style={{ fontSize: 10, color: '#f87171' }}>F</span>}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>

          {/* Detail Panel */}
          {panel && (
            <div style={{
              width: 300, flexShrink: 0,
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 12, overflow: 'hidden',
              position: 'sticky', top: 0,
            }}>
              {/* Panel Header */}
              <div style={{
                background: 'var(--bg)', borderBottom: '1px solid var(--border)',
                padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#818cf8', fontFamily: 'monospace' }}>{panel.control_id}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>{panel.domain}</div>
                </div>
                <button onClick={() => setPanel(null)} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 16 }}>✕</button>
              </div>

              <div style={{ padding: '14px', overflowY: 'auto', maxHeight: 'calc(100vh - 320px)' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 10, lineHeight: 1.4 }}>
                  {panel.control_name}
                </div>

                {panel.description && (
                  <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5, marginBottom: 14, padding: '8px 10px', background: 'var(--bg)', borderRadius: 6 }}>
                    {panel.description.slice(0, 200)}{panel.description.length > 200 ? '…' : ''}
                  </div>
                )}

                {/* P4I Status in Panel */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                    P4I — Implementierung
                  </div>
                  <div style={{ marginBottom: 8 }}><ImplBadge status={getImpl(panel.std, panel.control_id)} /></div>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    {['ausstehend', 'in_bearbeitung', 'implementiert'].map(s => {
                      const current = getImpl(panel.std, panel.control_id);
                      const labels = { ausstehend: 'Ausstehend', in_bearbeitung: 'In Bearb.', implementiert: 'Implementiert' };
                      return (
                        <button key={s} onClick={async () => {
                          await setImpl(panel.std, panel.control_id, s);
                          if (s === 'implementiert') setTimeout(() => runAudit(panel.std, panel), 400);
                        }} style={{
                          padding: '4px 8px', borderRadius: 5, fontSize: 10, cursor: 'pointer', fontWeight: 600,
                          border: current === s ? `1px solid ${IMPL_META[s].dot}` : '1px solid var(--border)',
                          background: current === s ? IMPL_META[s].bg : 'var(--bg)',
                          color: current === s ? IMPL_META[s].text : 'var(--text3)',
                        }}>{labels[s]}</button>
                      );
                    })}
                  </div>
                </div>

                {/* P4A Status in Panel */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#3b82f6', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                    P4A — Audit
                  </div>
                  {auditing[`${panel.std}:${panel.control_id}`] ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#818cf8', fontSize: 11 }}><Spin size={12} /><span>Audit läuft…</span></div>
                  ) : (
                    <div>
                      <div style={{ marginBottom: 6 }}><AuditBadge status={getAudit(panel.std, panel.control_id)} /></div>
                      {auditSt[panel.std]?.[panel.control_id]?.comment && (
                        <div style={{ fontSize: 10, color: 'var(--text2)', lineHeight: 1.5, background: 'var(--bg)', borderRadius: 5, padding: '6px 8px', marginBottom: 8 }}>
                          {auditSt[panel.std]?.[panel.control_id]?.comment}
                        </div>
                      )}
                      {findingMap[`${panel.std}:${panel.control_id}`] && (
                        <div style={{ fontSize: 10, color: '#f87171', background: '#450a0a', borderRadius: 5, padding: '6px 8px', marginBottom: 8 }}>
                          {findingMap[`${panel.std}:${panel.control_id}`]?.description?.slice(0, 120)}…
                        </div>
                      )}
                      {getImpl(panel.std, panel.control_id) === 'implementiert' && (
                        <button onClick={() => runAudit(panel.std, panel)} style={{
                          padding: '4px 10px', background: '#1e3a5f', color: '#93c5fd',
                          border: '1px solid #3b82f644', borderRadius: 5, fontSize: 10, cursor: 'pointer',
                        }}>▶ Audit starten</button>
                      )}
                    </div>
                  )}
                </div>

                {/* Evidence Requirements */}
                {panel.evidence_requirements?.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                      Nachweise erforderlich
                    </div>
                    {panel.evidence_requirements.slice(0, 3).map((e, i) => (
                      <div key={i} style={{ fontSize: 10, color: 'var(--text3)', padding: '3px 0', borderBottom: '1px solid var(--border)', display: 'flex', gap: 6 }}>
                        <span style={{ color: '#4f46e5' }}>·</span>{e}
                      </div>
                    ))}
                  </div>
                )}

                {/* Deep link to P4I */}
                <a
                  href={`https://p4i-mvp.secaas.it/controls/${panel.std}/${encodeURIComponent(panel.control_id)}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    padding: '8px 14px', background: 'linear-gradient(135deg, #052e16, #064e3b)',
                    border: '1px solid #34d39944', borderRadius: 8,
                    color: '#34d399', fontSize: 11, fontWeight: 600,
                    textDecoration: 'none', marginBottom: 8,
                  }}
                >
                  ↗ In P4I öffnen — {panel.control_id}
                </a>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────
   WebTerminalTab — Admin only, ttyd-based
───────────────────────────────────────────────────────────────── */
function WebTerminalTab() {
  const iframeRef = useRef(null);

  return (
    <div>
      <div style={{
        background: 'var(--red)', color: '#fff', borderRadius: 6,
        padding: '8px 14px', marginBottom: 12, fontSize: 12, fontWeight: 600,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span>⚠</span>
        Admin-Terminal — Nur für autorisierte Administratoren. Alle Aktionen werden protokolliert.
      </div>
      <div style={{
        border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden',
        height: 'calc(100vh - 220px)', minHeight: 400,
        background: '#1a1a1a',
      }}>
        <iframe
          ref={iframeRef}
          src="/terminal/"
          title="Web Terminal"
          style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
          sandbox="allow-same-origin allow-scripts allow-forms"
        />
      </div>
      <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text3)', textAlign: 'right' }}>
        Terminal läuft via ttyd — Verbindung durch nginx gesichert
      </div>
    </div>
  );
}

// ── User Management Tab ────────────────────────────────────────────────────────
const ROLES = ['admin', 'developer', 'viewer'];
const ROLE_LABELS = {
  admin:     '⚙️ Admin',
  developer: '💻 Developer',
  viewer:    '👁 Viewer',
};

function UserManagementTab({ token }) {
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [editId, setEditId]       = useState(null);
  const [editData, setEditData]   = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ username: '', email: '', display_name: '', password: '', role: 'developer' });
  const [createError, setCreateError] = useState(null);
  const [saving, setSaving]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await apiFetch(token, '/cockpit/users');
      setUsers(data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (u) => { setEditId(u.id); setEditData({ display_name: u.display_name || '', email: u.email || '', role: u.role, is_active: u.is_active }); };
  const cancelEdit = () => { setEditId(null); setEditData({}); };

  const saveEdit = async (userId) => {
    setSaving(true);
    try {
      const updated = await apiFetch(token, `/cockpit/users/${userId}`, { method: 'PATCH', body: JSON.stringify(editData) });
      setUsers(prev => prev.map(u => u.id === userId ? updated : u));
      cancelEdit();
    } catch (e) { alert(`Fehler: ${e.message}`); }
    finally { setSaving(false); }
  };

  const deactivate = async (userId, username) => {
    if (!window.confirm(`Benutzer "${username}" deaktivieren?`)) return;
    try {
      await apiFetch(token, `/cockpit/users/${userId}`, { method: 'DELETE' });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: false } : u));
    } catch (e) { alert(`Fehler: ${e.message}`); }
  };

  const createUser = async () => {
    if (!createForm.username.trim() || !createForm.password.trim()) {
      setCreateError('Benutzername und Passwort sind Pflichtfelder'); return;
    }
    setSaving(true); setCreateError(null);
    try {
      const u = await apiFetch(token, '/cockpit/users', { method: 'POST', body: JSON.stringify(createForm) });
      setUsers(prev => [...prev, u]);
      setShowCreate(false);
      setCreateForm({ username: '', email: '', display_name: '', password: '', role: 'developer' });
    } catch (e) { setCreateError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>👥 Benutzerverwaltung</h2>
          <p style={{ margin: 0, fontSize: 11, color: 'var(--text3)' }}>{users.length} Benutzer</p>
        </div>
        <button onClick={() => setShowCreate(s => !s)} style={{
          marginLeft: 'auto', background: 'var(--green)', border: 'none', color: '#000',
          borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
        }}>+ Benutzer anlegen</button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Neuen Benutzer anlegen</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              ['Benutzername *', 'username', 'text', 'jdoe'],
              ['Anzeigename', 'display_name', 'text', 'John Doe'],
              ['E-Mail', 'email', 'email', 'john@example.com'],
              ['Passwort *', 'password', 'password', ''],
            ].map(([label, field, type, ph]) => (
              <div key={field}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>{label}</div>
                <input type={type} value={createForm[field]} placeholder={ph}
                  onChange={e => setCreateForm(f => ({ ...f, [field]: e.target.value }))}
                  style={{ width: '100%', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 10px', fontSize: 12, color: 'var(--text)', boxSizing: 'border-box' }} />
              </div>
            ))}
          </div>
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Rolle</div>
            <select value={createForm.role} onChange={e => setCreateForm(f => ({ ...f, role: e.target.value }))}
              style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 10px', fontSize: 12, color: 'var(--text)' }}>
              {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
            </select>
          </div>
          {createError && <div style={{ marginTop: 8, color: 'var(--red)', fontSize: 12 }}>{createError}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button onClick={createUser} disabled={saving} style={{
              background: 'var(--accent)', border: 'none', color: '#fff', borderRadius: 6,
              padding: '6px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
            }}>✓ Anlegen</button>
            <button onClick={() => { setShowCreate(false); setCreateError(null); }} style={{
              background: 'none', border: '1px solid var(--border)', color: 'var(--text3)',
              borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer',
            }}>Abbrechen</button>
          </div>
        </div>
      )}

      {error && <div style={{ color: 'var(--red)', fontSize: 13, padding: 10 }}>Fehler: {error}</div>}
      {loading && <div style={{ color: 'var(--text3)', fontSize: 13 }}>Lädt…</div>}

      {/* User Table */}
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg3)' }}>
              {['Benutzer', 'E-Mail', 'Rolle', 'Status', 'MFA', 'Letzter Login', ''].map(h => (
                <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--text3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u, i) => (
              <tr key={u.id} style={{ borderBottom: i < users.length - 1 ? '1px solid var(--border)' : 'none', opacity: u.is_active ? 1 : 0.5 }}>
                {editId === u.id ? (
                  <>
                    <td style={{ padding: '8px 12px' }}>
                      <input value={editData.display_name} onChange={e => setEditData(d => ({ ...d, display_name: e.target.value }))}
                        style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 4, padding: '3px 8px', fontSize: 12, color: 'var(--text)', width: 130 }} />
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>@{u.username}</div>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <input value={editData.email} onChange={e => setEditData(d => ({ ...d, email: e.target.value }))}
                        style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 4, padding: '3px 8px', fontSize: 12, color: 'var(--text)', width: 160 }} />
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <select value={editData.role} onChange={e => setEditData(d => ({ ...d, role: e.target.value }))}
                        style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 4, padding: '3px 8px', fontSize: 12, color: 'var(--text)' }}>
                        {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                        <input type="checkbox" checked={editData.is_active} onChange={e => setEditData(d => ({ ...d, is_active: e.target.checked }))} />
                        <span style={{ fontSize: 11 }}>Aktiv</span>
                      </label>
                    </td>
                    <td style={{ padding: '8px 12px', color: 'var(--text3)' }}>{u.mfa_enabled ? '✅' : '—'}</td>
                    <td style={{ padding: '8px 12px', color: 'var(--text3)' }}>{u.last_login ? new Date(u.last_login).toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin' }) : '—'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => saveEdit(u.id)} disabled={saving} style={{ background: 'var(--green)', border: 'none', color: '#000', borderRadius: 4, padding: '3px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>✓</button>
                        <button onClick={cancelEdit} style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text3)', borderRadius: 4, padding: '3px 8px', fontSize: 11, cursor: 'pointer' }}>✕</button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td style={{ padding: '8px 12px' }}>
                      <div style={{ fontWeight: 600, color: 'var(--text)' }}>{u.display_name || u.username}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)' }}>@{u.username}</div>
                    </td>
                    <td style={{ padding: '8px 12px', color: 'var(--text2)' }}>{u.email || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ fontSize: 11, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 20, padding: '2px 8px', color: 'var(--text2)' }}>
                        {ROLE_LABELS[u.role] || u.role}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: u.is_active ? 'var(--green)' : 'var(--red)' }}>
                        {u.is_active ? '● Aktiv' : '● Inaktiv'}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', color: 'var(--text3)', fontSize: 13 }}>{u.mfa_enabled ? '✅' : '—'}</td>
                    <td style={{ padding: '8px 12px', color: 'var(--text3)' }}>{u.last_login ? new Date(u.last_login).toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin' }) : '—'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => startEdit(u)} style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text2)', borderRadius: 4, padding: '3px 10px', fontSize: 11, cursor: 'pointer' }}>✏️ Edit</button>
                        {u.is_active && (
                          <button onClick={() => deactivate(u.id, u.username)} style={{ background: 'none', border: '1px solid var(--red)', color: 'var(--red)', borderRadius: 4, padding: '3px 8px', fontSize: 11, cursor: 'pointer' }}>Deaktivieren</button>
                        )}
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* ── Dashboard Tab: Live Monitor + Server Health zusammen ───────────────── */
function DashboardTab({ token }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <LiveTab token={token} />
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 4 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)',
          textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>
          🖥️ Server Health
        </div>
        <ServerHealthTab token={token} />
      </div>
    </div>
  );
}

const TABS = [
  { id: 'dashboard',    label: 'Dashboard',      icon: ICONS.zap       },
  { id: 'taskgen',      label: 'Task Generator', icon: ICONS.cpu       },
  { id: 'claudecode',   label: 'Claude Code',    icon: ICONS.terminal  },
  { id: 'sessions',     label: 'Sessions',       icon: ICONS.activity  },
  { id: 'kanban',       label: 'Kanban',         icon: ICONS.columns   },
  { id: 'diskussion',   label: 'Diskussion',     icon: ICONS.message   },
  { id: 'projekte',     label: 'Projekte',       icon: ICONS.folder    },
  { id: 'logs',         label: 'Logs',           icon: ICONS.filetext  },
  { id: 'freigaben',    label: 'Freigaben',      icon: ICONS.clock     },
  { id: 'claudeusage',  label: 'Claude Nutzung', icon: ICONS.barchart  },
  { id: 'orchadmin',    label: 'Orchestrator',   icon: ICONS.settings  },
  { id: 'status',       label: 'Status',         icon: ICONS.trending  },
  { id: 'prism',        label: 'PRISM',          icon: ICONS.prism     },
  { id: 'ideenlog',     label: 'Ideen-Log',      icon: ICONS.lightbulb },
  { id: 'sicherheit',   label: 'Sicherheit',     icon: ICONS.lock      },
  { id: 'transporte',     label: 'Transporte',      icon: ICONS.truck },
  { id: 'claudeportal', label: 'Mein Claude Token', icon: ICONS.lock },
  { id: 'claudeaccounts', label: 'Claude Accounts', icon: ICONS.lock, adminOnly: true },
  { id: 'terminal',     label: 'Terminal',       icon: ICONS.terminal, adminOnly: true },
  { id: 'usermgmt',    label: 'Benutzer',       icon: ICONS.settings, adminOnly: true },
];



/* ─────────────────────────────────────────────────────────────────
   TransporteTab — Transport Management System (TMS)
   DEV → Q → PROD Pipeline mit Rollen-Ansichten und QA-Checkliste.
   Admins (jk, mt): sehen alle Umgebungen.
   Developer: sehen nur DEV-Spalten.
───────────────────────────────────────────────────────────────── */
function TransporteTab({ token, userEmail, userRole }) {
  const ADMIN_EMAILS = ['jk@sirius-europe.com', 'mt@sirius-europe.com'];
  const ADMIN_ROLES  = ['admin', 'dev_dash_admin', 'provider_admin'];
  const isAdmin = ADMIN_ROLES.includes(userRole) || ADMIN_EMAILS.includes(userEmail);

  const STATUS_COLS = isAdmin
    ? [
        { id: 'dev',            label: '🛠 In Entwicklung', env: 'dev',  color: 'var(--accent)' },
        { id: 'ready_for_q',    label: '📦 Bereit für Q',  env: 'dev',  color: '#f59e0b' },
        { id: 'in_qa',          label: '🔍 In QA',          env: 'q',    color: '#8b5cf6' },
        { id: 'qa_approved',    label: '✅ QA Freigegeben', env: 'q',    color: 'var(--green)' },
        { id: 'deployed',       label: '🚀 Produktion',     env: 'prod', color: '#34d399' },
      ]
    : [
        { id: 'dev',         label: '🛠 In Entwicklung', env: 'dev', color: 'var(--accent)' },
        { id: 'ready_for_q', label: '📦 Bereit für Q',  env: 'dev', color: '#f59e0b' },
      ];

  const [transports, setTransports] = useState([]);
  const [visibleEnvs, setVisibleEnvs] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);       // detail/QA view
  const [msg, setMsg]           = useState(null);
  const [creating, setCreating] = useState(false);
  const [transitioning, setTransitioning] = useState(null);

  // Create form state
  const [newT, setNewT] = useState({ title: '', description: '', systems: [], tags: '', notes: '' });
  const SYSTEMS = ['bpos'];

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const d = await apiFetch(token, '/transports');
      setTransports(d.transports || []);
      setVisibleEnvs(d.visible_envs || []);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newT.title.trim()) return;
    setCreating(true);
    try {
      const d = await apiFetch(token, '/transports', {
        method: 'POST',
        body: JSON.stringify({
          title: newT.title, description: newT.description,
          systems: newT.systems, tags: newT.tags ? newT.tags.split(',').map(t=>t.trim()) : [],
          notes: newT.notes,
        }),
      });
      setMsg({ type: 'ok', text: `Transport ${d.transport.number} erstellt.` });
      setShowCreate(false);
      setNewT({ title: '', description: '', systems: [], tags: '', notes: '' });
      load();
    } catch(e) { setMsg({ type: 'err', text: e.message }); }
    finally { setCreating(false); }
  };

  const transition = async (tid, toStatus, note, rejectReason) => {
    setTransitioning(tid + toStatus);
    try {
      const d = await apiFetch(token, `/transports/${tid}/transition`, {
        method: 'POST',
        body: JSON.stringify({ to_status: toStatus, note: note || null, reject_reason: rejectReason || null }),
      });
      setMsg({ type: 'ok', text: d.message });
      if (selected?.id === tid) setSelected(d.transport);
      load();
    } catch(e) { setMsg({ type: 'err', text: e.message }); }
    finally { setTransitioning(null); }
  };

  const toggleChecklist = async (tid, itemId, checked) => {
    try {
      const d = await apiFetch(token, `/transports/${tid}/checklist/${itemId}`, {
        method: 'POST',
        body: JSON.stringify({ checked }),
      });
      if (selected?.id === tid) setSelected(d.transport);
      load();
    } catch(e) { setMsg({ type: 'err', text: e.message }); }
  };

  const colTransports = (colId) => transports.filter(t => {
    if (colId === 'deployed') return ['deploying_prod','deployed'].includes(t.status);
    return t.status === colId;
  });

  const statusColor = (s) => {
    if (s === 'dev')         return 'var(--accent)';
    if (s === 'ready_for_q') return '#f59e0b';
    if (s === 'in_qa')       return '#8b5cf6';
    if (s === 'qa_approved') return 'var(--green)';
    if (s === 'deployed' || s === 'deploying_prod') return '#34d399';
    if (s === 'rejected')    return 'var(--red)';
    return 'var(--text3)';
  };

  const statusLabel = (s) => {
    const map = { dev:'DEV', ready_for_q:'→ Q', in_qa:'QA', qa_approved:'✓ QA',
                  deploying_prod:'→ PROD', deployed:'PROD', rejected:'✗' };
    return map[s] || s;
  };

  if (loading) return <div style={{ padding: 32, textAlign: 'center' }}><Spin size={24} /><div style={{ marginTop: 8, color: 'var(--text2)' }}>Transporte laden…</div></div>;
  if (error)   return <div style={{ padding: 24 }}><div style={{ color: 'var(--red)' }}>Fehler: {error}</div><button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={load}>Neu laden</button></div>;

  return (
    <div style={{ height: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column', gap: 0 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexShrink: 0 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Transport Management</h2>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
            {isAdmin
              ? 'DEV → Q → PROD · Vollzugriff'
              : 'DEV-Ansicht · Transporte erstellen und in Q schieben'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={load}>↻</button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(v => !v)}>+ Transport</button>
        </div>
      </div>

      {/* Nachrichten */}
      {msg && (
        <div style={{ padding: '8px 12px', borderRadius: 6, marginBottom: 10, flexShrink: 0,
          background: msg.type === 'ok' ? 'rgba(52,211,153,.12)' : 'rgba(248,113,113,.12)',
          color: msg.type === 'ok' ? 'var(--green)' : 'var(--red)',
          display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
          <span>{msg.type === 'ok' ? '✓' : '✗'} {msg.text}</span>
          <button onClick={() => setMsg(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>×</button>
        </div>
      )}

      {/* Create Form */}
      {showCreate && (
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10,
          padding: 16, marginBottom: 14, flexShrink: 0 }}>
          <div style={{ fontWeight: 700, marginBottom: 12 }}>Neuer Transport</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={{ gridColumn: '1/-1' }}>
              <input className="form-input" placeholder="Titel *" value={newT.title}
                onChange={e => setNewT(p => ({ ...p, title: e.target.value }))} />
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <textarea className="form-input" rows={2} placeholder="Beschreibung"
                value={newT.description} onChange={e => setNewT(p => ({ ...p, description: e.target.value }))} />
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 5 }}>Systeme</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {SYSTEMS.map(s => (
                  <button key={s} onClick={() => setNewT(p => ({
                    ...p, systems: p.systems.includes(s) ? p.systems.filter(x=>x!==s) : [...p.systems, s]
                  }))} style={{
                    padding: '3px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer',
                    border: `1px solid ${newT.systems.includes(s) ? 'var(--accent)' : 'var(--border)'}`,
                    background: newT.systems.includes(s) ? 'rgba(91,106,240,.15)' : 'transparent',
                    color: newT.systems.includes(s) ? 'var(--accent)' : 'var(--text2)',
                  }}>{s}</button>
                ))}
              </div>
            </div>
            <div>
              <input className="form-input" placeholder="Tags (kommagetrennt)" value={newT.tags}
                onChange={e => setNewT(p => ({ ...p, tags: e.target.value }))} />
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <textarea className="form-input" rows={2} placeholder="Notizen / Git-Commits"
                value={newT.notes} onChange={e => setNewT(p => ({ ...p, notes: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button className="btn btn-primary btn-sm" onClick={create}
              disabled={creating || !newT.title.trim()}>
              {creating ? <Spin size={11} /> : 'Erstellen'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowCreate(false)}>Abbrechen</button>
          </div>
        </div>
      )}

      {/* Kanban Board */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <div style={{ display: 'flex', gap: 12, minWidth: 'max-content', padding: '0 2px 16px', alignItems: 'flex-start' }}>
          {STATUS_COLS.map(col => (
            <div key={col.id} style={{ width: 260, flexShrink: 0 }}>
              {/* Spalten-Header */}
              <div style={{ padding: '8px 10px', borderRadius: '8px 8px 0 0',
                background: `${col.color}18`, borderBottom: `2px solid ${col.color}`,
                marginBottom: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: col.color }}>{col.label}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>
                  {colTransports(col.id).length} Transport{colTransports(col.id).length !== 1 ? 'e' : ''}
                  {col.env !== 'dev' && <span style={{ marginLeft: 6, fontWeight: 600 }}>· {col.env.toUpperCase()}</span>}
                </div>
              </div>

              {/* Cards */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {colTransports(col.id).length === 0 && (
                  <div style={{ padding: '20px 12px', textAlign: 'center', color: 'var(--text3)',
                    fontSize: 12, border: '1px dashed var(--border)', borderRadius: 8 }}>
                    Leer
                  </div>
                )}
                {colTransports(col.id).map(t => (
                  <div key={t.id} onClick={() => setSelected(t)} style={{
                    background: 'var(--bg2)', border: selected?.id === t.id
                      ? `1px solid ${col.color}` : '1px solid var(--border)',
                    borderRadius: 8, padding: '10px 12px', cursor: 'pointer',
                    transition: 'border-color .15s',
                  }}>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4, fontFamily: 'monospace' }}>
                      {t.number}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, lineHeight: 1.3 }}>
                      {t.title}
                    </div>

                    {/* Systeme */}
                    {t.systems?.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 7 }}>
                        {t.systems.map(s => (
                          <span key={s} style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3,
                            background: 'rgba(91,106,240,.12)', color: 'var(--accent)' }}>{s}</span>
                        ))}
                      </div>
                    )}

                    {/* Meta */}
                    <div style={{ fontSize: 11, color: 'var(--text3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{t.created_by?.split('@')[0]}</span>
                      <span>{new Date(t.created_at).toLocaleDateString('de-DE')}</span>
                    </div>

                    {/* QA Checklist Progress */}
                    {t.qa_checklist?.length > 0 && (
                      <div style={{ marginTop: 6 }}>
                        <div style={{ background: 'var(--border)', borderRadius: 3, height: 3, overflow: 'hidden' }}>
                          <div style={{
                            height: '100%', background: col.color,
                            width: `${(t.qa_checklist.filter(i=>i.checked).length / t.qa_checklist.length) * 100}%`,
                            transition: 'width .3s',
                          }} />
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>
                          QA {t.qa_checklist.filter(i=>i.checked).length}/{t.qa_checklist.length}
                        </div>
                      </div>
                    )}

                    {/* Aktionsbuttons */}
                    <div style={{ marginTop: 8, display: 'flex', gap: 5, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                      {/* DEV → bereit für Q */}
                      {t.status === 'dev' && (
                        <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '3px 7px' }}
                          disabled={transitioning === t.id + 'ready_for_q'}
                          onClick={() => transition(t.id, 'ready_for_q', 'In Q verschoben')}>
                          {transitioning === t.id + 'ready_for_q' ? <Spin size={9}/> : '→ Q schieben'}
                        </button>
                      )}
                      {/* Zurückziehen */}
                      {t.status === 'ready_for_q' && (
                        <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '3px 7px' }}
                          disabled={transitioning === t.id + 'dev'}
                          onClick={() => transition(t.id, 'dev', 'Zurückgezogen')}>
                          ← zurück
                        </button>
                      )}
                      {/* Admin: QA starten */}
                      {t.status === 'ready_for_q' && isAdmin && (
                        <button className="btn btn-primary btn-sm" style={{ fontSize: 10, padding: '3px 7px' }}
                          disabled={transitioning === t.id + 'in_qa'}
                          onClick={() => transition(t.id, 'in_qa', 'QA gestartet')}>
                          {transitioning === t.id + 'in_qa' ? <Spin size={9}/> : '▶ QA starten'}
                        </button>
                      )}
                      {/* Admin: QA freigeben */}
                      {t.status === 'in_qa' && isAdmin && (
                        <button className="btn btn-primary btn-sm" style={{ fontSize: 10, padding: '3px 7px', background: 'var(--green)', color: '#0a0a0a' }}
                          disabled={transitioning === t.id + 'qa_approved'}
                          onClick={() => transition(t.id, 'qa_approved', 'QA freigegeben')}>
                          ✓ Freigeben
                        </button>
                      )}
                      {/* Admin: ablehnen */}
                      {t.status === 'in_qa' && isAdmin && (
                        <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '3px 7px', color: 'var(--red)' }}
                          disabled={transitioning === t.id + 'rejected'}
                          onClick={() => { const r = prompt('Ablehnungsgrund:'); if(r !== null) transition(t.id, 'rejected', 'QA abgelehnt', r); }}>
                          ✗ Ablehnen
                        </button>
                      )}
                      {/* Admin: nach Prod deployen */}
                      {t.status === 'qa_approved' && isAdmin && (
                        <button className="btn btn-primary btn-sm" style={{ fontSize: 10, padding: '3px 7px', background: '#34d399', color: '#0a0a0a' }}
                          disabled={transitioning === t.id + 'deployed'}
                          onClick={() => { transition(t.id, 'deployed', 'Nach Prod deployed'); }}>
                          🚀 Prod
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Detail-Panel / QA-Checkliste */}
      {selected && (
        <div style={{ position: 'fixed', right: 0, top: 0, bottom: 0, width: 360, maxWidth: '90vw',
          background: 'var(--bg)', borderLeft: '1px solid var(--border)',
          padding: 20, overflowY: 'auto', zIndex: 500, display: 'flex', flexDirection: 'column', gap: 12 }}>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'monospace' }}>{selected.number}</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}>{selected.title}</div>
            </div>
            <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none',
              color: 'var(--text3)', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>

          {/* Status-Badge */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
            background: `${statusColor(selected.status)}18`,
            border: `1px solid ${statusColor(selected.status)}`,
            borderRadius: 20, padding: '4px 10px', fontSize: 12, fontWeight: 700,
            color: statusColor(selected.status), alignSelf: 'flex-start' }}>
            {statusLabel(selected.status)}
          </div>

          {selected.description && (
            <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>{selected.description}</div>
          )}

          {/* Systeme */}
          {selected.systems?.length > 0 && (
            <div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 5 }}>Systeme</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {selected.systems.map(s => (
                  <span key={s} style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4,
                    background: 'rgba(91,106,240,.12)', color: 'var(--accent)', border: '1px solid rgba(91,106,240,.2)' }}>{s}</span>
                ))}
              </div>
            </div>
          )}

          {/* Contents */}
          {(selected.contents?.git_commits?.length > 0 || selected.contents?.notes) && (
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px' }}>
              {selected.contents.git_commits?.length > 0 && (
                <div style={{ marginBottom: 6 }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 3 }}>Git Commits</div>
                  {selected.contents.git_commits.map((c,i) => (
                    <div key={i} style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text2)' }}>{c}</div>
                  ))}
                </div>
              )}
              {selected.contents.notes && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 3 }}>Notizen</div>
                  <div style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'pre-wrap' }}>{selected.contents.notes}</div>
                </div>
              )}
            </div>
          )}

          {/* QA-Checkliste */}
          {selected.qa_checklist?.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
                <span>QA-Checkliste</span>
                <span style={{ color: 'var(--text3)', fontWeight: 400, fontSize: 11 }}>
                  {selected.qa_checklist.filter(i=>i.checked).length}/{selected.qa_checklist.length}
                </span>
              </div>
              {selected.qa_checklist.map(item => (
                <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                  <input type="checkbox" checked={item.checked}
                    disabled={!isAdmin || !['in_qa','qa_approved'].includes(selected.status)}
                    onChange={e => toggleChecklist(selected.id, item.id, e.target.checked)}
                    style={{ width: 15, height: 15, cursor: isAdmin ? 'pointer' : 'default', accentColor: 'var(--green)' }} />
                  <span style={{ fontSize: 12, color: item.checked ? 'var(--text2)' : 'var(--text)',
                    textDecoration: item.checked ? 'line-through' : 'none', flex: 1 }}>
                    {item.label}
                  </span>
                  {item.checked_by && (
                    <span style={{ fontSize: 10, color: 'var(--text3)' }}>{item.checked_by.split('@')[0]}</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* History */}
          {selected.history?.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Verlauf</div>
              {[...selected.history].reverse().map((h, i) => (
                <div key={i} style={{ fontSize: 11, padding: '4px 0',
                  borderBottom: '1px solid var(--border)', color: 'var(--text2)' }}>
                  <span style={{ color: 'var(--text3)' }}>{new Date(h.ts).toLocaleString('de-DE', { day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit' })}</span>
                  {' · '}<strong>{h.by?.split('@')[0]}</strong>
                  {' · '}{h.action}
                  {h.note && <span style={{ color: 'var(--text3)' }}> — {h.note}</span>}
                </div>
              ))}
            </div>
          )}

          {/* QA Ablehnung Grund */}
          {selected.qa_reject_reason && (
            <div style={{ background: 'rgba(248,113,113,.1)', border: '1px solid rgba(248,113,113,.3)',
              borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--red)' }}>
              Abgelehnt: {selected.qa_reject_reason}
            </div>
          )}

          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
            Erstellt von {selected.created_by} · {new Date(selected.created_at).toLocaleString('de-DE')}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   ClaudeAccountsTab — Claude Account Vault (Admin only: jk@ + mt@)
   Zeigt alle 5 Team-Accounts, Usage %, aktiven Account, Rotation-Log
   und Billing-Freigabe-Flow.
───────────────────────────────────────────────────────────────── */
function ClaudeAccountsTab({ token, userEmail }) {
  const [status, setStatus]   = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [activating, setActivating] = useState(null);
  const [approving, setApproving] = useState(false);
  const [approveNote, setApproveNote] = useState('');
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAccount, setNewAccount] = useState({ label: '', email: '', note: '' });
  const [selectedCreds, setSelectedCreds] = useState(null); // {id, accessToken, refreshToken}
  const [showCredsDialog, setShowCredsDialog] = useState(false);
  const [credsInput, setCredsInput] = useState({ access_token: '', refresh_token: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const canApproveBilling = ['jk@sirius-europe.com', 'mt@sirius-europe.com'].includes(userEmail);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [s, a] = await Promise.all([
        apiFetch(token, '/vault/status'),
        apiFetch(token, '/vault/accounts'),
      ]);
      setStatus(s);
      setAccounts(a.accounts || []);
    } catch(e) {
      setError(e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const activate = async (id) => {
    setActivating(id);
    try {
      await apiFetch(token, `/vault/accounts/${id}/activate`, { method: 'POST' });
      setMsg({ type: 'ok', text: 'Account aktiviert.' });
      load();
    } catch(e) {
      setMsg({ type: 'err', text: e.message });
    } finally { setActivating(null); }
  };

  const resetUsage = async (id) => {
    try {
      await apiFetch(token, `/vault/accounts/${id}/reset-usage`, { method: 'POST' });
      setMsg({ type: 'ok', text: 'Usage zurückgesetzt.' });
      load();
    } catch(e) {
      setMsg({ type: 'err', text: e.message });
    }
  };

  const approveBilling = async () => {
    setApproving(true);
    try {
      await apiFetch(token, '/vault/approve-billing', { method: 'POST', body: JSON.stringify({ note: approveNote }) });
      setMsg({ type: 'ok', text: 'Billing freigegeben.' });
      setShowApproveDialog(false);
      load();
    } catch(e) {
      setMsg({ type: 'err', text: e.message });
    } finally { setApproving(false); }
  };

  const createAccount = async () => {
    if (!newAccount.label || !newAccount.email) return;
    try {
      await apiFetch(token, '/vault/accounts', { method: 'POST', body: JSON.stringify(newAccount) });
      setMsg({ type: 'ok', text: 'Account-Slot angelegt.' });
      setShowAddForm(false);
      setNewAccount({ label: '', email: '', note: '' });
      load();
    } catch(e) {
      setMsg({ type: 'err', text: e.message });
    }
  };

  const openCredsDialog = (acc) => {
    setSelectedCreds(acc);
    setCredsInput({ access_token: '', refresh_token: '' });
    setShowCredsDialog(true);
  };

  const saveCreds = async () => {
    if (!credsInput.access_token) return;
    setSaving(true);
    try {
      await apiFetch(token, `/vault/accounts/${selectedCreds.id}/set-credentials`, { method: 'POST', body: JSON.stringify(credsInput) });
      setMsg({ type: 'ok', text: 'Credentials gesetzt.' });
      setShowCredsDialog(false);
      load();
    } catch(e) {
      setMsg({ type: 'err', text: e.message });
    } finally { setSaving(false); }
  };

  const usageColor = (pct) => {
    if (pct >= 95) return 'var(--red)';
    if (pct >= 80) return '#f59e0b';
    if (pct >= 60) return '#fb923c';
    return 'var(--green)';
  };

  const p4aColor = (pct) => {
    // pct ist der P4A-Anteil (0-20)
    if (pct >= 18) return 'var(--red)';
    if (pct >= 12) return '#f59e0b';
    return '#f59e0b';
  };

  const formatExpiry = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}><Spin size={24} /><div style={{ marginTop: 8 }}>Vault wird geladen…</div></div>;
  if (error)   return <div style={{ padding: 32 }}><div className="badge badge-red">Fehler: {error}</div><button className="btn btn-secondary btn-sm" style={{ marginLeft: 12 }} onClick={load}>Neu laden</button></div>;

  return (
    <div style={{ padding: '0 0 32px 0', maxWidth: 900 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Claude Accounts</h2>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
            Rotation bei {status?.usage_threshold_pct || 80}% — {status?.total_accounts || 0} Accounts konfiguriert
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={load}>↻ Aktualisieren</button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowAddForm(v => !v)}>+ Account</button>
        </div>
      </div>

      {/* Nachrichten */}
      {msg && (
        <div style={{ padding: '8px 14px', borderRadius: 6, marginBottom: 14,
          background: msg.type === 'ok' ? 'rgba(52,211,153,0.15)' : 'rgba(248,113,113,0.15)',
          color: msg.type === 'ok' ? 'var(--green)' : 'var(--red)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{msg.type === 'ok' ? '✓' : '✗'} {msg.text}</span>
          <button onClick={() => setMsg(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 16 }}>×</button>
        </div>
      )}

      {/* Billing-Warnung */}
      {status?.billing_pending && (
        <div style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid #f59e0b',
          borderRadius: 8, padding: '12px 16px', marginBottom: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 700, color: '#f59e0b', fontSize: 14 }}>⚠ Alle Accounts ≥80% verbraucht</div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
              Billing-Erweiterung erforderlich, um weiterzuarbeiten.
              {status.billing_approved_by && (
                <span> — Freigegeben durch <strong>{status.billing_approved_by}</strong></span>
              )}
            </div>
          </div>
          {canApproveBilling && !status.billing_approved_by && (
            <button className="btn btn-sm" onClick={() => setShowApproveDialog(true)}
              style={{ background: '#f59e0b', color: '#1a1a1a', fontWeight: 700, whiteSpace: 'nowrap' }}>
              Freigeben
            </button>
          )}
        </div>
      )}

      {/* Aktueller Usage aus History */}
      {status?.history_usage_pct != null && (
        <div style={{ background: 'var(--bg2)', borderRadius: 8, padding: '10px 14px',
          marginBottom: 16, fontSize: 12, color: 'var(--text2)',
          display: 'flex', alignItems: 'center', gap: 10 }}>
          <span>📊 Erkannte Usage (aktiver Account):</span>
          <span style={{ color: usageColor(status.history_usage_pct), fontWeight: 700 }}>
            {status.history_usage_pct.toFixed(0)}%
          </span>
          <span>— letzte Rotation: {status.last_rotation
            ? new Date(status.last_rotation).toLocaleString('de-DE') : 'noch keine'}</span>
        </div>
      )}

      {/* Account-Karten */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {accounts.length === 0 && (
          <div style={{ textAlign: 'center', padding: 32, color: 'var(--text3)', fontSize: 13 }}>
            Noch keine Accounts konfiguriert. Klicke "+ Account" um zu beginnen.
          </div>
        )}
        {accounts.map(acc => (
          <div key={acc.id} style={{
            background: acc.is_active ? 'rgba(59,130,246,0.08)' : 'var(--bg2)',
            border: acc.is_active ? '1px solid rgba(59,130,246,0.4)' : '1px solid var(--border)',
            borderRadius: 10, padding: '14px 16px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {/* Status-Indikator */}
              <div style={{
                width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                background: acc.is_active ? 'var(--green)' : (acc.has_credentials ? '#6b7280' : 'var(--red)'),
                boxShadow: acc.is_active ? '0 0 6px var(--green)' : 'none',
              }} />

              {/* Label + Email */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {acc.label}
                  {acc.is_active && <span style={{ fontSize: 10, background: 'var(--green)', color: '#0a0a0a',
                    borderRadius: 4, padding: '1px 6px', fontWeight: 700 }}>AKTIV</span>}
                  {!acc.has_credentials && <span style={{ fontSize: 10, background: 'var(--red)', color: '#fff',
                    borderRadius: 4, padding: '1px 6px' }}>Keine Credentials</span>}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{acc.email}</div>
                {acc.note && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{acc.note}</div>}
                {acc.dev_dash_user_id && (
                  <div style={{ fontSize: 10, color: 'var(--accent)', marginTop: 3 }}>
                    ✓ Dev-Dash verknüpft
                  </div>
                )}
              </div>

              {/* Usage-Bar: zweigeteilt — Claude Code (0-80%) + P4A Reserve (80-100%) */}
              <div style={{ width: 160, flexShrink: 0 }}>
                {/* Effektive Gesamt-Usage */}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
                  <span style={{ color: 'var(--text2)' }}>Claude Code</span>
                  <span style={{ color: usageColor(acc.usage_percent), fontWeight: 700 }}>
                    {(acc.usage_percent || 0).toFixed(0)}%
                  </span>
                </div>
                {/* Stacked Bar: blau = Claude, orange = P4A Reserve */}
                <div style={{ background: 'var(--border)', borderRadius: 4, height: 7, overflow: 'hidden', display: 'flex' }}>
                  <div style={{
                    height: '100%', transition: 'width 0.4s', flexShrink: 0,
                    background: usageColor(acc.usage_percent),
                    width: `${Math.min(acc.usage_percent || 0, 80)}%`,
                  }} />
                  {(acc.p4a_usage_percent || 0) > 0 && (
                    <div style={{
                      height: '100%', transition: 'width 0.4s', flexShrink: 0,
                      background: '#f59e0b',
                      width: `${Math.min(acc.p4a_usage_percent || 0, 20)}%`,
                    }} />
                  )}
                </div>
                {/* P4A Reserve */}
                {(acc.p4a_usage_percent || 0) > 0 || acc.p4a_reserve_only ? (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, marginTop: 3 }}>
                    <span style={{ color: '#f59e0b' }}>P4A Reserve</span>
                    <span style={{ color: '#f59e0b', fontWeight: 600 }}>
                      {acc.p4a_reserve_only
                        ? `${(acc.p4a_usage_percent || 0).toFixed(0)}/20%`
                        : `${(acc.p4a_usage_percent || 0).toFixed(0)}%`}
                    </span>
                  </div>
                ) : null}
                {/* Effektiv gesamt */}
                {(acc.effective_usage_pct || 0) > (acc.usage_percent || 0) && (
                  <div style={{ fontSize: 10, color: usageColor(acc.effective_usage_pct), marginTop: 2, fontWeight: 600 }}>
                    ∑ {(acc.effective_usage_pct || 0).toFixed(0)}% gesamt
                  </div>
                )}
                {acc.weekly_reset_at && (
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>
                    Reset: {new Date(acc.weekly_reset_at).toLocaleDateString('de-DE')}
                  </div>
                )}
                {acc.expires_at != null && (() => {
                  const nowMs = Date.now();
                  const remMs = acc.expires_at - nowMs;
                  const remH  = Math.floor(remMs / 3600000);
                  const remMin = Math.floor(remMs / 60000);
                  const expired = remMs <= 0;
                  const warn    = !expired && remH < 2;
                  const soon    = !expired && !warn && remH < 12;
                  const color   = expired ? 'var(--red)' : warn ? '#f59e0b' : soon ? '#ca8a04' : 'var(--green)';
                  const label   = expired ? `Abgelaufen (${formatExpiry(acc.expires_at)})` :
                                  warn    ? `⚠ Läuft in ${remMin} Min ab` :
                                  soon    ? `⏱ Noch ${remH}h` :
                                            `✓ Noch ${Math.floor(remH/24)}d ${remH%24}h`;
                  return (
                    <div style={{ fontSize: 10, color, fontWeight: warn || expired ? 700 : 400, marginTop: 2,
                      background: expired ? 'rgba(248,113,113,0.12)' : warn ? 'rgba(245,158,11,0.12)' : 'transparent',
                      borderRadius: 3, padding: expired || warn ? '1px 4px' : 0 }}>
                      Token: {label}
                    </div>
                  );
                })()}
              </div>

              {/* Aktionen */}
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                {!acc.is_active && acc.has_credentials && (
                  <button className="btn btn-secondary btn-sm"
                    onClick={() => activate(acc.id)}
                    disabled={activating === acc.id}>
                    {activating === acc.id ? <Spin size={10} /> : '⚡ Aktivieren'}
                  </button>
                )}
                <button className="btn btn-secondary btn-sm"
                  onClick={() => openCredsDialog(acc)}
                  title="Credentials setzen">
                  🔑 Credentials
                </button>
                {acc.usage_percent >= 80 && (
                  <button className="btn btn-secondary btn-sm"
                    onClick={() => resetUsage(acc.id)}
                    title="Usage zurücksetzen">
                    ↺ Reset
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Account hinzufügen */}
      {showAddForm && (
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 10, padding: 16, marginTop: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 12 }}>Neuen Account-Slot anlegen</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input className="form-input" placeholder="Label (z.B. Account 2)" value={newAccount.label}
              onChange={e => setNewAccount(p => ({ ...p, label: e.target.value }))} />
            <input className="form-input" placeholder="E-Mail (z.B. user@sirius-europe.com)" value={newAccount.email}
              onChange={e => setNewAccount(p => ({ ...p, email: e.target.value }))} />
            <input className="form-input" placeholder="Notiz (optional)" value={newAccount.note}
              onChange={e => setNewAccount(p => ({ ...p, note: e.target.value }))} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={createAccount}
                disabled={!newAccount.label || !newAccount.email}>Anlegen</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowAddForm(false)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {/* Credentials-Dialog */}
      {showCredsDialog && selectedCreds && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--bg)', border: '1px solid var(--border)',
            borderRadius: 12, padding: 24, width: 480, maxWidth: '90vw' }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Credentials setzen</div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 16 }}>
              {selectedCreds.label} — {selectedCreds.email}
            </div>
            <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)',
              borderRadius: 6, padding: '8px 12px', marginBottom: 16, fontSize: 12, color: '#f59e0b' }}>
              ℹ Token aus Claude Code auslesen:<br/>
              <code style={{ fontFamily: 'monospace', fontSize: 11 }}>cat ~/.claude/.credentials.json</code>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4, display: 'block' }}>
                  Access Token *
                </label>
                <textarea className="form-input" rows={3} placeholder="eyJ..."
                  style={{ fontFamily: 'monospace', fontSize: 11, resize: 'vertical' }}
                  value={credsInput.access_token}
                  onChange={e => setCredsInput(p => ({ ...p, access_token: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4, display: 'block' }}>
                  Refresh Token (empfohlen)
                </label>
                <textarea className="form-input" rows={2} placeholder="eyJ..."
                  style={{ fontFamily: 'monospace', fontSize: 11, resize: 'vertical' }}
                  value={credsInput.refresh_token}
                  onChange={e => setCredsInput(p => ({ ...p, refresh_token: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button className="btn btn-primary btn-sm" onClick={saveCreds}
                  disabled={!credsInput.access_token || saving}>
                  {saving ? <Spin size={12} /> : 'Speichern'}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setShowCredsDialog(false)}>
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Billing-Approve-Dialog */}
      {showApproveDialog && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--bg)', border: '1px solid #f59e0b',
            borderRadius: 12, padding: 24, width: 440, maxWidth: '90vw' }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#f59e0b', marginBottom: 8 }}>
              ⚠ Billing-Freigabe
            </div>
            <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 16, lineHeight: 1.5 }}>
              Alle 5 Claude Accounts haben ≥80% ihrer Kapazität verbraucht.
              Mit dieser Freigabe läuft der aktive Account in die kostenpflichtige Zusatzleistung.
            </div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 8 }}>Notiz (optional)</div>
            <textarea className="form-input" rows={2} placeholder="Grund für Freigabe..."
              value={approveNote} onChange={e => setApproveNote(e.target.value)}
              style={{ marginBottom: 14 }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-sm" onClick={approveBilling} disabled={approving}
                style={{ background: '#f59e0b', color: '#1a1a1a', fontWeight: 700 }}>
                {approving ? <Spin size={12} /> : 'Jetzt freigeben'}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowApproveDialog(false)}>
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────
   ClaudePortalTab — Kollegen-Portal fuer Token-Einreichung
───────────────────────────────────────────────────────────────── */
function ClaudePortalTab({ token, userEmail }) {
  const PORTAL_SECRET = 'secaas-vault-portal-2024';
  const [slots, setSlots]       = useState([]);
  const [mySlot, setMySlot]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [step, setStep]         = useState(1);
  const [tokenInput, setTokenInput] = useState({ access_token: '', refresh_token: '', expires_at: '' });
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState(null);
  const [parseError, setParseError] = useState(null);

  const loadSlots = async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API}/vault/portal/slots`, {
        headers: { 'Content-Type': 'application/json', 'X-Portal-Secret': PORTAL_SECRET },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const allSlots = data.slots || [];
      setSlots(allSlots);
      const mine = allSlots.find(s => s.email?.toLowerCase() === userEmail?.toLowerCase());
      setMySlot(mine || null);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadSlots(); }, [userEmail]);

  const parseCredentialsJson = (raw) => {
    setParseError(null);
    try {
      const parsed = JSON.parse(raw.trim());
      const creds = parsed.claudeAiOauth || parsed;
      if (creds.accessToken) {
        setTokenInput({
          access_token:  creds.accessToken || '',
          refresh_token: creds.refreshToken || '',
          expires_at:    creds.expiresAt ? String(creds.expiresAt) : '',
        });
        setMsg({ type: 'ok', text: 'JSON erkannt — Felder automatisch befuellt.' });
        return true;
      }
    } catch(e) {}
    return false;
  };

  const handleRawInput = (val) => {
    if (val.trim().startsWith('{')) {
      const ok = parseCredentialsJson(val);
      if (!ok) setParseError('JSON nicht erkannt. Bitte accessToken direkt einfuegen.');
    } else {
      setTokenInput(p => ({ ...p, access_token: val }));
      setParseError(null);
    }
  };

  const submitToken = async () => {
    if (!mySlot || !tokenInput.access_token) return;
    setSaving(true); setMsg(null);
    try {
      const body = {
        account_id:    mySlot.id,
        access_token:  tokenInput.access_token.trim(),
        refresh_token: tokenInput.refresh_token.trim() || null,
        expires_at:    tokenInput.expires_at ? parseInt(tokenInput.expires_at) : null,
      };
      const res = await fetch(`${API}/vault/portal/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Portal-Secret': PORTAL_SECRET,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const result = await res.json();
      setStep(3);
      setMsg({ type: 'ok', text: result.message || 'Token gespeichert!' });
      loadSlots();
    } catch(e) { setMsg({ type: 'err', text: e.message }); }
    finally { setSaving(false); }
  };

  const expiryLabel = (ts) => {
    if (!ts) return null;
    const now = Date.now();
    if (ts < now) return { text: 'Abgelaufen', color: 'var(--red)' };
    const h = Math.round((ts - now) / 3600000);
    if (h < 24) return { text: `Laeuft in ${h}h ab`, color: '#f59e0b' };
    return { text: `Gueltig noch ${Math.round(h/24)} Tage`, color: 'var(--green)' };
  };

  const inputStyle = {
    width: '100%', padding: '8px 12px', borderRadius: 6,
    background: 'var(--bg)', border: '1px solid var(--border)',
    color: 'var(--text)', fontSize: 12, fontFamily: 'monospace',
    outline: 'none', resize: 'vertical', boxSizing: 'border-box',
  };

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}><Spin size={24} /><div style={{ marginTop: 8 }}>Lade Vault-Slots...</div></div>;
  if (error)   return <div style={{ padding: 32 }}><div className="badge badge-red">Fehler: {error}</div><button className="btn btn-secondary btn-sm" style={{ marginLeft: 12 }} onClick={loadSlots}>Neu laden</button></div>;

  return (
    <div style={{ padding: '0 0 32px 0', maxWidth: 680 }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Claude Token Portal</h2>
        <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
          Reiche deinen Claude Code Token ein damit die automatische Account-Rotation funktioniert.
        </div>
      </div>

      {msg && (
        <div style={{ padding: '8px 14px', borderRadius: 6, marginBottom: 16,
          background: msg.type === 'ok' ? 'rgba(52,211,153,0.15)' : 'rgba(248,113,113,0.15)',
          color: msg.type === 'ok' ? 'var(--green)' : 'var(--red)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{msg.type === 'ok' ? '✓' : '✗'} {msg.text}</span>
          <button onClick={() => setMsg(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 16 }}>×</button>
        </div>
      )}

      {!mySlot && (
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: 24, textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Kein Slot gefunden</div>
          <div style={{ fontSize: 13, color: 'var(--text2)' }}>
            Fuer <strong>{userEmail}</strong> ist kein Vault-Slot konfiguriert. Bitte wende dich an jk@sirius-europe.com.
          </div>
          {slots.length > 0 && (
            <div style={{ marginTop: 16, textAlign: 'left' }}>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>Konfigurierte Slots:</div>
              {slots.map(s => (
                <div key={s.id} style={{ fontSize: 12, color: 'var(--text2)', padding: '3px 0' }}>
                  {s.has_credentials ? '✓' : '○'} {s.email || s.label}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {mySlot && (
        <>
          <div style={{ background: mySlot.has_credentials ? 'rgba(52,211,153,0.08)' : 'rgba(245,158,11,0.08)',
            border: `1px solid ${mySlot.has_credentials ? 'rgba(52,211,153,0.3)' : 'rgba(245,158,11,0.4)'}`,
            borderRadius: 10, padding: '14px 16px', marginBottom: 24,
            display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 12, height: 12, borderRadius: '50%', flexShrink: 0,
              background: mySlot.has_credentials ? 'var(--green)' : '#f59e0b',
              boxShadow: mySlot.has_credentials ? '0 0 6px var(--green)' : 'none' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{mySlot.label || mySlot.email}</div>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
                {mySlot.has_credentials ? '✓ Token hinterlegt' : '⚠ Noch kein Token hinterlegt'}
              </div>
              {mySlot.has_credentials && mySlot.expires_at != null && (() => {
                const nowMs = Date.now();
                const remMs = mySlot.expires_at - nowMs;
                const remH  = Math.floor(remMs / 3600000);
                const remMin = Math.floor(remMs / 60000);
                const expired = remMs <= 0;
                const warn    = !expired && remH < 2;
                const color   = expired ? 'var(--red)' : warn ? '#f59e0b' : remH < 12 ? '#ca8a04' : 'var(--green)';
                const label   = expired ? 'Token abgelaufen — bitte erneuern!' :
                                warn    ? `Läuft in ${remMin} Min ab — bitte jetzt erneuern!` :
                                remH < 24 ? `Noch ${remH}h gültig` :
                                            `Noch ${Math.floor(remH/24)}d ${remH%24}h gültig`;
                return (
                  <div style={{ fontSize: 12, color, fontWeight: expired || warn ? 700 : 400, marginTop: 4 }}>
                    {expired || warn ? '⚠ ' : '⏱ '}{label}
                  </div>
                );
              })()}
            </div>
            {mySlot.has_credentials && (() => {
              const needsRenew = mySlot.expires_at != null && (mySlot.expires_at - Date.now()) < 7200000;
              return (
                <button className="btn btn-sm" onClick={() => { setStep(1); setMsg(null); }}
                  style={needsRenew ? { background: '#f59e0b', color: '#1a1a1a', fontWeight: 700 } : {}}>
                  {needsRenew ? '⚠ Jetzt erneuern' : 'Token erneuern'}
                </button>
              );
            })()}
          </div>

          {step === 3 && (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
              <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Token gespeichert!</div>
              <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 20 }}>
                Dein Claude Token ist jetzt im Vault hinterlegt. Die automatische Rotation laeuft ab sofort.
              </div>
              <button className="btn btn-secondary btn-sm"
                onClick={() => { setStep(1); setTokenInput({ access_token: '', refresh_token: '', expires_at: '' }); }}>
                Nochmal einreichen
              </button>
            </div>
          )}

          {step === 1 && (
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14 }}>So holst du deinen Token:</div>
              {[
                { n: 1, title: 'Terminal oeffnen', desc: 'Oeffne ein Terminal auf deinem eigenen Rechner.' },
                { n: 2, title: 'Befehl ausfuehren', desc: 'Fuehre diesen Befehl aus:', code: 'cat ~/.claude/.credentials.json' },
                { n: 3, title: 'JSON kopieren', desc: 'Kopiere den kompletten Output (alles von { bis }).' },
                { n: 4, title: 'Hier einfuegen', desc: 'Klicke auf "Token eingeben" und fuege den kopierten Text ein.' },
              ].map(s => (
                <div key={s.n} style={{ display: 'flex', gap: 14, marginBottom: 14,
                  background: 'var(--bg2)', borderRadius: 8, padding: '12px 14px' }}>
                  <div style={{ width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                    background: 'var(--accent)', color: '#0a0a0a',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 13 }}>{s.n}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 3 }}>{s.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--text2)' }}>{s.desc}</div>
                    {s.code && <code style={{ display: 'inline-block', marginTop: 6, padding: '4px 10px',
                      background: 'var(--bg)', border: '1px solid var(--border)',
                      borderRadius: 4, fontSize: 12, fontFamily: 'monospace', color: 'var(--green)' }}>{s.code}</code>}
                  </div>
                </div>
              ))}
              <button className="btn btn-primary" style={{ width: '100%', marginTop: 4 }} onClick={() => setStep(2)}>
                Token eingeben →
              </button>
            </div>
          )}

          {step === 2 && (
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14 }}>Token einfuegen</div>
              <div style={{ background: 'var(--bg2)', borderRadius: 8, padding: '12px 14px', marginBottom: 16, fontSize: 12, color: 'var(--text2)' }}>
                💡 Einfach den <strong>kompletten JSON</strong> aus <code style={{ fontFamily: 'monospace' }}>~/.claude/.credentials.json</code> einfuegen — wird automatisch erkannt.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6, display: 'block' }}>Access Token oder kompletter JSON *</label>
                  <textarea rows={5} style={inputStyle}
                    placeholder="Kompletten JSON einfuegen: { claudeAiOauth: { accessToken: eyJ... } } — oder nur den Token-Wert."
                    value={tokenInput.access_token}
                    onChange={e => handleRawInput(e.target.value)} />
                  {parseError && <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 4 }}>{parseError}</div>}
                </div>
                {!tokenInput.refresh_token && (
                  <div>
                    <label style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6, display: 'block' }}>
                      Refresh Token <span style={{ color: 'var(--text3)' }}>(optional)</span>
                    </label>
                    <textarea rows={2} style={inputStyle} placeholder="refreshToken aus credentials.json"
                      value={tokenInput.refresh_token}
                      onChange={e => setTokenInput(p => ({ ...p, refresh_token: e.target.value }))} />
                  </div>
                )}
                {tokenInput.expires_at && (() => {
                  const exp = parseInt(tokenInput.expires_at);
                  const label = expiryLabel(exp);
                  return label ? (
                    <div style={{ fontSize: 12, padding: '6px 10px', borderRadius: 6, background: 'var(--bg2)', color: label.color }}>
                      🕐 Token-Ablauf: {label.text} ({new Date(exp).toLocaleString('de-DE')})
                    </div>
                  ) : null;
                })()}
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <button className="btn btn-primary" style={{ flex: 1 }} onClick={submitToken}
                    disabled={!tokenInput.access_token.trim() || saving}>
                    {saving ? <Spin size={14} /> : '✓ Token speichern'}
                  </button>
                  <button className="btn btn-secondary" onClick={() => setStep(1)}>← Zurueck</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function CockpitPage() {
  const { auth } = useContext(AuthContext);
  const token = auth?.access_token;
  const isAdmin = ['admin', 'provider_admin', 'tenant_admin', 'dev_dash_admin'].includes(auth?.user_role);

  const visibleTabs = TABS.filter(t => !t.adminOnly || isAdmin);

  const [activeTab, setActiveTab]   = useState('dashboard');
  const [stats, setStats]           = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastDiscussProject, setLastDiscussProject] = useState(
    () => localStorage.getItem('lastDiscussProject') || 'bpos'
  );
  const [discussInitialMsg, setDiscussInitialMsg] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Kanban → Diskussion: Projekt wechseln + Nachricht vorbelegen
  const handleOpenDiscuss = useCallback((project, message) => {
    setLastDiscussProject(project);
    localStorage.setItem('lastDiscussProject', project);
    setDiscussInitialMsg(message);
    setActiveTab('diskussion');
  }, []);
  const isMounted = useRef(true);
  useEffect(() => { return () => { isMounted.current = false; }; }, []);

  // ── PWA App Badge — rote Zahl auf Home Screen Icon ────────────────────────
  useEffect(() => {
    if (!('setAppBadge' in navigator)) return;
    if (pendingCount > 0) {
      navigator.setAppBadge(pendingCount).catch(() => {});
    } else {
      navigator.clearAppBadge().catch(() => {});
    }
  }, [pendingCount]);

  // Background-Poll für Freigaben — Badge auch ohne Freigaben-Tab aktuell
  const pollApprovals = useCallback(async () => {
    if (!token) return;
    try {
      const data = await apiFetch(token, '/cockpit/approvals');
      if (isMounted.current) setPendingCount((data.pending || []).length);
    } catch { /* silent */ }
  }, [token]);

  useEffect(() => {
    pollApprovals();
    const iv = setInterval(pollApprovals, 60000);
    return () => clearInterval(iv);
  }, [pollApprovals]);

  // ── Dynamische Projektliste vom Backend laden ──────────────────────────────
  const [projects, setProjects] = useState([]);
  const [taskVersion, setTaskVersion] = useState(0);
  const bumpTaskVersion = useCallback(() => setTaskVersion(v => v + 1), []);
  const reloadProjects = useCallback(async () => {
    if (!token) return;
    try {
      const data = await apiFetch(token, '/cockpit/projects');
      const backendIds = new Set(Object.keys(data));
      // Filter out placeholders that have already been set up via the wizard
      const remainingPlaceholders = PROJECT_PLACEHOLDERS.filter(p => !backendIds.has(p.id));
      setProjects([...buildProjects(data), ...remainingPlaceholders]);
    } catch { /* silent — UI zeigt leere Liste */ }
  }, [token]);
  useEffect(() => { reloadProjects(); }, [reloadProjects]);

  const loadStats = useCallback(async () => {
    if (!token) return;
    try {
      const data = await apiFetch(token, '/cockpit/stats');
      if (isMounted.current) setStats(data);
    } catch(e) { console.warn("[dev-dash]", e?.message || e); }
  }, [token]);

  useEffect(() => {
    loadStats();
    const iv = setInterval(loadStats, 30000);
    return () => clearInterval(iv);
  }, [loadStats]);

  async function handleRefresh() {
    setRefreshing(true);
    await loadStats();
    setRefreshing(false);
  }

  const freigabenTab = TABS.find(t => t.id === 'freigaben');

  const tabContent = !token ? (
    <div className="alert alert-err">Kein Auth-Token verfügbar. Bitte neu einloggen.</div>
  ) : (<>
    {activeTab === 'dashboard'   && <DashboardTab token={token} />}
    {activeTab === 'taskgen'     && <TaskGeneratorTab token={token} />}
    {activeTab === 'claudecode'  && <ClaudeCodeTab token={token} />}
    {activeTab === 'sessions'    && <SessionsTab token={token} />}
    {activeTab === 'kanban'      && <KanbanTab token={token} onOpenDiscuss={handleOpenDiscuss} />}
    {activeTab === 'diskussion'  && <DiskussionTab token={token} lastProject={lastDiscussProject} onProjectChange={pid => { setLastDiscussProject(pid); localStorage.setItem('lastDiscussProject', pid); }} initialMessage={discussInitialMsg} onInitialMessageUsed={() => setDiscussInitialMsg(null)} />}
    {activeTab === 'projekte'    && <ProjekteTab token={token} />}
    {activeTab === 'logs'        && <LogsTab token={token} />}
    {activeTab === 'freigaben'   && <FreigabenTab token={token} onCountChange={setPendingCount} />}
    {activeTab === 'claudeusage' && <ClaudeUsageTab token={token} />}
    {activeTab === 'orchadmin'   && <OrchestratorAdminTab token={token} />}
    {activeTab === 'status'      && <StatusTab token={token} />}
    {activeTab === 'prism'       && <PrismTab token={token} />}
    {activeTab === 'ideenlog'    && <IdeenlogTab token={token} />}
    {activeTab === 'transporte'     && <TransporteTab token={token} userEmail={auth?.user_email} userRole={auth?.user_role} />}
    {activeTab === 'claudeportal' && <ClaudePortalTab token={token} userEmail={auth?.email || auth?.user?.email} />}
    {activeTab === 'claudeaccounts' && isAdmin && <ClaudeAccountsTab token={token} userEmail={auth?.user_email} />}
    {activeTab === 'terminal'    && isAdmin && <WebTerminalTab />}
    {activeTab === 'usermgmt'   && isAdmin && <UserManagementTab token={token} />}
  </>);

  return (
    <ProjectsCtx.Provider value={{ projects, reloadProjects, taskVersion, bumpTaskVersion }}>
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Header */}
      <div style={{ background: 'var(--bg2)', borderBottom: '1px solid var(--border)',
        padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
        <h1 style={{ margin: 0, fontSize: 15, fontWeight: 800, letterSpacing: -0.5,
          color: 'var(--text)', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 8 }}>
            <img src="/sirius-logo.png" alt="SIRIUS Ventures" style={{ height: 28, width: 'auto', objectFit: 'contain' }} />
            SIRIUS Ventures</h1>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="hide-mobile"><GlobalStats stats={stats} /></span>
          <button className="btn btn-secondary btn-sm" onClick={handleRefresh}
            disabled={refreshing} title="Aktualisieren">
            {refreshing ? <Spin size={12} /> : '↻'}
          </button>
          <button className="hamburger-btn" onClick={() => setMobileMenuOpen(true)} aria-label="Navigation öffnen">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
        </div>
      </div>

      {token && <SystemStatusBar token={token} />}

      {/* Mobile navigation menu */}
      {mobileMenuOpen && (
        <>
          <div className="mobile-nav-overlay" onClick={() => setMobileMenuOpen(false)} />
          <div className="mobile-nav-menu">
            <div className="mobile-nav-header">
              <span>Navigation</span>
              <button className="mobile-nav-close" onClick={() => setMobileMenuOpen(false)}>✕</button>
            </div>
            {visibleTabs.map(t => (
              <button key={t.id}
                className={`mobile-nav-item${activeTab === t.id ? ' active' : ''}`}
                onClick={() => { setActiveTab(t.id); setMobileMenuOpen(false); }}>
                <span className="tab-icon" dangerouslySetInnerHTML={{ __html: t.icon }} />
                <span style={{ flex: 1 }}>{t.label}</span>
                {t.id === 'freigaben' && pendingCount > 0 && (
                  <span className="mobile-nav-badge">{pendingCount}</span>
                )}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Body */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Fixed left sidebar — desktop only via CSS */}
        <nav className="cockpit-sidebar">
          {visibleTabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`cockpit-sidebar-item${activeTab === t.id ? ' active' : ''}`}>
              <span className="tab-icon" dangerouslySetInnerHTML={{ __html: t.icon }} />
              <span style={{ flex: 1, textAlign: 'left' }}>{t.label}</span>
              {t.id === 'freigaben' && pendingCount > 0 && (
                <span style={{ background: 'var(--red)', color: '#fff',
                  borderRadius: 8, padding: '1px 5px', fontSize: 9, fontWeight: 700 }}>
                  {pendingCount}
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* Content area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

          {/* Horizontal tab bar — mobile only via CSS */}
          <div className="cockpit-toptabs">
            {visibleTabs.map(t => (
              <button key={t.id}
                className={`tab${activeTab === t.id ? ' on' : ''}`}
                onClick={() => setActiveTab(t.id)}
                style={{ whiteSpace: 'nowrap', position: 'relative', flexShrink: 0 }}>
                <span className="tab-icon" style={{ marginRight: 4 }} dangerouslySetInnerHTML={{ __html: t.icon }} />{t.label}
                {t.id === 'freigaben' && pendingCount > 0 && (
                  <span style={{ position: 'absolute', top: 4, right: 2,
                    background: 'var(--red)', color: '#fff', borderRadius: '50%',
                    width: 14, height: 14, display: 'inline-flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: 8, fontWeight: 700 }}>
                    {pendingCount}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div className="cockpit-page-content" style={{ flex: 1, overflow: 'auto', padding: '16px 20px 20px', display: 'flex', flexDirection: 'column' }}>{tabContent}</div>
          </div>
        </div>
      </div>
    </div>
    </ProjectsCtx.Provider>
  );
}
